export interface CardScene {
    career: string;
    relationship: string;
    wealth: string;
    health: string;
    universal: string;
    transition: string;
}

export interface CardInterpretation {
    keywords: string[];
    keywordsEn?: string[];
    imageDescription: string;
    imageDescriptionEn?: string;
    generalMeaning: string;
    generalMeaningEn?: string;
    scenes: CardScene;
    scenesEn?: CardScene;
}

export interface CardMeaning {
    id: string;
    name: string;
    nameCn: string;
    introduction: string;
    introductionEn?: string;
    upright: CardInterpretation;
    reversed: CardInterpretation;
}

export const tarotMeanings: Record<string, CardMeaning> = {
    // 0 - The Fool 愚人
    '001': {
        id: '001',
        name: 'The Fool',
        nameCn: '愚人',
        introduction: '作为大阿卡纳的第0号牌，愚人牌是塔罗牌的起始与终结，既象征无拘无束的初始状态，也代表旅程闭环后的回归纯粹，是整个塔罗体系中"冒险、潜能、初心"的核心符号之一。作为塔罗的"开篇牌"，愚人是莽撞却鲜活的勇气。它赞美人怀揣天真奔赴未知的热情，是不被经验束缚的新生力，却也藏着隐性提醒，这份不管不顾里，既有无拘无束的自由，也带着忽略隐患的鲁莽。它像所有故事开头那股不管不顾的劲儿，是懵懂的出发，也是带着生命力的全新开端。',
        introductionEn: 'As card 0 of the Major Arcana, The Fool represents both the beginning and end of the Tarot journey. It symbolizes the unbounded initial state and the return to purity after completing the cycle. The Fool embodies adventure, potential, and innocence. It celebrates the courage to embrace the unknown with childlike enthusiasm, representing fresh energy unconstrained by experience, while also carrying a subtle reminder about the risks of recklessness.',
        upright: {
            keywords: ['天真无畏', '新起点', '直觉引领', '随遇而安', '玩笑', '自由', '开始', '漫游', '行动', '不可思议的行为', '远足', '灵魂触碰'],
            keywordsEn: ['Innocence', 'New beginnings', 'Free spirit', 'Spontaneity', 'Adventure', 'Potential', 'Faith', 'Leap of faith', 'Optimism', 'Trust'],
            imageDescription: '手持白玫瑰（象征纯真、理想）的行者，身背行囊立于悬崖边缘（代表未知冒险），身旁的小狗（对应本能守护/提醒）、背景的烈日（光明希望）与雪山（潜在挑战），共同构成"启程探索"的画面。以轻盈姿态拥抱未知，既饱含生命力，也暗藏对风险的懵懂。',
            imageDescriptionEn: 'A traveler holding a white rose stands at the edge of a cliff, with a small dog nearby. The bright sun and distant mountains represent hope and challenges ahead.',
            generalMeaning: '代表新生冒险、纯真勇气，是带着热情与理想开启新旅程的状态，虽略显莽撞，但承载"从零开始的无限可能性"（对应放下顾虑、拥抱潜能的阶段）。',
            generalMeaningEn: 'Represents new adventures and innocent courage. It\'s a state of embarking on a new journey with enthusiasm and ideals. Though somewhat reckless, it carries infinite possibilities from starting fresh.',
            scenes: {
                career: '大胆开启新领域（如新行业、新学科），不带预设的尝试反而能收获意外机遇。',
                relationship: '以纯粹真诚开启新关系，不刻意算计，轻松的互动能快速拉近距离。',
                wealth: '愿意尝试小额新机会（如副业体验），不求短期回报，先积累新领域的资源。',
                health: '心态松弛，愿意尝试轻量的新健康方式（如户外散步），身体状态随心情舒展。',
                universal: '当下适合跟随直觉迈出第一步，不必过度规划，轻盈探索会有新收获。',
                transition: '从固守舒适区转向"勇敢拥抱未知"的轻盈转折。'
            },
            scenesEn: {
                career: 'Boldly explore new fields. Unconventional approaches may lead to unexpected opportunities.',
                relationship: 'Start new relationships with pure sincerity. Relaxed interactions bring people closer.',
                wealth: 'Willing to try small new opportunities. Focus on building resources rather than short-term returns.',
                health: 'Relaxed mindset. Try light new health activities. Physical state improves with mood.',
                universal: 'Follow your intuition and take the first step. Light exploration brings new discoveries.',
                transition: 'A positive shift from comfort zone to bravely embracing the unknown.'
            }
        },
        reversed: {
            keywords: ['盲目冲动', '鲁莽冒进', '忽视风险', '逃避现实', '粗心大意', '欠考虑', '没有经验', '疯狂', '恶作剧者', '空想'],
            keywordsEn: ['Recklessness', 'Risk-taking', 'Naivety', 'Foolishness', 'Hesitation', 'Fear', 'Poor judgment', 'Carelessness'],
            imageDescription: '逆位中旅者脚步慌乱，小狗远离身侧；悬崖的危险感被放大，背景雪山显得冷峻，整体氛围是失控的莽撞。',
            imageDescriptionEn: 'The traveler\'s steps become chaotic, the dog moves away. The cliff\'s danger is amplified, creating an atmosphere of uncontrolled recklessness.',
            generalMeaning: '陷入"不加思考的冒险"，忽视潜在风险，因冲动行动陷入混乱，是缺乏准备、盲目前行的消耗阶段。',
            generalMeaningEn: 'Falling into thoughtless adventure, ignoring potential risks. Impulsive actions lead to chaos. A phase of unprepared, blind progress.',
            scenes: {
                career: '盲目换行/选专业，未做调研就行动，导致进展受阻、陷入被动。',
                relationship: '过于自我、忽视对方感受，鲁莽的表达易引发误解，让新关系出现摩擦。',
                wealth: '冲动投资/非理性消费，毫无规划地消耗资源，出现小额亏损。',
                health: '盲目尝试极端健康方式（如过度节食），反而让身体状态下滑。',
                universal: '当下需警惕冲动，避免无准备的冒险，先梳理风险再行动。',
                transition: '从轻盈探索转向"因鲁莽冒进而陷入困境"的被动转折。'
            },
            scenesEn: {
                career: 'Blindly changing careers without research leads to obstacles and passive situations.',
                relationship: 'Being too self-centered and ignoring others\' feelings causes friction in new relationships.',
                wealth: 'Impulsive investments or irrational spending without planning leads to small losses.',
                health: 'Blindly trying extreme health methods (like excessive dieting) actually worsens physical condition.',
                universal: 'Be wary of impulses now. Assess risks before taking action.',
                transition: 'A negative shift from exploration to difficulties caused by reckless behavior.'
            }
        }
    },

    // 1 - The Magician 魔术师
    '002': {
        id: '002',
        name: 'The Magician',
        nameCn: '魔术师',
        introduction: '作为大阿卡纳的第1号牌，魔术师牌的核心围绕"意志显化与资源整合"展开。牌面中人物双手高举（象征连接天地能量，锚定意志的方向），身前桌上的权杖、圣杯、星币、宝剑（对应塔罗四元素，代表对所有资源与能力的掌控）整齐陈列，背景流转的波纹是能量流动的具象，这些元素共同指向以清晰意志调动资源的状态，是将隐性潜能转化为显性行动的节点。魔术师是精准利落的行动者。',
        introductionEn: 'As card 1 of the Major Arcana, The Magician centers on "manifestation of will and resource integration". The figure raises both hands high (symbolizing connection between heaven and earth energy), with wand, cup, pentacle, and sword (the four Tarot elements representing mastery of all resources) arranged on the table. The Magician transforms hidden potential into concrete action.',
        upright: {
            keywords: ['才能', '资源整合', '主动创造', '潜能启动', '清晰掌控', '天赋', '专注', '操纵', '意志', '能量', '主动', '沟通', '联结', '通道'],
            keywordsEn: ['Willpower', 'Manifestation', 'Resourcefulness', 'Skill', 'Concentration', 'Action', 'Power', 'Creativity', 'Self-confidence', 'Communication'],
            imageDescription: '身着深色礼服的魔术师双手上举，呼应上方绿蓝黄交织的光（象征灵性与潜能）；桌台整齐摆放权杖、圣杯、圆盘、宝剑（四元素道具），周围绿植点缀，整体氛围是能量流通、主动掌控的。',
            imageDescriptionEn: 'The Magician in dark robes raises both hands toward the light above. The table displays the four elemental tools, symbolizing mastery and energy flow.',
            generalMeaning: '是主动开启新计划的阶段，能精准整合手头资源，借由自身行动力将"想法"落地为"行动"，是充满掌控感的积极开端。',
            generalMeaningEn: 'A phase of actively initiating new plans. You can precisely integrate available resources and transform ideas into action through your own initiative. A powerful beginning full of control.',
            scenes: {
                career: '能活用自身技能以及现有资源启动新项目，学习上可找到高效方法，进展比预期顺畅。',
                relationship: '主动发起沟通或新连接，表达清晰有吸引力，关系会出现高质量新互动。',
                wealth: '善用闲置资源（如小技能）创造额外收入，资源整合带来稳定小额收益。',
                health: '主动制定作息/运动计划并坚持，身体状态逐步回升。',
                universal: '当下是把握机遇、发挥个人能力的关键期，主动行动就能撬动新可能。',
                transition: '从迷茫/停滞转向"清晰目标+落地行动"的正向转折。'
            },
            scenesEn: {
                career: 'Utilize your skills and existing resources to start new projects. Find efficient methods for learning with smoother progress than expected.',
                relationship: 'Actively initiate communication or new connections. Clear and attractive expression leads to quality interactions.',
                wealth: 'Use idle resources (like small skills) to create extra income. Resource integration brings stable returns.',
                health: 'Actively create and stick to health and exercise plans. Physical condition gradually improves.',
                universal: 'Now is the key time to seize opportunities and demonstrate personal abilities. Taking initiative opens new possibilities.',
                transition: 'A positive shift from confusion/stagnation to clear goals and concrete action.'
            }
        },
        reversed: {
            keywords: ['欺骗', '资源浪费', '空想无行动', '掌控失效', '沟通错位', '欲望', '力量削弱', '沮丧', '压抑', '倦怠', '害羞'],
            keywordsEn: ['Manipulation', 'Trickery', 'Wasted talent', 'Unfocused', 'Poor planning', 'Deception', 'Lack of direction', 'Hesitation'],
            imageDescription: '逆位中魔术师姿态慌乱（无法连接能量）；桌台四元素道具杂乱散落，背景光黯淡，周围绿植萎靡，氛围被动失控。',
            imageDescriptionEn: 'The Magician appears chaotic, unable to connect with energy. The four elemental tools are scattered, the light dims, creating an atmosphere of passive loss of control.',
            generalMeaning: '资源整合失败，行动力跟不上想法，计划易落空，是自我掌控力缺失、能量无法落地的阶段。',
            generalMeaningEn: 'Failed resource integration. Action cannot keep up with ideas, plans easily fall through. A phase of lost self-control and ungrounded energy.',
            scenes: {
                career: '盲目启动项目却浪费资源，学习方法错位，效率低且进展停滞。',
                relationship: '沟通含糊不清，自我表达错位，易引发误解或无效互动。',
                wealth: '乱投资/浪费资源，收入不稳定，甚至出现小额亏损。',
                health: '拖延健康计划，作息混乱，身体状态下滑却不愿调整。',
                universal: '陷入"空想而不落地"的状态，错失当下机遇。',
                transition: '从主动筹备转向"计划落空、被迫停滞"的被动转折。'
            },
            scenesEn: {
                career: 'Blindly starting projects wastes resources. Misaligned learning methods lead to low efficiency and stalled progress.',
                relationship: 'Unclear communication and misaligned self-expression easily cause misunderstandings or ineffective interactions.',
                wealth: 'Reckless investments or wasted resources lead to unstable income and small losses.',
                health: 'Procrastinating health plans, chaotic routines, declining physical condition without willingness to adjust.',
                universal: 'Trapped in daydreaming without action, missing current opportunities.',
                transition: 'A negative shift from active preparation to failed plans and forced stagnation.'
            }
        }
    },

    // 2 - The High Priestess 女祭司
    '003': {
        id: '003',
        name: 'The High Priestess',
        nameCn: '女祭司',
        introduction: '女祭司的核心围绕"内在洞见与隐秘智慧"展开。牌面中，头顶的新月皇冠对应传统中的阴性直觉力量，闭眼静坐的姿态契合内省状态，身前的书本与卷轴承载着未完全公开的隐秘智慧，两侧的帷幔与立柱则象征意识与潜意识的边界，这些元素指向"不依赖外在行动、而通过内观与直觉捕捉真相"的状态，是聚焦内在感知、承接隐秘信息的节点。作为塔罗体系中"直觉与隐秘知识"的标志性符号之一，女祭司是从外在行动转向内在感知的关键环节，象征"以沉静内观触碰真相、以直觉承接隐秘智慧"的核心逻辑。',
        introductionEn: 'The High Priestess centers on inner insight and hidden wisdom. The crescent crown represents feminine intuitive power, the closed-eye meditation posture reflects introspection, the books and scrolls hold secret wisdom, and the veils symbolize the boundary between conscious and subconscious. She captures truth through inner observation and intuition rather than external action.',
        upright: {
            keywords: ['神秘', '保密', '未知事物', '直觉洞察', '内省沉淀', '隐藏智慧', '静默感知', '潜意识', '平静', '内省', '沉默', '顺从', '信仰', '严肃', '机密', '智慧', '自制力', '有智慧的女性'],
            keywordsEn: ['Intuition', 'Mystery', 'Subconscious', 'Inner voice', 'Wisdom', 'Secrets', 'Divine feminine', 'Higher knowledge', 'Patience', 'Contemplation'],
            imageDescription: '闭目的女祭司头戴新月皇冠（象征直觉力量），身着绿衣、佩珍珠（代表沉静与纯净），手持打开的书与卷轴，身处帷幔（隐喻隐藏边界）与绿植环绕的空间，整体氛围是沉静内观的。',
            imageDescriptionEn: 'The High Priestess with closed eyes wears a crescent crown, dressed in green with pearls, holding open books and scrolls, surrounded by veils and greenery.',
            generalMeaning: '是向内探索的阶段，依赖直觉能洞察隐藏信息，不宜急躁行动，适合沉淀思考、连接内在智慧。',
            generalMeaningEn: 'A phase of inward exploration. Rely on intuition to perceive hidden information. Avoid hasty action; focus on contemplation and connecting with inner wisdom.',
            scenes: {
                career: '靠直觉捕捉行业隐藏机遇，适合做研究、规划类工作；学业上能深入理解知识本质，而非死记硬背。',
                relationship: '静默中精准感知对方情绪，适合深度沟通（而非表面寒暄），能拉近心灵距离。',
                wealth: '关注"隐性资源"（如未发掘的技能价值），不宜冒进投资，稳守现有资源更稳妥。',
                health: '重视身体的内在信号（如疲劳时及时休息），通过冥想、轻养生等内调方式养护状态。',
                universal: '当下需沉静内观，直觉会帮你找到被忽略的关键信息。',
                transition: '从向外奔波、盲目行动，转向向内沉淀、精准洞察的转折。'
            },
            scenesEn: {
                career: 'Use intuition to capture hidden industry opportunities. Suitable for research and planning work. Deeply understand knowledge essence rather than rote memorization.',
                relationship: 'Precisely perceive others\' emotions in silence. Deep communication brings hearts closer.',
                wealth: 'Focus on hidden resources (like undiscovered skill value). Avoid aggressive investments; protect existing resources.',
                health: 'Pay attention to body\'s inner signals. Rest when tired. Nurture through meditation and gentle wellness practices.',
                universal: 'Time for quiet introspection. Intuition will help you find overlooked key information.',
                transition: 'A shift from external busyness to inner contemplation and precise insight.'
            }
        },
        reversed: {
            keywords: ['直觉失灵', '信息封闭', '过度内耗', '逃避沟通', '黑暗', '极端', '自负', '沉默', '神秘', '保密', '保守秘密者'],
            keywordsEn: ['Blocked intuition', 'Secrets', 'Withdrawal', 'Repression', 'Disconnection', 'Information overload', 'Self-doubt', 'Superficiality'],
            imageDescription: '逆位中女祭司的书与卷轴散落，周围绿植萎靡，整体氛围是躁动、封闭的。',
            imageDescriptionEn: 'The High Priestess\' books and scrolls are scattered, surrounding plants wilt, creating an atmosphere of agitation and closure.',
            generalMeaning: '陷入过度内耗或自我封闭，直觉失效、无法洞察有效信息，易因固执或逃避陷入迷茫。',
            generalMeaningEn: 'Trapped in excessive internal consumption or self-isolation. Intuition fails, unable to perceive useful information. Stubbornness or avoidance leads to confusion.',
            scenes: {
                career: '固执依赖主观"直觉"（如未发掘的技能价值），忽视实际信息，研究/规划偏离方向，效率下滑。',
                relationship: '封闭自己拒绝沟通，误解持续积累，关系陷入冷战或疏离。',
                wealth: '盲目依赖"直觉"投资，忽视实际财务数据，易出现小额亏损。',
                health: '过度纠结身体小信号，陷入焦虑内耗，反而让状态持续下滑。',
                universal: '当下需打破封闭，主动接纳外部信息、沟通交流，避免自我消耗。',
                transition: '从沉静内观，转向过度封闭、内耗失控的被动转折。'
            },
            scenesEn: {
                career: 'Stubbornly relying on subjective intuition while ignoring actual information. Research/planning goes off track, efficiency declines.',
                relationship: 'Closing yourself off and refusing communication. Misunderstandings accumulate, relationship becomes cold or distant.',
                wealth: 'Blindly relying on intuition for investments while ignoring actual financial data leads to losses.',
                health: 'Obsessing over small body signals leads to anxious internal consumption, worsening overall condition.',
                universal: 'Time to break through isolation. Actively accept external information and communicate to avoid self-consumption.',
                transition: 'A negative shift from quiet introspection to excessive isolation and uncontrolled internal consumption.'
            }
        }
    },

    // 3 - The Empress 女皇
    '004': {
        id: '004',
        name: 'The Empress',
        nameCn: '女皇',
        introduction: '皇后的核心围绕丰饶、滋养与生命的孕育展开。牌面中，手持的石榴对应多产与丰饶，麦穗代表物质收获与生活的富足，背景的花园与河流契合自然的生机滋养属性，这些元素共同指向"外在生命的丰足、物质的充盈与情感的滋养"的状态，是承载孕育、传递丰饶的节点。',
        introductionEn: 'The Empress centers on abundance, nurturing, and the creation of life. The pomegranate represents fertility and abundance, wheat symbolizes material harvest and prosperity, and the garden with river reflects natural nurturing vitality. She symbolizes the abundance of life, material prosperity, and emotional nourishment.',
        upright: {
            keywords: ['丰饶滋养', '创造盛放', '物质富足', '温柔掌控', '多产', '创造力', '正能量', '表现力', '舒适', '欢乐', '满足', '力量', '美丽'],
            keywordsEn: ['Abundance', 'Fertility', 'Nurturing', 'Creativity', 'Nature', 'Sensuality', 'Beauty', 'Comfort', 'Motherhood', 'Growth'],
            imageDescription: '戴镶钻皇冠的女皇端坐王座，身着绿衣，一手持石榴，一手握麦穗，背景是花草繁茂的自然与蜿蜒河流，整体氛围丰盈、舒适且充满生命力。',
            imageDescriptionEn: 'The Empress sits on her throne wearing a diamond crown and green robes, holding pomegranate and wheat, with lush gardens and a flowing river behind her.',
            generalMeaning: '处于资源与能量都很充沛的阶段，既能滋养自身与他人，也能在创造、生活中收获富足，是享受成果、舒展自我的时期。',
            generalMeaningEn: 'A phase of abundant resources and energy. Able to nurture yourself and others while harvesting abundance in creativity and life. A time to enjoy results and express yourself.',
            scenes: {
                career: '在创作、服务、滋养类工作中易出成果，学业上状态饱满，能轻松吸收知识并转化为自己的收获。',
                relationship: '以温柔滋养的方式经营关系，能给对方充足的关怀，关系氛围温馨且情感联结加深。',
                wealth: '收入稳定且有额外的物质收益（如福利、副业小成），生活物质条件充裕。',
                health: '身心状态舒展丰盈，食欲、作息都处于舒适的平衡状态，精力充沛。',
                universal: '当下适合投入创造、享受生活，你的滋养力会带来正向反馈。',
                transition: '从资源匮乏、状态紧绷，转向物质与情感都丰盈舒展的转折。'
            },
            scenesEn: {
                career: 'Creative, service, and nurturing work yields results easily. Studies are fulfilling with effortless knowledge absorption.',
                relationship: 'Nurture relationships with warmth, providing abundant care. Relationship atmosphere is cozy with deepening emotional bonds.',
                wealth: 'Stable income with extra material gains. Living conditions are comfortable and abundant.',
                health: 'Mind and body are relaxed and flourishing. Appetite and rest are in comfortable balance with abundant energy.',
                universal: 'Time to invest in creativity and enjoy life. Your nurturing energy brings positive feedback.',
                transition: 'A positive shift from scarcity and tension to material and emotional abundance.'
            }
        },
        reversed: {
            keywords: ['过度放纵', '滋养匮乏', '物质挥霍', '依赖被动', '资源分散', '贫瘠', '激情减少', '欲望', '生产力'],
            keywordsEn: ['Dependence', 'Emptiness', 'Creative block', 'Neglect', 'Smothering', 'Lack', 'Overindulgence', 'Disharmony'],
            imageDescription: '逆位中女皇姿态松散，石榴与麦穗滑落，绿衣黯淡，背景花草萎靡、河流凝滞，整体氛围懈怠、匮乏且失去掌控。',
            imageDescriptionEn: 'The Empress appears slack, pomegranate and wheat falling. Green robes dull, garden withering, river stagnant. An atmosphere of lethargy and scarcity.',
            generalMeaning: '陷入过度消耗资源（或情感）的状态，要么挥霍物质，要么缺乏自我滋养，易因被动依赖陷入匮乏或混乱。',
            generalMeaningEn: 'Trapped in excessive resource or emotional consumption. Either squandering material wealth or lacking self-nurturing. Passive dependence leads to scarcity or chaos.',
            scenes: {
                career: '懈怠拖延，创造力枯竭，学业上状态松散，知识吸收效率低下。',
                relationship: '要么过度依赖对方的滋养，要么给予失衡（过度付出或索取），关系陷入疲惫。',
                wealth: '盲目挥霍物质资源，或收入缩水，生活出现入不敷出的匮乏感。',
                health: '放纵饮食、作息，身体状态下滑，易感到疲惫或代谢紊乱。',
                universal: '当下需收敛放纵，主动规划资源、滋养自身，避免陷入更匮乏的状态。',
                transition: '从丰盈舒展，转向过度消耗、资源匮乏的被动转折。'
            },
            scenesEn: {
                career: 'Procrastination and creative exhaustion. Studies become scattered with low efficiency in knowledge absorption.',
                relationship: 'Either overly dependent on partner\'s nurturing or imbalanced giving. Relationship becomes exhausting.',
                wealth: 'Blindly squandering resources or income shrinking. Life feels financially strained.',
                health: 'Indulging in food and irregular routines leads to declining physical condition, fatigue, or metabolic issues.',
                universal: 'Time to curb indulgence, actively plan resources, and nurture yourself to avoid deeper scarcity.',
                transition: 'A negative shift from abundance to excessive consumption and resource depletion.'
            }
        }
    },

    // 4 - The Emperor 皇帝
    '005': {
        id: '005',
        name: 'The Emperor',
        nameCn: '皇帝',
        introduction: '作为大阿卡纳的第4号牌，皇帝的核心围绕秩序、权威与稳固的统治展开。牌面中，手持的权杖对应牌意里的统治权威，手中的金球是王权掌控的象征，背景的雪山契合稳固不可动摇的秩序属性，石质王座则代表坚固的权力与结构基础。',
        introductionEn: 'As card 4 of the Major Arcana, The Emperor centers on order, authority, and stable rule. The scepter represents ruling authority, the orb symbolizes sovereign control, the snowy mountains reflect unshakeable stability, and the stone throne represents solid power and structural foundation.',
        upright: {
            keywords: ['权威掌控', '结构秩序', '稳定规划', '责任担当', '统治权', '经验', '秩序', '父亲', '政府', '理性主义', '法律', '顽固', '保守', '至高无上', '命令'],
            keywordsEn: ['Authority', 'Structure', 'Stability', 'Father figure', 'Leadership', 'Control', 'Discipline', 'Protection', 'Logic', 'Organization'],
            imageDescription: '皇帝端坐石质稳固王座，头戴皇冠，一手持权杖、一手握宝球，服饰庄重，背景是巍峨雪山，整体氛围是严谨、可靠的掌控感。',
            imageDescriptionEn: 'The Emperor sits on a solid stone throne, wearing a crown, holding scepter and orb, with majestic snow mountains behind. An atmosphere of rigorous and reliable control.',
            generalMeaning: '处于建立秩序、掌控全局的阶段，依靠清晰规划与责任担当推进事务，能为局面带来稳定的框架与高效的执行。',
            generalMeaningEn: 'A phase of establishing order and controlling the overall situation. Clear planning and responsibility bring stable frameworks and efficient execution.',
            scenes: {
                career: '适合主导项目、建立工作规则，能高效推进任务；学业上制定严谨计划，稳步提升成绩。',
                relationship: '作为关系中的稳定支撑者，提供可靠依靠，能明确关系边界与责任分工。',
                wealth: '采取稳健理财策略，重视资产积累与长期规划，财富状态稳定增长。',
                health: '严格自律管理作息与饮食，身体状态因规律习惯保持稳定。',
                universal: '当下需要主动建立秩序、承担责任，你的掌控力能让局面更稳固。',
                transition: '从混乱无序、缺乏规划，转向建立清晰框架、高效推进的正向转折。'
            },
            scenesEn: {
                career: 'Suitable for leading projects and establishing work rules. Efficiently advance tasks. Create rigorous study plans for steady academic improvement.',
                relationship: 'Serve as the stable supporter in relationships, providing reliable dependence with clear boundaries and responsibilities.',
                wealth: 'Adopt stable financial strategies. Focus on asset accumulation and long-term planning for steady wealth growth.',
                health: 'Strict self-discipline in routines and diet keeps physical condition stable through regular habits.',
                universal: 'Time to actively establish order and take responsibility. Your control brings greater stability.',
                transition: 'A positive shift from chaos and lack of planning to clear frameworks and efficient progress.'
            }
        },
        reversed: {
            keywords: ['独裁专断', '僵化失控', '责任缺失', '过度固执', '唯我主义', '突然不稳定', '失态', '经验', '控制支配', '稳固', '自大傲慢', '权威'],
            keywordsEn: ['Tyranny', 'Rigidity', 'Domination', 'Stubbornness', 'Lack of discipline', 'Inflexibility', 'Loss of control', 'Abusive power'],
            imageDescription: '逆位中皇帝姿态紧绷，权杖与宝球松动，王座石纹开裂，背景雪山黯淡压抑，整体氛围是固执、失控的压迫感。',
            imageDescriptionEn: 'The Emperor appears tense, scepter and orb loosening, throne cracking, mountains dark and oppressive. An atmosphere of stubbornness and loss of control.',
            generalMeaning: '陷入过度掌控或秩序崩塌的状态，要么独裁专断引发抵触，要么僵化固执导致进展停滞，失去对局面的合理掌控。',
            generalMeaningEn: 'Trapped in excessive control or collapsing order. Dictatorial behavior causes resistance, or rigid stubbornness leads to stagnation, losing reasonable control of the situation.',
            scenes: {
                career: '独裁式管理引发团队抵触，或规划僵化不知变通，项目/学业进展受阻。',
                relationship: '控制欲过强、忽视对方感受，引发矛盾冲突，关系陷入压抑。',
                wealth: '理财过于保守僵化，错失合理机遇；或盲目独裁式投资，导致资产缩水。',
                health: '要么过度自律（如极端节食）损伤身体，要么彻底放纵作息，状态下滑。',
                universal: '当下需放下固执，避免独裁式掌控，学会灵活调整才能改善局面。',
                transition: '从有序稳定，转向独裁僵化、局面失控的被动转折。'
            },
            scenesEn: {
                career: 'Dictatorial management causes team resistance, or rigid planning fails to adapt, blocking project/academic progress.',
                relationship: 'Excessive control and ignoring partner\'s feelings causes conflicts. Relationship becomes oppressive.',
                wealth: 'Overly conservative and rigid financial management misses opportunities, or blind dictatorial investments shrink assets.',
                health: 'Either excessive self-discipline (like extreme dieting) harms body, or complete indulgence in routines leads to declining condition.',
                universal: 'Time to let go of stubbornness; avoid dictatorial control. Learn to flexibly adjust to improve the situation.',
                transition: 'A negative shift from stable order to dictatorial rigidity and loss of control.'
            }
        }
    },

    // 5 - The Hierophant 教皇
    '006': {
        id: '006',
        name: 'The Hierophant',
        nameCn: '教皇',
        introduction: '作为大阿卡纳的第5号牌，教皇的核心围绕精神传承、教义引导与群体的精神联结展开。牌面中，手持的双权杖对应牌意里的精神权威，跪坐的信徒代表群体对教义的遵从与精神层面的联结，神圣场域的背景契合精神引导的专属环境。',
        introductionEn: 'As card 5 of the Major Arcana, The Hierophant centers on spiritual tradition, doctrinal guidance, and collective spiritual connection. The double scepter represents spiritual authority, the kneeling followers represent collective adherence to teachings and spiritual bonding.',
        upright: {
            keywords: ['传统指引', '教义规范', '集体智慧', '精神联结', '权威启蒙', '信仰', '指引', '知识', '制度习俗', '信仰体系', '桥梁', '沟通', '理想典范', '祝福', '调停者'],
            keywordsEn: ['Tradition', 'Conformity', 'Spiritual guidance', 'Education', 'Belief systems', 'Institutions', 'Wisdom', 'Mentorship', 'Ethics', 'Community'],
            imageDescription: '牌中人物身着宗教式服饰、头戴主教冠，持双权杖端坐于肃穆席位；下方两人以跪拜姿态呈现，代表对其精神引领的追随；整体氛围是权威且正统的精神指导感。',
            imageDescriptionEn: 'The Hierophant in religious robes and papal tiara holds double scepters on a solemn seat. Two kneeling followers below represent spiritual following. An atmosphere of authoritative orthodox spiritual guidance.',
            generalMeaning: '处于遵循传统规范、获得权威（或集体）精神指引的阶段，借由既定的经验、教义框架，可获得稳定的方向与精神联结。',
            generalMeaningEn: 'A phase of following traditional norms and receiving authoritative spiritual guidance. Through established experience and doctrinal frameworks, stable direction and spiritual connection are gained.',
            scenes: {
                career: '遵循行业传统规则，获得前辈权威的经验指导；学业上依托经典理论体系，进展契合集体认可的标准。',
                relationship: '以集体认可的相处模式经营关系，借共同价值观深化精神联结，关系状态符合传统认知中的稳定范式。',
                wealth: '采用传统稳健的财富策略（如行业通用理财方式），收益契合常规预期，无剧烈波动。',
                health: '遵循经典养生理念（如规律作息、传统食疗），身体状态维持在大众认知的健康区间。',
                universal: '当下适合借助权威经验、集体智慧做决策，遵循传统框架可规避不必要的试错。',
                transition: '从精神层面的迷茫无向，转向依托传统规范、获得权威指引的稳定转折。'
            },
            scenesEn: {
                career: 'Follow industry traditions and receive guidance from senior authorities. Studies rely on classical theoretical systems meeting collectively recognized standards.',
                relationship: 'Manage relationships with collectively approved patterns. Shared values deepen spiritual bonds in traditionally recognized stable paradigms.',
                wealth: 'Use traditional stable wealth strategies. Returns meet conventional expectations without dramatic fluctuations.',
                health: 'Follow classic wellness concepts like regular routines and traditional dietary therapy. Maintain health within commonly recognized ranges.',
                universal: 'Time to make decisions using authoritative experience and collective wisdom. Following traditional frameworks avoids unnecessary trial and error.',
                transition: 'A positive shift from spiritual confusion to stability through traditional norms and authoritative guidance.'
            }
        },
        reversed: {
            keywords: ['教条僵化', '反抗权威', '背离传统', '精神束缚', '认知狭隘', '等级制度', '教条主义', '无视忠告', '指导说明', '导师', '教学', '知识', '调停者'],
            keywordsEn: ['Rebellion', 'Dogma', 'Restriction', 'Challenging tradition', 'New approaches', 'Breaking free', 'Personal beliefs', 'Questioning authority'],
            imageDescription: '逆位中人物姿态紧绷，双权杖握持失稳；下方追随者姿态抗拒，背景肃穆感消解，整体氛围是教条带来的压抑，或对传统指引的抵触。',
            imageDescriptionEn: 'The Hierophant appears tense with unstable grip on scepters. Followers below show resistance. The solemn atmosphere dissolves, replaced by oppressive dogma or resistance to traditional guidance.',
            generalMeaning: '陷入教条僵化的束缚，或主动反抗固有规范，因过度固守/背离既定框架，导致方向失准、事务陷入动荡。',
            generalMeaningEn: 'Trapped in rigid dogmatic constraints, or actively rebelling against established norms. Excessive adherence to or departure from frameworks leads to misguided direction and turmoil.',
            scenes: {
                career: '抗拒行业规则，或过度固守陈旧经验，项目推进受阻；学业上排斥经典体系却无有效替代，进展混乱。',
                relationship: '抵触传统相处模式，或用僵化价值观束缚对方，引发关系矛盾与疏离。',
                wealth: '盲目尝试非共识理财方式，或固守过时策略，导致财富收益波动、甚至缩水。',
                health: '抵触传统养生却采用非理性方案，或固守陈旧理念忽视身体需求，状态下滑。',
                universal: '当下需打破教条束缚，避免盲目反抗或固守传统，需建立适配自身的行动框架。',
                transition: '从遵循传统的稳定，转向抵触权威、教条压抑下的动荡转折。'
            },
            scenesEn: {
                career: 'Resisting industry rules or clinging to outdated experience blocks projects. Rejecting classical systems without effective alternatives causes chaotic progress.',
                relationship: 'Resisting traditional relationship patterns or constraining partners with rigid values causes conflicts and distance.',
                wealth: 'Blindly trying non-consensus financial methods or clinging to outdated strategies causes fluctuating or shrinking returns.',
                health: 'Resisting traditional wellness while adopting irrational methods, or clinging to outdated concepts while ignoring body needs, leads to declining condition.',
                universal: 'Time to break free from dogmatic constraints. Avoid blind rebellion or rigid adherence. Build frameworks that fit yourself.',
                transition: 'A negative shift from traditional stability to turmoil under authority resistance and dogmatic oppression.'
            }
        }
    },

    // 6 - The Lovers 恋人
    '007': {
        id: '007',
        name: 'The Lovers',
        nameCn: '恋人',
        introduction: '恋人的核心围绕自我与他人的灵魂联结、情感契合及本心选择展开。',
        introductionEn: 'The Lovers centers on soul connection, emotional alignment, and heart-led choices. It represents the harmony of opposites and the necessity of choosing based on personal values.',
        upright: {
            keywords: ['灵魂联结', '价值观契合', '真爱选择', '深度共鸣', '自主抉择'],
            keywordsEn: ['Soul connection', 'Alignment', 'Love', 'Choice', 'Harmony', 'Relationships'],
            imageDescription: '一对男女对视而立，上方带光环的天使笼罩两人。',
            imageDescriptionEn: 'A man and woman stand facing each other, blessed by an angel above under a bright sun, symbolizing divine alignment.',
            generalMeaning: '处于核心选择阶段，能建立深度灵魂联结，做出顺应内心的抉择。',
            generalMeaningEn: 'A stage of core choices. It signifies establishing deep connections and making decisions that resonate with your inner truth.',
            scenes: {
                career: '选择契合自身价值观的项目，能与同伴建立共鸣合作。',
                relationship: '遇见灵魂共鸣的伴侣，双方价值观高度契合。',
                wealth: '与理念一致的伙伴合作创收，收益稳步积累。',
                health: '情感愉悦带来身心舒展，精神状态饱满。',
                universal: '当下的选择需贴合内心价值观。',
                transition: '从迷茫抉择转向契合内心的转折。'
            },
            scenesEn: {
                career: 'Choose projects aligned with your values; collaborative success through shared vision.',
                relationship: 'Meeting a soulmate or deepening a bond through mutual understanding.',
                wealth: 'Partnerships based on shared ethics lead to stable growth.',
                health: 'Emotional balance translates to physical well-being.',
                universal: 'Decisions should follow your heart; harmony is within reach.',
                transition: 'Shift from indecision to soul-aligned commitment.'
            }
        },
        reversed: {
            keywords: ['选择错位', '价值观冲突', '情感疏离', '盲目抉择'],
            keywordsEn: ['Imbalance', 'Misalignment', 'Disharmony', 'Detachment', 'Poor choices'],
            imageDescription: '逆位中男女眼神失焦，背景树林显得沉闷。',
            imageDescriptionEn: 'The connection seems severed; the couple is out of sync, and the divine blessing feels distant.',
            generalMeaning: '陷入选择违背内心价值观的状态，关系因理念冲突出现矛盾。',
            generalMeaningEn: 'Facing internal or external conflict. Choices are made for the wrong reasons, leading to a lack of harmony.',
            scenes: {
                career: '选择了不符合自身价值观的方向，进展卡顿。',
                relationship: '双方价值观存在核心冲突，情感联结逐渐疏离。',
                wealth: '与理念相悖的伙伴合作导致收益受损。',
                health: '情感矛盾引发精神焦虑，身心紊乱。',
                universal: '需警惕盲目抉择，先梳理自身价值观。',
                transition: '从深度共鸣转向理念冲突的被动转折。'
            },
            scenesEn: {
                career: 'Misalignment with job values; friction with team members.',
                relationship: 'Emotional distance; a relationship struggling with core differences.',
                wealth: 'Financial loss due to mismanaged partnerships or impulsive spending.',
                health: 'Mental stress causing physical fatigue.',
                universal: 'Avoid hasty decisions; re-evaluate what you truly value.',
                transition: 'Shift from harmony to detachment and internal struggle.'
            }
        }
    },

    // 7 - The Chariot 战车
    '008': {
        id: '008',
        name: 'The Chariot',
        nameCn: '战车',
        introduction: '战车代表通过意志力与行动力克服困难，协调对立力量。',
        introductionEn: 'The Chariot represents victory through willpower, discipline, and the successful integration of opposing forces to move toward a goal.',
        upright: {
            keywords: ['目标驱动', '克服阻碍', '行动力', '胜利', '自律'],
            keywordsEn: ['Willpower', 'Determination', 'Victory', 'Control', 'Success', 'Action'],
            imageDescription: '武士驾驭双狮战车前行，背景是规整城市。',
            imageDescriptionEn: 'A warrior in a chariot pulled by two opposing sphinxes/lions, heading forward from a city, showing total control.',
            generalMeaning: '处于目标清晰的推进阶段，借由强大行动力克服阻碍。',
            generalMeaningEn: 'A period of momentum. Success is achieved through maintaining focus and driving through obstacles.',
            scenes: {
                career: '明确目标，高效推进项目，攻克学业难点。',
                relationship: '主动协调关系分歧，将矛盾转化为动力。',
                wealth: '制定清晰财务目标，通过主动规划实现积累。',
                health: '明确健康目标，克服惰性坚持运动。',
                universal: '当下需集中力量，向目标主动推进。',
                transition: '从资源分散转向目标明确的正向转折。'
            },
            scenesEn: {
                career: 'Aggressively pursuing career milestones; overcoming work challenges.',
                relationship: 'Resolving conflicts to move the relationship forward; taking control of one\'s love life.',
                wealth: 'Focused efforts lead to financial gains; overcoming past debt.',
                health: 'Strict discipline in diet and exercise yielding great results.',
                universal: 'Take the reins of your life; success comes from sheer determination.',
                transition: 'Shift from stagnation to swift, controlled progress.'
            }
        },
        reversed: {
            keywords: ['失控混乱', '目标动摇', '涣散', '阻碍反噬'],
            keywordsEn: ['Aggression', 'Loss of direction', 'Lack of control', 'Failure', 'Chaos'],
            imageDescription: '逆位中驾驶者姿态慌乱，整体氛围是力量溃散。',
            imageDescriptionEn: 'The driver loses control; the chariot is aimless or overturned, symbolizing scattered energy.',
            generalMeaning: '陷入目标动摇的停滞阶段，行动力涣散。',
            generalMeaningEn: 'Energy is misdirected or lacking discipline. You may be facing obstacles you cannot yet manage.',
            scenes: {
                career: '目标摇摆，无法协调团队矛盾，进展受阻。',
                relationship: '分歧激化，导致关系陷入冷战或冲突。',
                wealth: '财务目标混乱，盲目行动引发损耗。',
                health: '目标动摇，作息中断，身体状态下滑。',
                universal: '需暂停推进，先梳理目标与矛盾。',
                transition: '从目标明确转向力量涣散的被动转折。'
            },
            scenesEn: {
                career: 'Lack of focus causing projects to fail; loss of authority.',
                relationship: 'A relationship spiraling out of control due to excessive aggression or apathy.',
                wealth: 'Reckless financial decisions; loss of money through lack of direction.',
                health: 'Giving up on health goals; physical burnout.',
                universal: 'Check your impulses; you need a better plan before moving forward.',
                transition: 'Shift from momentum to loss of control and frustration.'
            }
        }
    },

    // 8 - Strength 力量
    '009': {
        id: '009',
        name: 'Strength',
        nameCn: '力量',
        introduction: '力量的核心围绕温柔的内在掌控、以包容驯服力量展开。',
        introductionEn: 'Strength represents the power of the human spirit. It is about taming our wilder instincts with compassion and patience rather than brute force.',
        upright: {
            keywords: ['内在韧性', '温柔掌控', '耐心包容', '心灵力量', '勇气'],
            keywordsEn: ['Courage', 'Persuasion', 'Influence', 'Compassion', 'Patience', 'Soft power'],
            imageDescription: '女性温柔拥抱雄狮，上方有无限符号。',
            imageDescriptionEn: 'A woman calmly closes a lion\'s jaw, an infinity symbol above her head, showing mastery over fear.',
            generalMeaning: '用内在温柔力量调和冲突，推动事务向和谐方向发展。',
            generalMeaningEn: 'A stage of using inner resilience. It suggests that you can handle a difficult situation with grace and persistence.',
            scenes: {
                career: '耐心沟通协调团队冲突，不靠强硬施压。',
                relationship: '以包容理解化解矛盾，关系转向和谐。',
                wealth: '靠稳健耐心积累资源，不急躁冒进。',
                health: '通过情绪疏导缓解疲惫，身体随心态改善。',
                universal: '当下需依托内在温柔力量。',
                transition: '从强硬碰壁转向调和冲突的正向转折。'
            },
            scenesEn: {
                career: 'Leading through empathy; staying calm under high workplace stress.',
                relationship: 'Healing a bond through forgiveness and soft influence.',
                wealth: 'Disciplined and steady financial growth; avoiding panic selling.',
                health: 'Recovery through mental fortitude and gentle self-care.',
                universal: 'Your kindness is your greatest power; stay steadfast.',
                transition: 'Shift from external struggle to internal mastery and peace.'
            }
        },
        reversed: {
            keywords: ['蛮力失控', '缺乏耐心', '内在软弱', '情绪激化'],
            keywordsEn: ['Self-doubt', 'Weakness', 'Insecurity', 'Impatience', 'Aggression'],
            imageDescription: '逆位中雄狮躁动挣脱，整体氛围是混乱感。',
            imageDescriptionEn: 'The lion overpowers the woman, or she looks frightened, showing fear winning over logic.',
            generalMeaning: '陷入依靠蛮力或缺乏耐心的状态，事务陷入内耗。',
            generalMeaningEn: 'Feeling weak or overwhelmed by instinct. A lack of self-confidence or an outburst of raw emotion.',
            scenes: {
                career: '强硬态度引发抵触，事务推进受阻。',
                relationship: '情绪失控引发矛盾升级，无法包容。',
                wealth: '急躁冒进投资，频繁调整导致亏损。',
                health: '情绪焦虑引发身体紧绷，无法调解。',
                universal: '需收敛蛮力，回归内在耐心。',
                transition: '从温柔掌控转向冲突反噬的被动转折。'
            },
            scenesEn: {
                career: 'Bullying tactics at work backfire; feeling inadequate for a role.',
                relationship: 'Aggressive behavior or excessive insecurity damaging a bond.',
                wealth: 'Impulsive financial moves driven by fear or greed.',
                health: 'Physical decline due to lack of self-discipline or depression.',
                universal: 'Find your inner core again; don\'t let fear rule you.',
                transition: 'Shift from calm strength to reactive weakness.'
            }
        }
    },

    // 9 - The Hermit 隐士
    '010': {
        id: '010',
        name: 'The Hermit',
        nameCn: '隐士',
        introduction: '隐士的核心围绕独处中的内省、深层智慧的追寻展开。',
        introductionEn: 'The Hermit represents the search for inner truth. It is a card of introspection, solitude, and guidance from within.',
        upright: {
            keywords: ['独处内省', '智慧沉淀', '自我指引', '深度思考'],
            keywordsEn: ['Solitude', 'Introspection', 'Guidance', 'Inner search', 'Wisdom'],
            imageDescription: '隐士持灯笼与手杖站立于雪山。',
            imageDescriptionEn: 'An elderly man stands alone on a mountain peak, holding a lantern with a six-pointed star, lighting the way.',
            generalMeaning: '主动独处沉淀，借由向内探索获得深层智慧。',
            generalMeaningEn: 'A time for quiet reflection. You are encouraged to withdraw from the world to find answers within yourself.',
            scenes: {
                career: '独处深耕专业领域，突破学业/工作瓶颈。',
                relationship: '暂时独处梳理情感需求，而非盲目迎合。',
                wealth: '保守沉淀资产，借独处梳理财务规划。',
                health: '独处调理身心，缓解外界疲惫。',
                universal: '当下适合主动抽离喧嚣，向内探索。',
                transition: '从向外奔波转向向内沉淀的正向转折。'
            },
            scenesEn: {
                career: 'Research and deep study; taking a sabbatical to find your path.',
                relationship: 'Taking a break to understand your own needs before committing.',
                wealth: 'Low-risk financial strategies; prioritizing long-term security over gossip.',
                health: 'Meditation and mental rest are crucial for healing.',
                universal: 'Seek the truth inside you; the noise of others is a distraction.',
                transition: 'Shift from social noise to internal clarity.'
            }
        },
        reversed: {
            keywords: ['过度孤立', '逃避社交', '自我封闭', '脱离现实'],
            keywordsEn: ['Isolation', 'Loneliness', 'Withdrawal', 'Reclusiveness', 'Paranoia'],
            imageDescription: '逆位中灯笼光芒微弱，氛围是封闭逃避。',
            imageDescriptionEn: 'The Hermit is trapped in his own head; the lantern is dim, indicating a lost sense of direction.',
            generalMeaning: '陷入被动独处状态，以封闭逃避现实问题。',
            generalMeaningEn: 'Too much solitude leading to loneliness. You may be ignoring sound advice or becoming disconnected from reality.',
            scenes: {
                career: '闭门造车脱离实际，拒绝外界交流导致停滞。',
                relationship: '逃避沟通而长期孤立，关系逐渐疏离。',
                wealth: '保守过度错失机遇，因封闭拒绝财务资讯。',
                health: '独处引发焦虑压抑，身体状态下滑。',
                universal: '需适度打开自我，外界反馈能打破停滞。',
                transition: '从沉静内省转向脱离现实的被动转折。'
            },
            scenesEn: {
                career: 'Isolation in the office; missing important collaborative opportunities.',
                relationship: 'Avoiding intimacy; being the "cold" partner who won\'t talk.',
                wealth: 'Financial mistakes made in secret; refusal to seek financial advice.',
                health: 'Social anxiety or depression worsening due to total withdrawal.',
                universal: 'Reconnect with the world; you have been alone for too long.',
                transition: 'Shift from healthy reflection to unhealthy isolation.'
            }
        }
    },

    // 10 - Wheel of Fortune 命运之轮
    '011': {
        id: '011',
        name: 'Wheel of Fortune',
        nameCn: '命运之轮',
        introduction: '象征命运循环、无常变化与顺势而为。',
        introductionEn: 'The Wheel of Fortune symbolizes the cycles of life, karma, and the ever-changing nature of fate. It reminds us that "this too shall pass."',
        upright: {
            keywords: ['命运流转', '机遇降临', '周期上扬', '命中注定'],
            keywordsEn: ['Luck', 'Karma', 'Cycles', 'Turning point', 'Fate', 'Chance'],
            imageDescription: '多彩轮盘背景是云层与闪电，四灵环绕。',
            imageDescriptionEn: 'A wheel inscribed with symbols rotates in the sky, surrounded by a sphinx and the four elemental creatures.',
            generalMeaning: '处于命运周期上升阶段，机遇随运势自然而来。',
            generalMeaningEn: 'A positive turning point. Good luck is approaching, and you are encouraged to follow the flow of events.',
            scenes: {
                career: '遇到行业趋势机遇，学业借力突破瓶颈。',
                relationship: '结识新的契合伙伴，现有关系外部契机升温。',
                wealth: '获得意外收益，财富随周期进入上升区间。',
                health: '运势带动身心回升，小不适自然缓解。',
                universal: '当下是顺应趋势的关键期，把握机遇。',
                transition: '从运势低谷转向时运上扬的正向转折。'
            },
            scenesEn: {
                career: 'Sudden promotion or a lucky break in your field; success through timing.',
                relationship: 'A "meant-to-be" encounter; relationships shifting into a better phase.',
                wealth: 'Financial windfall; an investment finally paying off.',
                health: 'Rapid improvement in health; vitality returns.',
                universal: 'Accept change; the universe is conspiring in your favor.',
                transition: 'Shift from bad luck to a new, fortunate cycle.'
            }
        },
        reversed: {
            keywords: ['运势低迷', '周期回落', '机遇错失', '抗拒变动'],
            keywordsEn: ['Bad luck', 'Resisting change', 'Breaking cycles', 'Chaos'],
            imageDescription: '逆位中轮盘色块黯淡，传递出滞涩感。',
            imageDescriptionEn: 'The wheel turns against you; the symbols feel chaotic, indicating a downward spiral.',
            generalMeaning: '处于回落阶段，逆势而为会加剧被动。',
            generalMeaningEn: 'Things are not going your way. This is a time to break negative patterns rather than forcing a change.',
            scenes: {
                career: '遭遇行业低谷，错失趋势机遇，进展停滞。',
                relationship: '时运波动引发矛盾，外部契机加剧分歧。',
                wealth: '意外损耗，财富状态进入收缩区间。',
                health: '身心下滑，小不适被放大，恢复变慢。',
                universal: '需放下抗拒，接受周期回落的状态。',
                transition: '从运势上扬转向周期低迷的被动转折。'
            },
            scenesEn: {
                career: 'Setbacks at work; bad timing leads to project failure.',
                relationship: 'A run of misunderstandings; feeling like external forces are tearing you apart.',
                wealth: 'Unexpected expenses; financial drought.',
                health: 'Slow recovery; feeling like the "odds are against" your healing.',
                universal: 'Don\'t panic; wait for the wheel to turn again.',
                transition: 'Shift from success to temporary hardship.'
            }
        }
    },

    // 11 - Justice 正义
    '012': {
        id: '012',
        name: 'Justice',
        nameCn: '正义',
        introduction: '正义代表因果、真理与公平的裁断。',
        introductionEn: 'Justice represents fairness, truth, and the law of cause and effect. It is a reminder that actions have consequences.',
        upright: {
            keywords: ['公正裁决', '因果法则', '真相揭示', '责任'],
            keywordsEn: ['Fairness', 'Equality', 'Truth', 'Law', 'Accountability', 'Balance'],
            imageDescription: '女神持天平与宝剑坐于立柱间。',
            imageDescriptionEn: 'A figure sits between two pillars, holding a balanced scale in one hand and a sword in the other.',
            generalMeaning: '真相将被揭示，依循因果法则行事能获正当结果。',
            generalMeaningEn: 'A time for objectivity. If you have been honest, justice will be served in your favor.',
            scenes: {
                career: '付出获公正回报，适合处理法律事务。',
                relationship: '矛盾需公正对待，双方责任明确划分。',
                wealth: '财务反映个人努力，理性权衡避投机。',
                health: '身体是生活习惯真实反映，正视问题。',
                universal: '当下需理性权衡，因果法则会带来结果。',
                transition: '从模糊混乱转向责任清晰的正向转折。'
            },
            scenesEn: {
                career: 'Contracts and legal matters resolved; receiving fair credit for work.',
                relationship: 'Balance in a partnership; deciding to stay or leave based on facts.',
                wealth: 'A fair settlement; stable financial ethics.',
                health: 'Achieving balance through structured habits.',
                universal: 'The truth will come out; take responsibility for your part.',
                transition: 'Shift from confusion to a fair resolution.'
            }
        },
        reversed: {
            keywords: ['不公正', '逃避责任', '偏见', '真相被掩'],
            keywordsEn: ['Unfairness', 'Lack of accountability', 'Dishonesty', 'Legal flaws'],
            imageDescription: '天平倾斜失衡，背景立柱歪斜。',
            imageDescriptionEn: 'The scales are tipped; the sword is blunt or misused, representing injustice.',
            generalMeaning: '陷入不公正处境或逃避责任，事务陷入偏颇。',
            generalMeaningEn: 'Situations are being handled unfairly. You may be avoiding the consequences of your actions.',
            scenes: {
                career: '遭遇不公正待遇，或逃避责任推诿他人。',
                relationship: '存在不公平付出，一方逃避责任。',
                wealth: '财务遭遇损失，或不诚信导致资产受损。',
                health: '忽视身体警示信号，逃避健康责任。',
                universal: '需正视逃避责任，回归法则正轨。',
                transition: '从公正明朗转向偏颇失衡的被动转折。'
            },
            scenesEn: {
                career: 'Workplace bias; legal issues not going your way.',
                relationship: 'An imbalanced relationship where one person does all the work.',
                wealth: 'Financial dishonesty; unfair distribution of assets.',
                health: 'Ignored symptoms or lack of balance causing issues.',
                universal: 'Face the truth you are hiding from; honesty is required.',
                transition: 'Shift from balance to corruption or imbalance.'
            }
        }
    },

    // 12 - The Hanged Man 倒吊人
    '013': {
        id: '013',
        name: 'The Hanged Man',
        nameCn: '倒吊人',
        introduction: '倒吊人象征主动牺牲、视角转换与等待时机。',
        introductionEn: 'The Hanged Man represents a pause, a surrender, and looking at life from a completely different perspective.',
        upright: {
            keywords: ['主动牺牲', '视角转换', '暂停', '等待时机'],
            keywordsEn: ['Surrender', 'Letting go', 'Perspective', 'Pause', 'Enlightenment'],
            imageDescription: '人物倒吊于树上，面容平静有光环。',
            imageDescriptionEn: 'A man hangs upside down by one foot from a T-shaped tree; his face is serene with a bright halo.',
            generalMeaning: '通过放下执念获新洞见，悬而未决带来内在成长。',
            generalMeaningEn: 'Letting go of control. By stopping your frantic activity, you gain clarity and spiritual growth.',
            scenes: {
                career: '暂停急于求成，换角度看困境会发现突破口。',
                relationship: '放下执念与控制，以开放心态理解对方。',
                wealth: '暂缓激进决策，等待更好时机更稳妥。',
                health: '放慢生活节奏，内在调养比外在重要。',
                universal: '当下需主动放慢脚步，转换视角看问题。',
                transition: '从急躁推进转向主动暂停的正向转折。'
            },
            scenesEn: {
                career: 'A period of stagnation that allows for strategic re-thinking; career shift.',
                relationship: 'A relationship in "wait" mode; gaining insight before moving forward.',
                wealth: 'Sacrificing short-term spending for long-term gain.',
                health: 'Rest is the best medicine; changing your view on illness.',
                universal: 'Don\'t force it; some things require patience and surrender.',
                transition: 'Shift from struggle to peaceful observation.'
            }
        },
        reversed: {
            keywords: ['拖延逃避', '无谓牺牲', '固执', '拒绝改变'],
            keywordsEn: ['Stalling', 'Resistance', 'Avoidance', 'Stagnation', 'Fear of change'],
            imageDescription: '逆位中人物姿态紧绷，光晕黯淡消失。',
            imageDescriptionEn: 'The figure struggles against the rope; the halo is gone, showing a refusal to see new truths.',
            generalMeaning: '陷入被动停滞或无谓牺牲，固执导致事务卡顿。',
            generalMeaningEn: 'A refusal to let go. You are stuck in an old way of thinking and it\'s making you suffer unnecessarily.',
            scenes: {
                career: '以拖延逃避问题，固守无效的工作方式。',
                relationship: '不愿为关系做改变，双方僵持不下。',
                wealth: '因拖延错失机会，或无谓付出无回报。',
                health: '忽视需要休息的信号，硬撑导致恶化。',
                universal: '当下需打破僵局，主动寻求改变。',
                transition: '从内在沉淀转向被动停滞的消极转折。'
            },
            scenesEn: {
                career: 'Boredom at work; being stuck in a rut without learning.',
                relationship: 'Making sacrifices for a partner who doesn\'t care; being stuck.',
                wealth: 'Wasting money on things that bring no value.',
                health: 'Physical burnout from refusing to rest.',
                universal: 'Stop waiting for things to change on their own; take action.',
                transition: 'Shift from waiting to wasted time.'
            }
        }
    },

    // 13 - Death 死神
    '014': {
        id: '014',
        name: 'Death',
        nameCn: '死神',
        introduction: '死神并非肉体死亡，而是旧阶段终结与新生的必然。',
        introductionEn: 'Death signifies the end of a major phase or aspect of your life that is no longer serving you, clearing space for something new.',
        upright: {
            keywords: ['终结重生', '彻底转变', '放下过去', '蜕变'],
            keywordsEn: ['Endings', 'Transformation', 'Transition', 'Letting go', 'Rebirth'],
            imageDescription: '死神骑白马前行，背景是新生旭日。',
            imageDescriptionEn: 'A skeleton in black armor rides a white horse; a sun rises in the distance as old structures fall.',
            generalMeaning: '旧阶段彻底终结，必须放下过去才能拥抱新生。',
            generalMeaningEn: 'A major transformation. Something must end for a better beginning to happen. Do not fear it.',
            scenes: {
                career: '旧模式需彻底放弃，新的职场方向正开启。',
                relationship: '不再适合的关系需结束，腾出空间迎接更佳。',
                wealth: '旧模式需调整，新的增长点正在形成。',
                health: '不良习惯需改变，身体将在调整后转好。',
                universal: '勇敢告别过去，终结是新生的前提。',
                transition: '从旧循环终结转向新阶段开启的重大转折。'
            },
            scenesEn: {
                career: 'Changing careers entirely; the end of a project or job role.',
                relationship: 'A major breakup or a total transformation of how you love.',
                wealth: 'Changing your financial habits drastically; a complete reset.',
                health: 'Cutting out toxins; a total change in lifestyle.',
                universal: 'Out with the old, in with the new. Acceptance is key.',
                transition: 'A powerful shift from ending to beginning.'
            }
        },
        reversed: {
            keywords: ['抗拒改变', '无法放手', '转变受阻', '恐惧终结'],
            keywordsEn: ['Resistance to change', 'Stagnation', 'Decay', 'Fear of letting go'],
            imageDescription: '逆位中死神前行受阻，日出被乌云遮蔽。',
            imageDescriptionEn: 'The horse stops; the sun is obscured, showing someone clinging to a dead situation.',
            generalMeaning: '因恐惧抗拒必要的终结，导致持续的停滞消耗。',
            generalMeaningEn: 'You are resisting the inevitable. Clinging to the past is preventing you from growing.',
            scenes: {
                career: '抗拒职业转型，固守已失效模式导致受阻。',
                relationship: '无法放下不适合的关系，恐惧孤独持续痛苦。',
                wealth: '抗拒调整财务策略，固守亏损投资导致缩水。',
                health: '不愿改变不良习惯，身体状态持续恶化。',
                universal: '需正视恐惧，抗拒只会延长痛苦。',
                transition: '从新生转折转向抗拒改变的停滞转折。'
            },
            scenesEn: {
                career: 'Staying in a dead-end job out of fear; resisting new technology.',
                relationship: 'Clinging to a toxic relationship to avoid being alone.',
                wealth: 'Refusing to sell a failing asset; worsening debt.',
                health: 'Returning to old bad habits after a brief healthy stint.',
                universal: 'The longer you wait, the harder the change will be.',
                transition: 'Shift from transformation to decay.'
            }
        }
    },

    // 14 - Temperance 节制
    '015': {
        id: '015',
        name: 'Temperance',
        nameCn: '节制',
        introduction: '节制代表平衡、调和、融合与找到中庸之道。',
        introductionEn: 'Temperance is the card of balance, moderation, and patience. It involves merging dualities to create harmony.',
        upright: {
            keywords: ['平衡调和', '中道融合', '耐心', '适度有度'],
            keywordsEn: ['Balance', 'Moderation', 'Patience', 'Purpose', 'Harmony'],
            imageDescription: '天使站立水边，将水在两杯间自如流动。',
            imageDescriptionEn: 'An angel pours liquid between two cups; one foot is on land and one in water, showing perfect balance.',
            generalMeaning: '调和对立寻找平衡，以耐心和节制推进事务。',
            generalMeaningEn: 'You are finding inner peace. It is a sign to stay calm and avoid extremes to achieve long-term success.',
            scenes: {
                career: '协调工作生活平衡，以稳健节奏推进项目。',
                relationship: '调和双方差异，以包容态度化解矛盾。',
                wealth: '收支保持平衡，投资策略稳健适度。',
                health: '身心达到平衡状态，作息饮食规律适度。',
                universal: '需以中道应对，避免极端，平衡是关键。',
                transition: '从失衡动荡转向平衡和谐的正向转折。'
            },
            scenesEn: {
                career: 'Smooth workflow; successfully managing diverse tasks without stress.',
                relationship: 'A harmonious partnership; calm communication and mutual growth.',
                wealth: 'Healthy cash flow; careful budgeting and moderate growth.',
                health: 'Holistic health; balance between exercise and rest.',
                universal: 'Take the middle path; stability comes through moderation.',
                transition: 'Shift from chaos to a balanced state of flow.'
            }
        },
        reversed: {
            keywords: ['失去平衡', '过度极端', '不和谐', '冲突激化'],
            keywordsEn: ['Imbalance', 'Excess', 'Lack of vision', 'Discord', 'Impatience'],
            imageDescription: '逆位中水从杯中溢出，天使姿态摇晃失衡。',
            imageDescriptionEn: 'The cups spill; the angel is unsteady, symbolizing a loss of focus and self-control.',
            generalMeaning: '陷入失衡极端状态，缺乏耐心导致冲突激化。',
            generalMeaningEn: 'Living in excess or feeling out of sync. You are struggling to find a stable rhythm.',
            scenes: {
                career: '工作生活严重失衡，急躁推进效率低下。',
                relationship: '双方矛盾激化，无法调和差异陷入冲突。',
                wealth: '收支失衡，投资策略激进极端出现问题。',
                health: '身心失衡，作息饮食紊乱状态下滑。',
                universal: '需回归中道，重建平衡是当下核心。',
                transition: '从平衡和谐转向失衡动荡的消极转折。'
            },
            scenesEn: {
                career: 'Workaholism or total lack of focus; workplace arguments.',
                relationship: 'Hot and cold emotions; inability to compromise.',
                wealth: 'Overspending or gambling; unstable finances.',
                health: 'Binge eating or erratic sleep patterns damaging health.',
                universal: 'You are pushing too hard; stop and find your center.',
                transition: 'Shift from harmony to erratic behavior.'
            }
        }
    },

    // 15 - The Devil 恶魔
    '016': {
        id: '016',
        name: 'The Devil',
        nameCn: '恶魔',
        introduction: '恶魔揭示内心的阴暗面、执念与物质主义的束缚。',
        introductionEn: 'The Devil represents the shadow self, addictions, and the feeling of being trapped by material desires or negative patterns.',
        upright: {
            keywords: ['欲望束缚', '物质执念', '上瘾依赖', '阴暗面'],
            keywordsEn: ['Bondage', 'Addiction', 'Materialism', 'Playfulness', 'Shadow self'],
            imageDescription: '恶魔高坐基座，男女被松散锁链系住。',
            imageDescriptionEn: 'A horned devil sits above a man and woman chained to a block; their chains are loose, suggesting they can leave.',
            generalMeaning: '处于被欲望或执念束缚阶段，需正视阴暗面才获解脱。',
            generalMeaningEn: 'You feel trapped by your own choices or habits. It is a call to recognize the power you actually have to free yourself.',
            scenes: {
                career: '执着成就或物质回报，忽视成长陷入内耗。',
                relationship: '存在不健康依赖或控制，被欲望驱动非真爱。',
                wealth: '被金钱欲望控制，贪婪或挥霍导致紊乱。',
                health: '存在不良嗜好或上瘾行为，身体受损。',
                universal: '正视束缚你的执念，觉醒后锁链可挣脱。',
                transition: '从自由状态陷入欲望束缚的消极转折。'
            },
            scenesEn: {
                career: 'Greed-driven choices; toxic office politics; being a slave to the paycheck.',
                relationship: 'Codependency; obsession with a partner; a purely sexual bond without heart.',
                wealth: 'Debt due to gambling or luxury; greed causing bad decisions.',
                health: 'Substance abuse or obsessive-compulsive behaviors.',
                universal: 'Face your demons; you are the one holding the chain.',
                transition: 'Shift from freedom to self-imposed prison.'
            }
        },
        reversed: {
            keywords: ['挣脱束缚', '觉醒解脱', '面对阴暗', '释放'],
            keywordsEn: ['Release', 'Breaking free', 'Restoration', 'Independence', 'Revelation'],
            imageDescription: '逆位中锁链断裂，男女挣脱束缚，背景现微光。',
            imageDescriptionEn: 'The chains are broken; the figure steps away from the darkness into the light.',
            generalMeaning: '正在挣脱欲望束缚，正视阴暗面后重获自由。',
            generalMeaningEn: 'Breaking free from a negative cycle. You are regaining control of your life and letting go of old addictions.',
            scenes: {
                career: '放下对成就过度执着，回归工作本身意义。',
                relationship: '摆脱不健康关系模式，清醒认识真需。',
                wealth: '戒除贪婪挥霍习惯，财务观念回归理性。',
                health: '开始戒除不良嗜好，身体状态逐步恢复。',
                universal: '正在觉醒，勇敢面对并挣脱束缚。',
                transition: '从欲望束缚转向觉醒解脱的正向转折。'
            },
            scenesEn: {
                career: 'Leaving a toxic job; prioritizing values over money.',
                relationship: 'Ending a codependent relationship; healing from trauma.',
                wealth: 'Paying off debt; breaking free from material greed.',
                health: 'Sobriety; choosing health over momentary pleasure.',
                universal: 'The chains are off; walk away and don\'t look back.',
                transition: 'Shift from bondage to total liberation.'
            }
        }
    },

    // 16 - The Tower 塔
    '017': {
        id: '017',
        name: 'The Tower',
        nameCn: '塔',
        introduction: '塔象征突发的剧变，摧毁虚假基础，虽痛苦但为重建铺路。',
        introductionEn: 'The Tower represents sudden, jarring change. It breaks down structures built on weak foundations to reveal the truth.',
        upright: {
            keywords: ['突然剧变', '结构崩塌', '虚假瓦解', '强制觉醒'],
            keywordsEn: ['Sudden change', 'Upheaval', 'Chaos', 'Revelation', 'Awakening'],
            imageDescription: '高塔被闪电击中，皇冠坠落，人物跌出，火焰燃烧。',
            imageDescriptionEn: 'A bolt of lightning strikes a tower; a crown falls, and people leap into the unknown as flames erupt.',
            generalMeaning: '遭遇突然剧变，虚假结构瓦解，这是真相揭示的开端。',
            generalMeaningEn: 'A moment of crisis that forces you to change. It is painful but necessary for spiritual growth.',
            scenes: {
                career: '工作遭遇突然变故或失败，是调整方向契机。',
                relationship: '关系遭受重大冲击，真相揭示面临分离。',
                wealth: '财务遭遇突然损失，需重新审视理财策略。',
                health: '健康出现突发状况，需要立即重视处理。',
                universal: '剧变虽痛，但能清除虚假，为重建铺路。',
                transition: '从稳定状态突然转向剧烈崩塌的重大转折。'
            },
            scenesEn: {
                career: 'Unexpected layoffs; a project failing completely; sudden professional scandal.',
                relationship: 'A sudden breakup; a deep secret revealed that changes everything.',
                wealth: 'Financial crash; bankruptcy; sudden major loss.',
                health: 'A sudden accident or health emergency.',
                universal: 'Let the old fall; only the truth survives the fire.',
                transition: 'Shift from false security to sudden chaos.'
            }
        },
        reversed: {
            keywords: ['逃避剧变', '延迟崩塌', '内在动荡', '抵抗改变'],
            keywordsEn: ['Avoiding disaster', 'Fear of change', 'Delayed end', 'Internal crisis'],
            imageDescription: '逆位中高塔未完倒塌，闪电余威仍在。',
            imageDescriptionEn: 'The tower is cracked but standing; the inhabitants are frozen in fear inside.',
            generalMeaning: '试图逃避必然剧变，或仍在抗拒影响，内在持续动荡。',
            generalMeaningEn: 'You are avoiding a necessary breakdown. Delaying the inevitable only makes the final collapse harder.',
            scenes: {
                career: '勉强维持无法持续的工作状态，崩塌只是时间。',
                relationship: '明知关系有裂痕不愿面对，内在持续焦虑。',
                wealth: '财务危机已显端倪视而不见，拖延加剧损失。',
                health: '身体已发警报不愿就医，错过最佳时机。',
                universal: '需直面非逃避变化，拖延让冲击更难承受。',
                transition: '从剧变中艰难恢复或持续动荡的过渡。'
            },
            scenesEn: {
                career: 'Clinging to a dying business; ignoring workplace warning signs.',
                relationship: 'Staying in a broken relationship to "keep the peace" while suffering.',
                wealth: 'Ignoring debt collectors; avoiding a necessary financial audit.',
                health: 'Ignoring symptoms; prolonging an unhealthy lifestyle.',
                universal: 'Rip the bandage off; you cannot stop the change, only delay it.',
                transition: 'Shift from external crisis to prolonged internal tension.'
            }
        }
    },

    // 17 - The Star 星星
    '018': {
        id: '018',
        name: 'The Star',
        nameCn: '星星',
        introduction: '象征希望、疗愈、灵感与宇宙的指引。',
        introductionEn: 'The Star brings hope, renewed power, and the strength to continue on your journey. It is the light at the end of the tunnel.',
        upright: {
            keywords: ['希望降临', '内在治愈', '灵感指引', '宁静'],
            keywordsEn: ['Hope', 'Faith', 'Purpose', 'Renewal', 'Spirituality'],
            imageDescription: '女性跪水边倾倒水罐，上方八芒星闪耀。',
            imageDescriptionEn: 'A naked woman pours water onto land and pool; a giant star shines above, surrounded by seven smaller ones.',
            generalMeaning: '处于疗愈与希望阶段，灵感浮现，未来充满光明。',
            generalMeaningEn: 'You are being divinely guided. This is a card of peaceful renewal and a promise of a bright future.',
            scenes: {
                career: '走出低谷灵感涌现，对未来充满信心。',
                relationship: '关系进入温柔治愈阶段，坦诚相待。',
                wealth: '财务状态恢复平稳，对未来规划充满希望。',
                health: '身心进入疗愈恢复期，状态平和改善。',
                universal: '希望之光已降临，保持信任与乐观。',
                transition: '从风暴困境转向平静希望的正向转折。'
            },
            scenesEn: {
                career: 'Creative inspiration returning; pursuing a true calling.',
                relationship: 'Healing after a breakup; a relationship filled with spiritual connection.',
                wealth: 'Financial recovery; believing in your long-term goals.',
                health: 'Peaceful recovery; mental health improving.',
                universal: 'Trust the universe; you are exactly where you need to be.',
                transition: 'Shift from darkness to a hopeful dawn.'
            }
        },
        reversed: {
            keywords: ['失去希望', '信心崩塌', '灵感枯竭', '内在空虚'],
            keywordsEn: ['Lack of faith', 'Despair', 'Discouragement', 'Disconnected'],
            imageDescription: '逆位中星光黯淡，女性姿态疲惫颓丧。',
            imageDescriptionEn: 'The stars are cold; the water is wasted on dry land, symbolizing a loss of inspiration.',
            generalMeaning: '陷入失去希望或信心崩塌，内心空虚迷茫。',
            generalMeaningEn: 'You feel disconnected from your path. You may be focusing on the negative and missing the guidance available to you.',
            scenes: {
                career: '对工作失信心前途渺茫，缺乏动力灵感。',
                relationship: '对感情失希望内心封闭，难感温暖。',
                wealth: '对财务未来悲观，缺乏规划信心。',
                health: '身心疲惫恢复缓慢，情绪低落影响康复。',
                universal: '需重燃内心之光，相信希望的存在。',
                transition: '从希望状态转向信心低迷的消极转折。'
            },
            scenesEn: {
                career: 'Feeling like your dreams are unreachable; creative burnout.',
                relationship: 'Pessimism in love; feeling like you don\'t deserve happiness.',
                wealth: 'Financial hopelessness; ignoring potential solutions.',
                health: 'Chronic fatigue; feeling like you\'ll never get better.',
                universal: 'Don\'t let cynicism win; the star is still there, look up.',
                transition: 'Shift from hope to temporary despair.'
            }
        }
    },

    // 18 - The Moon 月亮
    '019': {
        id: '019',
        name: 'The Moon',
        nameCn: '月亮',
        introduction: '揭示潜意识、幻象、恐惧与直觉的幽暗面。',
        introductionEn: 'The Moon deals with the world of the subconscious. It represents intuition, but also illusions and the fears that hide in the shadows.',
        upright: {
            keywords: ['潜意识', '幻象迷惑', '内心恐惧', '直觉模糊'],
            keywordsEn: ['Illusion', 'Fear', 'Anxiety', 'Subconscious', 'Intuition'],
            imageDescription: '月亮悬于夜空，狗与狼向其嚎叫，龙虾爬出。',
            imageDescriptionEn: 'A moon hangs above a pool where a crayfish emerges; a dog and wolf howl, symbolizing the path between fear and instinct.',
            generalMeaning: '事物不如表面所见，易被幻象迷惑，需依直觉。',
            generalMeaningEn: 'A time of uncertainty. Not everything is as it seems. Trust your gut but be wary of being misled by your own anxiety.',
            scenes: {
                career: '存在不确定因素，信息不全或有欺骗。',
                relationship: '存在未说出口秘密或误解，不够坦诚。',
                wealth: '财务信息不透明，存隐患或欺骗需核实。',
                health: '身体存查明问题，情绪焦虑影响健康。',
                universal: '需穿越幻象看清真相，信任直觉警惕恐惧。',
                transition: '从清晰状态进入迷雾笼罩的过渡。'
            },
            scenesEn: {
                career: 'Vague promises; hidden agendas in the office; uncertainty about the future.',
                relationship: 'Confused emotions; secrets; not knowing where you stand with someone.',
                wealth: 'Hidden financial risks; be careful with investments that look too good.',
                health: 'Hormonal issues or sleep disorders; psychological blockages.',
                universal: 'Be still and listen to your dreams; things will clarify soon.',
                transition: 'Shift from day-light logic to moonlight mystery.'
            }
        },
        reversed: {
            keywords: ['走出迷雾', '真相显现', '恐惧消散', '清明'],
            keywordsEn: ['Release of fear', 'Truth revealed', 'Clarity', 'Subconscious clearing'],
            imageDescription: '逆位中月光清晰，迷雾消散，氛围趋向平稳。',
            imageDescriptionEn: 'The mist clears; the animals are quiet, showing a mind that is finally finding its way.',
            generalMeaning: '正在走出幻象，真相显现，直觉变得可靠。',
            generalMeaningEn: 'The fog is lifting. You are uncovering the truth and realizing that your fears were largely groundless.',
            scenes: {
                career: '工作中的疑虑澄清，隐藏信息浮出水面。',
                relationship: '关系中秘密揭示，双方开始坦诚沟通。',
                wealth: '财务真相明朗，能够做出清晰判断。',
                health: '病因查明，情绪恢复稳定，状态改善。',
                universal: '迷雾消散，保持耐心真相会更清晰。',
                transition: '从迷雾笼罩转向真相显现的正向转折。'
            },
            scenesEn: {
                career: 'Clarification of work roles; exposed lies; seeing a clear path.',
                relationship: 'Secrets coming out; clearing up a major misunderstanding.',
                wealth: 'Recovering from a scam; finding lost money.',
                health: 'Mental clarity; finding the right diagnosis.',
                universal: 'The truth sets you free; walk toward the light.',
                transition: 'Shift from confusion to revelation.'
            }
        }
    },

    // 19 - The Sun 太阳
    '020': {
        id: '020',
        name: 'The Sun',
        nameCn: '太阳',
        introduction: '象征纯粹的快乐、成功、生命力与清晰。',
        introductionEn: 'The Sun is the card of success, joy, and vitality. It radiates warmth and clarity, ensuring that everything will work out perfectly.',
        upright: {
            keywords: ['光明喜悦', '成功达成', '生命活力', '真相明朗'],
            keywordsEn: ['Joy', 'Success', 'Vitality', 'Celebration', 'Positivity'],
            imageDescription: '孩童骑白马在向日葵前欢笑。',
            imageDescriptionEn: 'A naked child rides a white horse under a massive sun, with sunflowers blooming behind a brick wall.',
            generalMeaning: '处于光明与喜悦阶段，事务顺遂，能量充沛。',
            generalMeaningEn: 'The best card in the deck. Success is guaranteed, and you are surrounded by love and positivity.',
            scenes: {
                career: '事业显著成功获认可，前景一片光明。',
                relationship: '关系阳光美好感到满足，可能有喜事。',
                wealth: '财运亨通收益增加，财务状态非常理想。',
                health: '身心状态极佳，精力充沛活力四射。',
                universal: '当下是最美好时期，尽情享受成功。',
                transition: '从困境阴霾转向光明成功的重大转折。'
            },
            scenesEn: {
                career: 'Peak performance; public recognition; a successful launch.',
                relationship: 'Happy marriage; vibrant social life; the birth of a child.',
                wealth: 'Prosperity; freedom from financial worry; growth.',
                health: 'Total vitality; fast recovery; peak fitness.',
                universal: 'Smile; the universe is smiling back at you.',
                transition: 'Shift from shadow to brilliant success.'
            }
        },
        reversed: {
            keywords: ['喜悦受阻', '成功延迟', '消极', '萧条'],
            keywordsEn: ['Temporary depression', 'Lack of enthusiasm', 'Delayed success'],
            imageDescription: '逆位中太阳被云遮挡，向日葵低垂。',
            imageDescriptionEn: 'Clouds pass over the sun; the child looks small, representing a temporary loss of sparkle.',
            generalMeaning: '喜悦有所减损或延迟，总体仍积极向好。',
            generalMeaningEn: 'You are still successful, but you may be struggling to feel the joy of it. Success is just around the corner, don\'t give up.',
            scenes: {
                career: '成功延迟，或因过度自信忽略细节。',
                relationship: '关系有小摩擦喜悦略减，基础仍稳固。',
                wealth: '财运良好不如预期，需调整期望值。',
                health: '活力有所下降，需注意休息调养。',
                universal: '虽有小波折光明仍在，保持乐观。',
                transition: '从圆满成功到略有波折的过渡期。'
            },
            scenesEn: {
                career: 'Success is coming but slower than you wanted; minor ego issues.',
                relationship: 'Taking a partner for granted; minor arguments.',
                wealth: 'Good income but excessive spending; being ungrateful for wealth.',
                health: 'Mild fatigue; need for more Vitamin D or sunlight.',
                universal: 'The sun is still there; just wait for the clouds to pass.',
                transition: 'Shift from radiance to slightly dimmed enthusiasm.'
            }
        }
    },

    // 20 - Judgement 审判
    '021': {
        id: '021',
        name: 'Judgement',
        nameCn: '审判',
        introduction: '象征觉醒的召唤、重生与对自己过去行为的总结。',
        introductionEn: 'Judgement is a call to awaken. It is a card of self-evaluation, rebirth, and making a life-changing decision.',
        upright: {
            keywords: ['觉醒召唤', '终极评判', '灵性重生', '使命'],
            keywordsEn: ['Rebirth', 'Inner calling', 'Absolution', 'Judgment', 'Awakening'],
            imageDescription: '天使吹响号角，人们从大地升起迎接。',
            imageDescriptionEn: 'An angel blows a trumpet from the sky; men, women, and children rise from their coffins, arms outstretched.',
            generalMeaning: '接受更高召唤，对过往总结，灵性重大转变。',
            generalMeaningEn: 'You have reached a crossroads. It is time to let go of the past and step into a new version of yourself.',
            scenes: {
                career: '职业迎来转折点，需做关乎未来的大决定。',
                relationship: '关系进入决定性阶段，需对过往评判。',
                wealth: '财务状况面临评估，需做长期规划决策。',
                health: '健康观念转变，重视身心整体调养。',
                universal: '正接受召唤，回顾总结为新阶段重生。',
                transition: '从日常状态进入关键评判与重生转折。'
            },
            scenesEn: {
                career: 'Finding your true vocation; a final decision on a legal case; promotion.',
                relationship: 'Forgiving past mistakes to move forward; a defining moment in love.',
                wealth: 'A significant financial turning point; debt settlement.',
                health: 'A complete turnaround in health habits; renewal.',
                universal: 'Listen to the call; it is time to move up.',
                transition: 'Shift from old karma to new life.'
            }
        },
        reversed: {
            keywords: ['逃避召唤', '拒绝反省', '自我怀疑', '停滞'],
            keywordsEn: ['Self-doubt', 'Refusal of calling', 'Inner conflict', 'Stalling'],
            imageDescription: '逆位中号角声微弱，人们犹豫不前。',
            imageDescriptionEn: 'The figures are hesitant or hiding; the angel\'s call is ignored, showing a fear of growth.',
            generalMeaning: '逃避内心召唤，错失觉醒机会，陷入怀疑。',
            generalMeaningEn: 'You are ignoring a vital call to change. Fear of being judged or self-doubt is keeping you stuck in the past.',
            scenes: {
                career: '逃避职业重要抉择，因害怕改变错失机会。',
                relationship: '不愿面对关系真实状态，逃避反思。',
                wealth: '拖延财务决策，错过调整最佳时机。',
                health: '忽视身体警示信号，不愿做必要改变。',
                universal: '需勇敢面对内心召唤，逃避延长迷茫。',
                transition: '从觉醒机遇转向逃避停滞的消极转折。'
            },
            scenesEn: {
                career: 'Missing a promotion because of self-doubt; ignoring a clear career path.',
                relationship: 'Dragging out an old argument; refusing to forgive or let go.',
                wealth: 'Poor financial judgment based on past mistakes.',
                health: 'Neglecting a health issue that needs urgent attention.',
                universal: 'Stop judging yourself so harshly; just move forward.',
                transition: 'Shift from awakening to stagnation and guilt.'
            }
        }
    },

    // 21 - The World 世界
    '022': {
        id: '022',
        name: 'The World',
        nameCn: '世界',
        introduction: '塔罗旅程的终点，象征圆满、整合与成功。',
        introductionEn: 'The World represents completion, fulfillment, and harmony. It is the final step of the Fool\'s journey, symbolizing total integration.',
        upright: {
            keywords: ['圆满成就', '旅程完成', '整合统一', '成功'],
            keywordsEn: ['Completion', 'Integration', 'Accomplishment', 'Travel', 'Success'],
            imageDescription: '舞者于花环中漂浮，四灵位于四角。',
            imageDescriptionEn: 'A dancing figure is surrounded by a laurel wreath; the four creatures of the zodiac represent a complete universe.',
            generalMeaning: '阶段性圆满完成，努力汇聚成果，开启新循环。',
            generalMeaningEn: 'You have mastered your environment. Success is complete, and you are ready for a whole new level of existence.',
            scenes: {
                career: '达成重大成就获认可，可能开启更高层面。',
                relationship: '关系达到圆满和谐，或是圆满落幕迎新。',
                wealth: '财务目标达成收获丰厚，进入新层次。',
                health: '身心达到极佳平衡状态，整体健康圆满。',
                universal: '当下收获圆满，尽情享受同时为新循环准备。',
                transition: '从追求过程进入圆满达成的终极转折。'
            },
            scenesEn: {
                career: 'Global success; finishing a major project; total professional mastery.',
                relationship: 'A perfect match; marriage; feeling whole as an individual.',
                wealth: 'Financial independence; the completion of a long-term investment.',
                health: 'Total well-being; feeling physically and mentally "complete."',
                universal: 'The world is yours; celebrate your victory.',
                transition: 'Shift from effort to ultimate realization.'
            }
        },
        reversed: {
            keywords: ['未竟之业', '无法完成', '延迟圆满', '受阻'],
            keywordsEn: ['Incompletion', 'Shortcuts', 'Delayed success', 'Stagnation'],
            imageDescription: '逆位中花环松散断裂，舞者姿态失衡。',
            imageDescriptionEn: 'The wreath is broken; the dancer is clumsy, indicating a project that lacks a proper finish.',
            generalMeaning: '无法达成圆满或延迟，存在未了结的事务。',
            generalMeaningEn: 'You are close to the end, but something is missing. You may be taking shortcuts that prevent a true sense of completion.',
            scenes: {
                career: '工作接近完成却遇阻碍，感觉缺少成就感。',
                relationship: '关系未能达到理想状态，存未解决问题。',
                wealth: '财务目标尚未完全达成，需继续努力。',
                health: '身心状态尚未平衡，仍需调养。',
                universal: '距离圆满还有距离，需耐心完成未竟事。',
                transition: '从接近圆满到暂时受阻的过渡期。'
            },
            scenesEn: {
                career: 'A project that is "almost" done; feeling unfulfilled in success.',
                relationship: 'A relationship that feels like a dead end or lacks closure.',
                wealth: 'Financial goals just out of reach; nearly independent but not quite.',
                health: 'Unresolved health issues; a feeling of physical imbalance.',
                universal: 'Tie up the loose ends before you try to start over.',
                transition: 'Shift from mastery to an incomplete cycle.'
            }
        }
    },

    // ========== Minor Arcana - Wands (权杖) ==========
    // (篇幅原因，以下展示权杖王牌，其余牌组翻译逻辑一致)

    '023': {
        id: '023',
        name: 'Ace of Wands',
        nameCn: '权杖王牌',
        introduction: '象征新的开始、创造力的火花与行动的起点。',
        introductionEn: 'The Ace of Wands is the seed of fire. It represents a new spark of inspiration, a bold beginning, and pure creative potential.',
        upright: {
            keywords: ['新开始', '创造力', '热情', '灵感', '行动力'],
            keywordsEn: ['Inspiration', 'New potential', 'Growth', 'Willpower', 'Opportunity'],
            imageDescription: '手从云中伸出，握着发芽的权杖。',
            imageDescriptionEn: 'A hand emerges from a cloud, clutching a blooming wand, symbolizing the birth of a new idea.',
            generalMeaning: '新的机会和灵感来临，是开启新计划的好时机。',
            generalMeaningEn: 'A breakthrough is here. This is the green light to pursue a new project or passion with full force.',
            scenes: {
                career: '事业有新机遇或新项目，充满动力。',
                relationship: '感情上可能有新的吸引力或新开始。',
                wealth: '财务有新收入来源或投资机会。',
                health: '精力充沛，活力十足。',
                universal: '当下是开始新事物的最佳时机，行动。',
                transition: '从停滞转向新开始的正向转折。'
            },
            scenesEn: {
                career: 'Launching a startup; a brilliant business idea; new creative role.',
                relationship: 'Love at first sight; a new spark in a long-term bond.',
                wealth: 'An unexpected financial offer; the start of a profitable venture.',
                health: 'High energy; starting a new fitness journey.',
                universal: 'Seize the day; your passion is your compass.',
                transition: 'Shift from thought to initial bold action.'
            }
        },
        reversed: {
            keywords: ['延迟', '缺乏热情', '创意受阻', '浪费潜力'],
            keywordsEn: ['Lack of direction', 'Distractions', 'Delays', 'Creativity block'],
            imageDescription: '权杖失去生机，灵感枯竭。',
            imageDescriptionEn: 'The wand is limp or falling; leaves are withering, showing wasted energy.',
            generalMeaning: '新计划受阻或缺乏动力，灵感难付诸行动。',
            generalMeaningEn: 'A false start. You may have the idea but lack the energy or resources to make it happen.',
            scenes: {
                career: '新项目可能延迟或面临重重障碍。',
                relationship: '新感情无法开始或缺乏后续热情。',
                wealth: '新的财务机会可能因迟疑而错失。',
                health: '精力不足，动力持续下降。',
                universal: '需重新激发热情，克服内在阻碍。',
                transition: '新开始受阻的消极转折。'
            },
            scenesEn: {
                career: 'Creative block; a project that fails to launch; loss of drive.',
                relationship: 'A short-lived romance; lack of spark.',
                wealth: 'Investment delays; missing a deadline for a financial deal.',
                health: 'Burnout; feeling physically drained.',
                universal: 'Refocus your passion; don\'t rush if the spark isn\'t there.',
                transition: 'Shift from inspiration to frustration.'
            }
        }
    },

    // 024 - Two of Wands 权杖二
    '024': {
        id: '024',
        name: 'Two of Wands',
        nameCn: '权杖二',
        introduction: '权杖二象征规划与决策，站在城堡上眺望世界，手握权杖与地球，代表对未来的规划与选择。',
        introductionEn: 'The Two of Wands symbolizes discovery and long-term planning. It represents a state of having achieved initial success and now standing at a crossroads, deciding how to expand your horizons and manifest your future.',
        upright: {
            keywords: ['规划', '决策', '探索', '远见', '合作', '选择'],
            keywordsEn: ['Planning', 'Decision Making', 'Discovery', 'Foresight', 'Progress', 'Confidence'],
            imageDescription: '一人站在城堡上，一手持权杖，一手持地球，眺望远方。',
            imageDescriptionEn: 'A man stands on a balcony overlooking the sea and mountains, holding a small globe in one hand and a wand in the other, symbolizing his command over his future.',
            generalMeaning: '处于规划未来的阶段，有远见地做出选择，探索新可能。',
            generalMeaningEn: 'A phase of strategic planning and looking at the big picture. You have the power to choose your path and the confidence to explore new possibilities.',
            scenes: {
                career: '事业上规划长远发展，考虑新方向或合作。',
                relationship: '感情上考虑关系的未来走向。',
                wealth: '财务规划与投资决策。',
                health: '制定健康计划。',
                universal: '当下是规划与决策的时期，展望未来。',
                transition: '从单一走向探索更多可能的转折。'
            },
            scenesEn: {
                career: 'Long-term business strategy; deciding between two job offers; expanding professional networks.',
                relationship: 'Discussing future goals with a partner; planning a life journey together.',
                wealth: 'Diversifying investments; long-term financial forecasting.',
                health: 'Creating a sustainable long-term wellness plan.',
                universal: 'The world is in your hands; plan your next move with courage.',
                transition: 'A shift from initial ideas to concrete, visionary planning.'
            }
        },
        reversed: {
            keywords: ['缺乏规划', '恐惧变化', '犹豫不决', '局限', '错失机会'],
            keywordsEn: ['Lack of Planning', 'Fear of Unknown', 'Indecision', 'Playing it Safe', 'Over-analysis'],
            imageDescription: '眺望受阻，无法看清远方，决策困难。',
            imageDescriptionEn: 'The horizon is blurred; the globe seems heavy or neglected, representing a hesitation to leave one\'s comfort zone.',
            generalMeaning: '缺乏远见或害怕做出选择，停留在舒适区。',
            generalMeaningEn: 'Indicates a fear of stepping out. You may be over-planning to avoid action or feeling restricted by your own indecision.',
            scenes: {
                career: '事业发展缺乏规划或害怕改变。',
                relationship: '感情上不敢做出决定。',
                wealth: '财务规划不足。',
                health: '健康管理缺乏计划。',
                universal: '当下需要勇敢面对选择，不要害怕变化。',
                transition: '从远见转向局限的消极转折。'
            },
            scenesEn: {
                career: 'Staying in an unfulfilling job due to fear; lack of professional direction.',
                relationship: 'Uncertainty about a partner; avoiding talks about the future.',
                wealth: 'Disorganized finances; missing out on opportunities due to hesitation.',
                health: 'Start-and-stop fitness attempts; lack of a clear health goal.',
                universal: 'Break free from the fear of the unknown; commit to a direction.',
                transition: 'A shift from broad vision to narrow-minded hesitation.'
            }
        }
    },

    // 025 - Three of Wands 权杖三
    '025': {
        id: '025',
        name: 'Three of Wands',
        nameCn: '权杖三',
        introduction: '权杖三象征进展与扩展，站在高处眺望远航的船只，代表等待成果、发展与机遇的到来。',
        introductionEn: 'The Three of Wands represents the first signs of success and expansion. Your plans are in motion, and you are now watching your efforts begin to bear fruit while looking toward even greater opportunities.',
        upright: {
            keywords: ['进展', '扩展', '远见', '等待成果', '旅行', '机遇'],
            keywordsEn: ['Expansion', 'Growth', 'Foresight', 'Overseas Opportunities', 'Confidence', 'Vision'],
            imageDescription: '一人站在高处，三根权杖立于身旁，眺望远方归来的船只。',
            imageDescriptionEn: 'A figure stands on a cliff edge, looking out across the sea at ships. Three wands are planted in the ground, showing a stable foundation for growth.',
            generalMeaning: '计划正在顺利进展，扩展视野，等待努力的成果。',
            generalMeaningEn: 'Progress is steady. You have laid the foundation and are now expanding your vision or waiting for your "ships to come in."',
            scenes: {
                career: '事业有进展，等待项目成果或业务扩展。',
                relationship: '感情稳步发展，关系有进展。',
                wealth: '投资开始见回报，财务扩展。',
                health: '身体状态逐步改善。',
                universal: '当下耐心等待，成果即将到来。',
                transition: '从起步进入进展顺利的正向阶段。'
            },
            scenesEn: {
                career: 'Business expansion; international trade; seeing the first results of a successful project.',
                relationship: 'Relationships growing stronger through shared travel or future visions.',
                wealth: 'Waiting for long-term investments to mature; financial growth.',
                health: 'Entering a stage of steady physical recovery and improvement.',
                universal: 'Be patient and keep looking forward; your efforts are moving in the right direction.',
                transition: 'A positive shift from planning to witnessing progress.'
            }
        },
        reversed: {
            keywords: ['进展缓慢', '阻碍', '失望', '计划受挫', '延迟'],
            keywordsEn: ['Delay', 'Lack of Progress', 'Disappointment', 'Broken Promises', 'Frustrated Ambition'],
            imageDescription: '船只迟迟未归，等待落空。',
            imageDescriptionEn: 'The ships are lost at sea or stuck; the figure looks tired from waiting, symbolizing delayed rewards.',
            generalMeaning: '进展不如预期，可能遇到障碍或延迟。',
            generalMeaningEn: 'A lack of progress or a delay in results. External obstacles may be hindering your growth, or your vision was too narrow.',
            scenes: {
                career: '事业进展缓慢或遇阻。',
                relationship: '感情发展停滞。',
                wealth: '投资回报延迟。',
                health: '康复进程缓慢。',
                universal: '当下需调整期望，耐心应对延迟。',
                transition: '从进展转向受阻的消极转折。'
            },
            scenesEn: {
                career: 'Trade delays; project setbacks; feeling that career growth is stagnant.',
                relationship: 'A relationship failing to move to the next level; long-distance issues.',
                wealth: 'Delayed financial returns; unexpected costs in expansion.',
                health: 'Slow healing process; lack of improvement in energy levels.',
                universal: 'Re-evaluate your plans; perhaps the current path needs adjustment.',
                transition: 'A shift from expansion to frustrating delays.'
            }
        }
    },

    // 026 - Four of Wands 权杖四
    '026': {
        id: '026',
        name: 'Four of Wands',
        nameCn: '权杖四',
        introduction: '权杖四象征庆祝与稳定，四根权杖搭起花门，人们欢庆，代表成就、喜悦与团聚。',
        introductionEn: 'The Four of Wands is a card of celebration, harmony, and homecoming. It marks a period of joy and stability, often linked to milestones in family or communal life.',
        upright: {
            keywords: ['庆祝', '稳定', '家庭', '成就', '团聚', '喜悦', '里程碑'],
            keywordsEn: ['Celebration', 'Harmony', 'Marriage', 'Homecoming', 'Stability', 'Community'],
            imageDescription: '四根权杖搭建花门，人们在下方欢庆，背景是城堡。',
            imageDescriptionEn: 'Two people dance beneath a festive wreath suspended between four wands. A large castle or home stands in the background, signifying safety.',
            generalMeaning: '达成阶段性成就，值得庆祝的时刻，家庭和睦、稳定幸福。',
            generalMeaningEn: 'A time of happiness and relaxation. You have reached a safe harbor and should celebrate your success with loved ones.',
            scenes: {
                career: '事业达成里程碑，值得庆祝。',
                relationship: '感情稳定，可能有婚礼或团聚等喜事。',
                wealth: '财务稳定，有值得庆祝的收获。',
                health: '身心健康，状态良好。',
                universal: '当下是庆祝与享受成就的时刻。',
                transition: '从努力进入收获庆祝的正向转折。'
            },
            scenesEn: {
                career: 'Completion of a successful phase; workplace celebration; a stable and supportive environment.',
                relationship: 'Engagement, marriage, or moving in together; deep harmony in a relationship.',
                wealth: 'Buying a home; financial stability; enjoying material rewards.',
                health: 'Good health and high vitality; feeling "at home" in your body.',
                universal: 'Celebrate your wins; harmony is present in your life.',
                transition: 'A joyful shift from hard labor to stable celebration.'
            }
        },
        reversed: {
            keywords: ['不稳定', '冲突', '庆祝受阻', '家庭矛盾', '缺乏归属感'],
            keywordsEn: ['Instability', 'Lack of Harmony', 'Cancelled Celebration', 'Home Conflict', 'Transitions'],
            imageDescription: '花门摇晃，庆祝氛围受到干扰。',
            imageDescriptionEn: 'The festive wreath is wilting or the wands are unstable; the mood is somber rather than joyful.',
            generalMeaning: '稳定受到威胁，家庭或团体可能有矛盾。',
            generalMeaningEn: 'Tension in the home or community. The success is there, but inner conflict or external delays are dampening the joy.',
            scenes: {
                career: '事业成就被质疑或不被认可。',
                relationship: '家庭或感情出现矛盾。',
                wealth: '财务稳定受到威胁。',
                health: '身心不够安定。',
                universal: '当下需修复关系，重建稳定。',
                transition: '从稳定转向动荡的消极转折。'
            },
            scenesEn: {
                career: 'Lack of teamwork; achievements not recognized; workplace tension.',
                relationship: 'Arguments at home; a wedding or party delay; feeling like an outsider.',
                wealth: 'Uncertainty regarding property or housing; shaky financial foundation.',
                health: 'Feeling unsettled or restless; minor physical discomfort.',
                universal: 'Seek internal peace when external harmony is lacking.',
                transition: 'A shift from stability to temporary disharmony.'
            }
        }
    },

    // 027 - Five of Wands 权杖五
    '027': {
        id: '027',
        name: 'Five of Wands',
        nameCn: '权杖五',
        introduction: '权杖五象征竞争与冲突，五人各持权杖交战，代表竞争、挑战与意见分歧。',
        introductionEn: 'The Five of Wands represents healthy competition, rivalry, and struggle. It often indicates a clash of egos or a bustling environment where everyone is fighting for their voice to be heard.',
        upright: {
            keywords: ['竞争', '冲突', '挑战', '分歧', '较量', '辩论'],
            keywordsEn: ['Competition', 'Conflict', 'Rivalry', 'Disagreement', 'Struggle', 'Chaos'],
            imageDescription: '五人各持权杖，相互交战或竞争。',
            imageDescriptionEn: 'Five youths brandish wands in a confusing fray. They are not hurting each other, but they are clearly in competition or disagreement.',
            generalMeaning: '处于竞争激烈的环境，需要应对挑战和分歧。',
            generalMeaningEn: 'A time of tension and competition. You may be facing opposition or simply a lot of "noise" that requires you to stand your ground.',
            scenes: {
                career: '事业上面临激烈竞争或团队分歧。',
                relationship: '感情中可能有争论或竞争者。',
                wealth: '财务上可能有竞争压力。',
                health: '压力可能影响健康。',
                universal: '当下需面对竞争，在冲突中找到解决之道。',
                transition: '进入竞争激烈的挑战阶段。'
            },
            scenesEn: {
                career: 'Workplace rivalry; brainstorming sessions that turn into arguments; intense market competition.',
                relationship: 'Petty arguments; competing for attention; different opinions causing friction.',
                wealth: 'Financial competition; multiple parties bidding for the same asset.',
                health: 'Stress-related tension; the body fighting off a minor illness.',
                universal: 'Don\'t be afraid of the fray; competition can lead to growth.',
                transition: 'A shift from peace into a chaotic, competitive environment.'
            }
        },
        reversed: {
            keywords: ['避免冲突', '化解分歧', '合作', '妥协', '竞争结束'],
            keywordsEn: ['Avoiding Conflict', 'Resolution', 'Cooperation', 'Harmony', 'Ending Competition'],
            imageDescription: '权杖放下，冲突逐渐平息。',
            imageDescriptionEn: 'The youths drop their wands; the scene becomes quiet, indicating the end of a struggle.',
            generalMeaning: '冲突开始化解，或选择避免不必要的竞争。',
            generalMeaningEn: 'The end of a conflict or a decision to walk away from a fight. You are seeking peace and common ground.',
            scenes: {
                career: '团队矛盾得到解决或竞争告一段落。',
                relationship: '争吵过后开始和解。',
                wealth: '竞争压力减轻。',
                health: '压力开始缓解。',
                universal: '当下冲突正在化解，寻求合作。',
                transition: '从冲突转向和解的正向转折。'
            },
            scenesEn: {
                career: 'Resolution of workplace disputes; deciding to collaborate instead of compete.',
                relationship: 'Arguments subsiding; letting go of the need to be "right" in a relationship.',
                wealth: 'Easing of financial competition; finding a compromise in a deal.',
                health: 'Release of tension; beginning to relax after a stressful period.',
                universal: 'Peace is returning; focus on harmony rather than winning.',
                transition: 'A shift from chaos to a state of relief and cooperation.'
            }
        }
    },

    // 028 - Six of Wands 权杖六
    '028': {
        id: '028',
        name: 'Six of Wands',
        nameCn: '权杖六',
        introduction: '权杖六象征胜利与荣耀，骑马凯旋的人头戴桂冠，众人欢呼，代表成功被认可。',
        introductionEn: 'The Six of Wands is the card of victory and public recognition. It signifies that your efforts have been successful and you are receiving the praise and status you deserve.',
        upright: {
            keywords: ['胜利', '荣耀', '成功', '认可', '自信', '领导'],
            keywordsEn: ['Victory', 'Success', 'Public Recognition', 'Pride', 'Self-Confidence', 'Leadership'],
            imageDescription: '骑着白马的人头戴桂冠，举权杖凯旋，众人欢呼。',
            imageDescriptionEn: 'A man wearing a laurel wreath sits on a white horse, riding through a cheering crowd. He holds a wand adorned with another wreath.',
            generalMeaning: '取得胜利并获得公众认可，自信心提升。',
            generalMeaningEn: 'You are the "winner" in this situation. Expect good news, rewards, and a boost to your reputation.',
            scenes: {
                career: '事业取得成功，获得认可和赞誉。',
                relationship: '感情中感到被重视和欣赏。',
                wealth: '财务上取得成功收益。',
                health: '克服健康挑战。',
                universal: '当下是胜利与荣耀的时刻，享受成就。',
                transition: '从努力转向成功凯旋的正向转折。'
            },
            scenesEn: {
                career: 'A promotion; winning an award; successful project completion with high praise.',
                relationship: 'Being admired by your partner; a successful "conquest" in love.',
                wealth: 'Financial triumph; hitting a big savings goal or investment win.',
                health: 'Successful recovery from an illness; reaching peak physical condition.',
                universal: 'You have done well; accept the applause with grace.',
                transition: 'A positive shift from struggle to public success.'
            }
        },
        reversed: {
            keywords: ['失败', '不被认可', '骄傲', '名誉受损', '自我怀疑'],
            keywordsEn: ['Defeat', 'Lack of Recognition', 'Fall from Grace', 'Self-Doubt', 'Ego Issues'],
            imageDescription: '凯旋场面消失，荣耀不再。',
            imageDescriptionEn: 'The horse falters or the crowd turns away; the wreath slips, representing a loss of status.',
            generalMeaning: '成功受挫或不被认可，可能因骄傲而跌落。',
            generalMeaningEn: 'A loss of confidence or a public setback. You may have achieved success, but it went unnoticed, or you are facing a "hollow" victory.',
            scenes: {
                career: '事业成就不被认可或遭遇失败。',
                relationship: '感情中感到不被重视。',
                wealth: '财务损失或投资失败。',
                health: '健康目标未能达成。',
                universal: '当下需反思失败原因，保持谦虚。',
                transition: '从胜利转向挫折的消极转折。'
            },
            scenesEn: {
                career: 'Being overlooked for a promotion; project failure; damaged professional reputation.',
                relationship: 'Feeling ignored or unappreciated; a loss of "spark" or pride in a partner.',
                wealth: 'Financial losses after a period of overconfidence; hollow gains.',
                health: 'Slow recovery; feeling defeated by health setbacks.',
                universal: 'Humility is needed now; don\'t let your ego dictate your next move.',
                transition: 'A shift from triumph to a humbling reality.'
            }
        }
    },

    // 029 - Seven of Wands 权杖七
    '029': {
        id: '029',
        name: 'Seven of Wands',
        nameCn: '权杖七',
        introduction: '权杖七象征坚守与防卫，一人站在高处持权杖抵御下方六根权杖的攻击，代表捍卫立场。',
        introductionEn: 'The Seven of Wands represents defensiveness and maintaining your position. It signifies that you have the "high ground" and must fight to keep it against competitors or challenges.',
        upright: {
            keywords: ['坚守', '防卫', '挑战', '坚持立场', '勇气', '竞争'],
            keywordsEn: ['Defense', 'Perseverance', 'Maintaining Control', 'Challenge', 'Courage', 'Stamina'],
            imageDescription: '一人站在高处持权杖，抵御下方的攻击。',
            imageDescriptionEn: 'A man stands on a hilltop, fiercely defending his position with a wand. Below him, six other wands strike upward at him.',
            generalMeaning: '面对挑战需要坚守立场，勇敢捍卫自己的位置。',
            generalMeaningEn: 'You are under pressure, but you have the advantage. Stand firm in your beliefs and do not let others push you down.',
            scenes: {
                career: '事业上需要捍卫成果或面对挑战者。',
                relationship: '感情中需要坚持自我。',
                wealth: '财务上需要保护已有资产。',
                health: '健康上需要坚持良好习惯。',
                universal: '当下需勇敢坚守，不畏挑战。',
                transition: '进入需要防卫坚守的阶段。'
            },
            scenesEn: {
                career: 'Defending a business idea; fighting to keep your job position; protecting your professional reputation.',
                relationship: 'Defending your relationship from outside interference; standing up for yourself in a bond.',
                wealth: 'Protecting your assets; resisting pressure to spend or invest unwisely.',
                health: 'Maintaining a healthy routine despite social pressure to indulge.',
                universal: 'Hold your ground; you have the strength to win this fight.',
                transition: 'A shift from success to the need to protect that success.'
            }
        },
        reversed: {
            keywords: ['放弃', '被击败', '退让', '精疲力竭', '失去优势'],
            keywordsEn: ['Giving Up', 'Overwhelmed', 'Lack of Confidence', 'Surrender', 'Losing the Lead'],
            imageDescription: '防卫失败，被攻击压倒。',
            imageDescriptionEn: 'The man slips from the hilltop; his wand is lowered, symbolizing a loss of defensive power.',
            generalMeaning: '无法继续坚守或选择退让。',
            generalMeaningEn: 'Feeling overwhelmed by the sheer number of challenges. You may be losing your nerve or feeling that the fight is no longer worth it.',
            scenes: {
                career: '事业地位可能失去。',
                relationship: '感情中可能过于退让。',
                wealth: '财务可能受到损失。',
                health: '精力耗尽，难以坚持。',
                universal: '当下需评估是否值得继续坚守。',
                transition: '从坚守转向放弃的消极转折。'
            },
            scenesEn: {
                career: 'Losing a competitive edge; buckling under workplace pressure; being outmaneuvered.',
                relationship: 'Giving in to a partner\'s demands too easily; feeling "attacked" and unable to cope.',
                wealth: 'Financial vulnerability; unable to protect your savings from loss.',
                health: 'Burnout; giving up on a health journey because it feels too hard.',
                universal: 'It\'s okay to step back if you are truly exhausted, but don\'t lose yourself.',
                transition: 'A shift from defensive strength to weary surrender.'
            }
        }
    },

    // 030 - Eight of Wands 权杖八
    '030': {
        id: '030',
        name: 'Eight of Wands',
        nameCn: '权杖八',
        introduction: '权杖八象征快速行动与进展，八根权杖在空中飞速前进，代表事情快速发展。',
        introductionEn: 'The Eight of Wands is the card of speed, action, and rapid movement. It signifies that the waiting is over and everything is now moving forward at an accelerated pace.',
        upright: {
            keywords: ['快速', '行动', '进展', '消息', '旅行', '势头'],
            keywordsEn: ['Speed', 'Action', 'Air Travel', 'Swift Progress', 'Movement', 'Quick News'],
            imageDescription: '八根权杖在天空中飞速前进。',
            imageDescriptionEn: 'Eight wands fly through the air in parallel, moving toward a green landscape. There is a sense of immense momentum and no resistance.',
            generalMeaning: '事情快速发展，消息即将到来，抓住机会行动。',
            generalMeaningEn: 'Sudden changes and fast progress. Communication is key, and news will arrive quickly. It is a time to act while the iron is hot.',
            scenes: {
                career: '事业快速进展，项目加速推进。',
                relationship: '感情快速发展或有消息来临。',
                wealth: '财务上有快速收益或交易。',
                health: '身体快速恢复。',
                universal: '当下事物快速发展，把握势头。',
                transition: '从缓慢转向快速发展的正向转折。'
            },
            scenesEn: {
                career: 'Rapid project completion; quick responses to applications; business travel.',
                relationship: 'A "whirlwind" romance; sudden deep connections; fast-paced news from a lover.',
                wealth: 'Quick financial transactions; fast returns; sudden money movement.',
                health: 'Rapid recovery; high energy levels returning suddenly.',
                universal: 'Things are moving fast; stay focused to catch the opportunities.',
                transition: 'A shift from stagnation to high-speed momentum.'
            }
        },
        reversed: {
            keywords: ['延迟', '受阻', '混乱', '仓促', '信息不准确'],
            keywordsEn: ['Delay', 'Slowdown', 'Missed Timing', 'Frustration', 'Chaotic Speed'],
            imageDescription: '权杖飞行受阻或方向混乱。',
            imageDescriptionEn: 'The wands fall from the sky or fly in different directions, symbolizing lost momentum or poorly aimed action.',
            generalMeaning: '进展被延迟或因仓促而出错。',
            generalMeaningEn: 'Movement is blocked or happening too fast and in a disorganized way. You may be facing delays or acting without thinking.',
            scenes: {
                career: '项目进度延迟或陷入混乱。',
                relationship: '感情进展受阻。',
                wealth: '财务交易延迟。',
                health: '恢复过程受阻。',
                universal: '当下需耐心，避免仓促行事。',
                transition: '从快速转向延迟的消极转折。'
            },
            scenesEn: {
                career: 'Missed deadlines; travel cancellations; communication breakdowns at work.',
                relationship: 'A relationship slowing down; delayed texts or calls; miscommunication.',
                wealth: 'Financial delays; checks being held up; losing money due to hasty moves.',
                health: 'Slow recuperation; feeling sluggish or "stuck" physically.',
                universal: 'Slow down; hasty actions will lead to mistakes right now.',
                transition: 'A shift from swift movement to frustrating obstacles.'
            }
        }
    },

    // 031 - Nine of Wands 权杖九
    '031': {
        id: '031',
        name: 'Nine of Wands',
        nameCn: '权杖九',
        introduction: '权杖九象征坚韧与警惕，受伤的人依然握着权杖站立，身后八根权杖形成屏障。',
        introductionEn: 'The Nine of Wands represents the strength to persevere despite exhaustion. You have been through a lot, but you are not yet at the end—stay alert and protect what you have built.',
        upright: {
            keywords: ['坚韧', '警惕', '毅力', '防备', '最后一搏', '坚持'],
            keywordsEn: ['Resilience', 'Courage', 'Persistence', 'Boundaries', 'Vigilance', 'Last Stand'],
            imageDescription: '一个受伤但不倒的人，握权杖站立，身后是权杖屏障。',
            imageDescriptionEn: 'A battle-weary man with a bandage on his head leans on a wand, standing before a "wall" of eight wands. He looks ready for one last fight.',
            generalMeaning: '虽然疲惫受伤，但仍坚韧不拔地坚持到底。',
            generalMeaningEn: 'You are tired, but you must not give up. Resilience is your greatest asset now. Protect your progress and prepare for the final stage.',
            scenes: {
                career: '事业虽有困难但坚持不懈。',
                relationship: '感情经历考验但依然坚守。',
                wealth: '财务上谨慎防备。',
                health: '身体虽疲惫但仍坚持。',
                universal: '当下需要坚韧毅力，胜利在望。',
                transition: '进入坚韧坚持的最后阶段。'
            },
            scenesEn: {
                career: 'Persevering through a grueling project; protecting your professional boundaries; nearing the end of a long struggle.',
                relationship: 'Standing firm in a relationship during tough times; protecting the heart after past hurts.',
                wealth: 'Being defensive with money; protecting your savings from potential risks.',
                health: 'Pushing through the final stages of recovery; stubborn physical resilience.',
                universal: 'The end is near; find your second wind and hold your ground.',
                transition: 'A shift from active battle to defensive resilience.'
            }
        },
        reversed: {
            keywords: ['精疲力竭', '放弃', '多疑', '固执', '无法继续'],
            keywordsEn: ['Exhaustion', 'Giving Up', 'Paranoia', 'Weakness', 'Defensiveness'],
            imageDescription: '受伤的人再也无法站立。',
            imageDescriptionEn: 'The man drops his wand; the wall of wands behind him looks broken or useless.',
            generalMeaning: '坚持到极限，可能需要休息或放弃。',
            generalMeaningEn: 'Total burnout. You may be too defensive or so tired that you can no longer keep up your guard. The weight of the struggle has become too much.',
            scenes: {
                career: '事业上精疲力竭，需要休息。',
                relationship: '感情上无法再坚持。',
                wealth: '财务防线崩溃。',
                health: '身体需要恢复。',
                universal: '当下需要休息恢复力量。',
                transition: '从坚持转向需要休息的消极转折。'
            },
            scenesEn: {
                career: 'Burnout at work; unable to meet the final deadline; failing to defend a position.',
                relationship: 'Walking away from a long struggle; being too exhausted to fight for a partner anymore.',
                wealth: 'Financial defenses crumbling; inability to manage debt or expenses.',
                health: 'Chronic fatigue; the body giving in to stress; need for urgent rest.',
                universal: 'Rest is not defeat; you cannot fight when you are completely empty.',
                transition: 'A shift from persistence to total exhaustion.'
            }
        }
    },

    // 032 - Ten of Wands 权杖十
    '032': {
        id: '032',
        name: 'Ten of Wands',
        nameCn: '权杖十',
        introduction: '权杖十象征负担与责任，一人艰难地背负着十根权杖前行，代表责任沉重。',
        introductionEn: 'The Ten of Wands signifies that you have taken on too much responsibility. It is a card of hard work and duty, but warns that the burden may be nearing an unsustainable level.',
        upright: {
            keywords: ['负担', '责任', '压力', '辛劳', '完成', '坚持'],
            keywordsEn: ['Burden', 'Responsibility', 'Over-extension', 'Hard Work', 'Stress', 'Achievement'],
            imageDescription: '一人弯腰背负十根权杖，艰难前行。',
            imageDescriptionEn: 'A man is bent over, struggling to carry ten heavy wands toward a nearby city. He cannot see the path clearly because of the load.',
            generalMeaning: '承担沉重责任，虽然辛苦但接近完成。',
            generalMeaningEn: 'You are carrying the weight of the world on your shoulders. Success is close, but the cost to your well-being is high.',
            scenes: {
                career: '事业责任重大，工作量大。',
                relationship: '感情中承担过多责任。',
                wealth: '财务压力较大。',
                health: '身体因负担过重而疲惫。',
                universal: '当下承担重任，但终点在望。',
                transition: '进入负重前行的艰难阶段。'
            },
            scenesEn: {
                career: 'Taking on all the work in a project; burnout; the final push before completion.',
                relationship: 'Feeling like you are the only one putting in effort; a relationship that feels like "work."',
                wealth: 'Financial burdens; the stress of high-maintenance assets or debt.',
                health: 'Physical strain, especially in the back or shoulders; total exhaustion.',
                universal: 'The finish line is in sight, but don\'t break yourself getting there.',
                transition: 'A shift from personal power to overwhelming duty.'
            }
        },
        reversed: {
            keywords: ['卸下负担', '委托他人', '精疲力竭', '放弃责任'],
            keywordsEn: ['Relief', 'Delegation', 'Release', 'Collapse', 'Dropping the Load'],
            imageDescription: '权杖散落，负担卸下或无法承受。',
            imageDescriptionEn: 'The wands are scattered on the ground; the man is either resting or has collapsed beneath the weight.',
            generalMeaning: '需要卸下过重的负担或学会委托他人。',
            generalMeaningEn: 'You are letting go of responsibilities—either by choice or because you have collapsed. It is time to delegate or simplify your life.',
            scenes: {
                career: '需要分担工作或减轻任务。',
                relationship: '需要平衡责任分配。',
                wealth: '需要减轻财务压力。',
                health: '需要休息减压。',
                universal: '当下需要学会放下或寻求帮助。',
                transition: '从超负荷转向需要卸载的转折。'
            },
            scenesEn: {
                career: 'Delegating tasks; quitting an overwhelming job; suffering a breakdown from stress.',
                relationship: 'Ending a one-sided relationship; finally sharing the emotional load.',
                wealth: 'Letting go of costly possessions; declaring bankruptcy; debt relief.',
                health: 'Forced rest; healing by removing the source of stress.',
                universal: 'Release the wands you don\'t need to carry; seek help.',
                transition: 'A shift from carrying a burden to either relief or collapse.'
            }
        }
    },

    // 033 - Page of Wands 权杖侍从
    '033': {
        id: '033',
        name: 'Page of Wands',
        nameCn: '权杖侍从',
        introduction: '权杖侍从象征热情的探索者，年轻人好奇地端详权杖，代表新想法、热情与探险精神。',
        introductionEn: 'The Page of Wands is the messenger of inspiration. He represents a fresh spark of creativity, the excitement of new beginnings, and a fearless desire to explore the world.',
        upright: {
            keywords: ['探索', '热情', '新想法', '好奇', '消息', '创意'],
            keywordsEn: ['Exploration', 'Excitement', 'Inspiration', 'Discovery', 'New Beginnings', 'Messenger'],
            imageDescription: '年轻人好奇地端详发芽的权杖。',
            imageDescriptionEn: 'A youth stands in a desert under a sunny sky, looking with wonder at a tall wand with green leaves budding at the top.',
            generalMeaning: '充满热情与好奇心，有新想法或消息到来。',
            generalMeaningEn: 'A time of newfound passion. You may receive an exciting message or feel a sudden urge to start a creative project or adventure.',
            scenes: {
                career: '事业上有新的想法或机会。',
                relationship: '感情上有热情的新开始。',
                wealth: '有新的财务想法。',
                health: '对健康充满热情尝试新方法。',
                universal: '当下充满热情探索新事物。',
                transition: '进入热情探索的新阶段。'
            },
            scenesEn: {
                career: 'A creative job offer; starting an internship or new project; high enthusiasm for learning.',
                relationship: 'A new crush; flirtatious messages; a fresh, playful start in romance.',
                wealth: 'A new idea for making money; small but exciting financial leads.',
                health: 'High energy; starting a new, fun sport or activity.',
                universal: 'Follow your spark; a new door is opening for you.',
                transition: 'A shift from stillness to inspired movement.'
            }
        },
        reversed: {
            keywords: ['缺乏方向', '鲁莽', '延迟消息', '热情消退', '不成熟'],
            keywordsEn: ['Lack of Direction', 'Procrastination', 'Creative Block', 'Pessimism', 'Unreliable'],
            imageDescription: '年轻人迷失方向或对权杖失去兴趣。',
            imageDescriptionEn: 'The Page looks bored or distracted; the wand seems too big for him or is neglected.',
            generalMeaning: '热情消退或行为鲁莽不成熟。',
            generalMeaningEn: 'A false start or a loss of interest. You may have the idea but lack the discipline to follow through, or you are acting on immature impulses.',
            scenes: {
                career: '新想法无法实现或方向迷失。',
                relationship: '感情热情消退。',
                wealth: '财务想法不切实际。',
                health: '对健康计划缺乏热情。',
                universal: '当下需找回方向和热情。',
                transition: '从热情转向迷失的消极转折。'
            },
            scenesEn: {
                career: 'Workplace boredom; ideas that fail to launch; bad career news.',
                relationship: 'Fading interest in a partner; immature communication; ghosting.',
                wealth: 'Impractical schemes; money delays; overspending on temporary hobbies.',
                health: 'Low energy; starting and immediately quitting a diet or gym.',
                universal: 'Refocus your energy before you waste it on the wrong things.',
                transition: 'A shift from inspiration to scattered frustration.'
            }
        }
    },

    // 034 - Knight of Wands 权杖骑士
    '034': {
        id: '034',
        name: 'Knight of Wands',
        nameCn: '权杖骑士',
        introduction: '权杖骑士象征冒险与行动，骑着烈马冲锋，代表勇敢、热情与冒险精神。',
        introductionEn: 'The Knight of Wands is the embodiment of energy and movement. He is fearless, impulsive, and full of passion, charging forward to conquer his goals with intense charisma.',
        upright: {
            keywords: ['冒险', '行动', '热情', '勇敢', '旅行', '变化'],
            keywordsEn: ['Adventure', 'Passion', 'Action', 'Speed', 'Enthusiasm', 'Confidence'],
            imageDescription: '骑士骑着烈马，高举权杖冲锋。',
            imageDescriptionEn: 'A knight in armor, with a plume of flames on his helmet, charges forward on a rearing orange horse, holding his wand like a spear.',
            generalMeaning: '充满行动力和冒险精神，勇往直前。',
            generalMeaningEn: 'The time for waiting is over. This card signals bold action, travel, and pursuing your desires with full speed and confidence.',
            scenes: {
                career: '事业上大胆行动，可能涉及变化或旅行。',
                relationship: '感情热烈而充满激情。',
                wealth: '财务上敢于冒险投资。',
                health: '积极锻炼，活力充沛。',
                universal: '当下是冒险行动的时机。',
                transition: '进入充满活力的行动阶段。'
            },
            scenesEn: {
                career: 'Taking a big risk at work; a sudden promotion or change in role; high productivity.',
                relationship: 'A passionate romance; pursuing someone intensely; being the "hero" in love.',
                wealth: 'Bold investments; aggressive financial moves; travel expenses.',
                health: 'Strong physical vitality; intense workouts; high-energy recovery.',
                universal: 'Charge forward; your courage will see you through.',
                transition: 'A surge from thought into full-speed action.'
            }
        },
        reversed: {
            keywords: ['鲁莽', '冲动', '缺乏方向', '延迟', '过于激进'],
            keywordsEn: ['Recklessness', 'Impulsiveness', 'Restlessness', 'Frustration', 'Delayed Progress'],
            imageDescription: '骑士冲锋受阻或失去控制。',
            imageDescriptionEn: 'The knight falls from his horse or the horse is out of control; the flames look dangerous rather than inspiring.',
            generalMeaning: '行动过于冲动或鲁莽，需要控制。',
            generalMeaningEn: 'You may be acting with haste and making waste. Impulsiveness, anger, or a lack of direction is causing your plans to fail.',
            scenes: {
                career: '事业行动过于激进可能出问题。',
                relationship: '感情可能因冲动而出现问题。',
                wealth: '财务冒险可能带来损失。',
                health: '运动过度可能受伤。',
                universal: '当下需控制冲动，谨慎行事。',
                transition: '从行动转向失控的消极转折。'
            },
            scenesEn: {
                career: 'Arrogance in the office; a project stalled by poor planning; aggressive job-hopping.',
                relationship: 'Arguments caused by hot tempers; a partner who is "all talk" and no commitment.',
                wealth: 'Gambling losses; impulsive purchases that lead to regret.',
                health: 'Injuries from over-exertion; burnout from living too fast.',
                universal: 'Check your temper and your pace; speed is not always success.',
                transition: 'A shift from directed energy to reckless chaos.'
            }
        }
    },

    // 035 - Queen of Wands 权杖皇后
    '035': {
        id: '035',
        name: 'Queen of Wands',
        nameCn: '权杖皇后',
        introduction: '权杖皇后象征自信与魅力，皇后端坐王座，一手持权杖一手持向日葵，代表热情、独立与领导力。',
        introductionEn: 'The Queen of Wands is the master of self-confidence and social grace. She is courageous, vibrant, and independent, radiating a warmth that attracts others and inspires creativity.',
        upright: {
            keywords: ['自信', '魅力', '独立', '热情', '领导力', '温暖'],
            keywordsEn: ['Confidence', 'Courage', 'Independence', 'Social Butterfly', 'Vibrancy', 'Determination'],
            imageDescription: '皇后端坐王座，持权杖和向日葵，黑猫在脚边。',
            imageDescriptionEn: 'The Queen sits on a throne decorated with lions. She holds a wand and a sunflower, while a black cat—symbolizing intuition and hidden power—sits at her feet.',
            generalMeaning: '充满自信与魅力，独立且有领导能力。',
            generalMeaningEn: 'You are in control of your life and your image. This is a time to be bold, social, and focus on your personal power and passions.',
            scenes: {
                career: '事业上展现领导魅力和自信。',
                relationship: '感情中热情而独立。',
                wealth: '财务上自信管理。',
                health: '身心状态积极健康。',
                universal: '当下以自信和魅力吸引好运。',
                transition: '进入自信领导的正向阶段。'
            },
            scenesEn: {
                career: 'Influential leadership; being the "face" of a company; high creative output.',
                relationship: 'An equal, passionate partnership; a confident and attractive partner.',
                wealth: 'Wealth through charisma or creative ventures; confident money management.',
                health: 'Radiant health; glowing energy; strong physical presence.',
                universal: 'Be your own light; your confidence is your best tool.',
                transition: 'A shift from action to graceful, confident mastery.'
            }
        },
        reversed: {
            keywords: ['自私', '嫉妒', '专横', '缺乏自信', '情绪化'],
            keywordsEn: ['Selfishness', 'Jealousy', 'Insecurity', 'Manipulativeness', 'Demanding'],
            imageDescription: '皇后的魅力变为专横或自私。',
            imageDescriptionEn: 'The Queen looks angry or withdrawn; the sunflower is wilting and the cat is hissing.',
            generalMeaning: '自信变为自大，或失去自信变得情绪化。',
            generalMeaningEn: 'A loss of self-esteem or an abuse of power. You may be feeling jealous, demanding, or acting in a way that pushes others away.',
            scenes: {
                career: '事业上可能过于专横或失去动力。',
                relationship: '感情中可能嫉妒或情绪化。',
                wealth: '财务管理可能出问题。',
                health: '情绪影响健康。',
                universal: '当下需平衡自信与谦逊。',
                transition: '从自信转向失衡的消极转折。'
            },
            scenesEn: {
                career: 'A bossy or toxic female figure at work; feeling intimidated; loss of professional spark.',
                relationship: 'Jealousy or controlling behavior; emotional drama; feeling unattractive.',
                wealth: 'Financial mistakes driven by ego; wasting money to show off.',
                health: 'Low energy; vanity-related health issues; feeling burnt out.',
                universal: 'Rebuild your self-worth from the inside, not through external praise.',
                transition: 'A shift from vibrant leadership to insecure demands.'
            }
        }
    },

    // 036 - King of Wands 权杖国王
    '036': {
        id: '036',
        name: 'King of Wands',
        nameCn: '权杖国王',
        introduction: '权杖国王象征领导与远见，国王端坐王座持权杖，代表权威、领导力与企业家精神。',
        introductionEn: 'The King of Wands is the ultimate visionary and entrepreneur. He represents the peak of creative energy and pure leadership, possessing the fire to turn any idea into reality.',
        upright: {
            keywords: ['领导', '远见', '权威', '企业家', '热情', '掌控'],
            keywordsEn: ['Leadership', 'Vision', 'Entrepreneurship', 'Honor', 'Grand Designs', 'Power'],
            imageDescription: '国王端坐于狮子和蜥蜴装饰的王座上，持权杖。',
            imageDescriptionEn: 'The King sits on a throne adorned with salamanders (symbols of fire). He holds a blossoming wand and looks forward with a steady, commanding gaze.',
            generalMeaning: '具有领导者的远见和权威，能够掌控局面。',
            generalMeaningEn: 'You have reached a position of true authority. Use your vision to lead others and your passion to fuel grand projects. Success comes from bold leadership.',
            scenes: {
                career: '事业上展现杰出领导力，适合创业。',
                relationship: '感情中是可靠的领导者和保护者。',
                wealth: '财务上有远见卓识的投资眼光。',
                health: '身体强健，精力充沛。',
                universal: '当下以领导者姿态掌控全局。',
                transition: '进入领导掌控的巅峰阶段。'
            },
            scenesEn: {
                career: 'CEO or high-level executive energy; starting a successful company; visionary leadership.',
                relationship: 'A protective and inspiring partner; a relationship built on mutual respect and ambition.',
                wealth: 'Major financial success; wise risk-taking that leads to prosperity.',
                health: 'Vigorous health; master of one\'s own physical limits; strong heart.',
                universal: 'Lead with your heart and your head; you are the master of your fate.',
                transition: 'A shift from individual ambition to powerful leadership.'
            }
        },
        reversed: {
            keywords: ['独裁', '傲慢', '鲁莽', '失去控制', '期望过高'],
            keywordsEn: ['Tyranny', 'Arrogance', 'High Expectations', 'Aggression', 'Lack of Direction'],
            imageDescription: '国王的权威变为独裁或失去控制。',
            imageDescriptionEn: 'The King grips his wand too tightly or looks away from his goal; the salamanders seem to bite his tail, showing self-sabotage.',
            generalMeaning: '领导力变为独裁，或失去对局面的控制。',
            generalMeaningEn: 'Abuse of power or a loss of vision. You may be acting like a tyrant or making reckless decisions that hurt your reputation and goals.',
            scenes: {
                career: '事业上可能过于专断或失控。',
                relationship: '感情中可能过于霸道。',
                wealth: '财务决策可能过于冒险。',
                health: '精力过度消耗。',
                universal: '当下需反思领导方式，避免独裁。',
                transition: '从领导转向失控的消极转折。'
            },
            scenesEn: {
                career: 'A ruthless boss; failed business ventures due to over-ego; loss of authority.',
                relationship: 'Domineering behavior; a partner who is too self-centered; lack of empathy.',
                wealth: 'Financial recklessness; losing money through pride or bad gambles.',
                health: 'Stress due to over-ambition; burnout; high blood pressure or tension.',
                universal: 'Check your pride; a king without a heart has no kingdom.',
                transition: 'A shift from powerful vision to arrogant instability.'
            }
        }
    },

    // ========== Minor Arcana - Cups (圣杯) ==========

    // 037 - Ace of Cups 圣杯王牌
    '037': {
        id: '037',
        name: 'Ace of Cups',
        nameCn: '圣杯王牌',
        introduction: '圣杯王牌象征情感的开始，圣杯满溢代表爱、直觉与情感的丰盛，是新感情或情感觉醒的开端。',
        introductionEn: 'The Ace of Cups is the essence of the element of Water. It represents the overflowing of love, spiritual abundance, and the birth of new emotional experiences or intuitive awakening.',
        upright: {
            keywords: ['新感情', '情感丰盛', '爱', '直觉', '创意', '灵性'],
            keywordsEn: ['New Feelings', 'Abundance', 'Love', 'Intuition', 'Creativity', 'Spirituality', 'Compassion'],
            imageDescription: '一只手从云中伸出托着溢满的圣杯，五道水流涌出。',
            imageDescriptionEn: 'A divine hand holds a cup overflowing with five streams of water, symbolizing the five senses and the abundance of the soul.',
            generalMeaning: '情感世界有新的开始，充满爱与灵性的力量。',
            generalMeaningEn: 'A breakthrough in your emotional world. It marks a period where your heart is open, and you are ready to give and receive unconditional love.',
            scenes: {
                career: '工作中充满创意灵感，与同事关系融洽。',
                relationship: '新恋情开始或现有感情深化。',
                wealth: '情感满足带来的内在富足。',
                health: '情绪平衡，心灵健康。',
                universal: '当下是情感丰盛的时期，敞开心扉。',
                transition: '进入情感新阶段的正向转折。'
            },
            scenesEn: {
                career: 'New creative projects; harmonious work environment; following your passion.',
                relationship: 'The start of a new romance; deep emotional connection; a time of joy in love.',
                wealth: 'Inner fulfillment that transcends material wealth; spending on what brings true joy.',
                health: 'Emotional healing; mental peace; vitality through positive emotions.',
                universal: 'Open your heart to the universe; love is flowing toward you.',
                transition: 'A positive shift from emotional drought to spiritual abundance.'
            }
        },
        reversed: {
            keywords: ['情感封闭', '空虚', '失落', '情感受阻', '创意枯竭'],
            keywordsEn: ['Emotional Block', 'Emptiness', 'Unrequited Love', 'Repressed Feelings', 'Self-Love Deficit'],
            imageDescription: '圣杯倾斜，水流外洒或枯竭。',
            imageDescriptionEn: 'The cup is overturned, the water spills and the spirit seems to drain away, representing an emotional leak or blockage.',
            generalMeaning: '情感世界受阻或封闭，难以表达或接受爱。',
            generalMeaningEn: 'A time of emotional depletion. You may be repressing your feelings or feeling disconnected from your intuition and inner self.',
            scenes: {
                career: '工作缺乏热情和创意。',
                relationship: '感情受阻或无法开始。',
                wealth: '内心空虚影响对财务的判断。',
                health: '情绪压抑影响健康。',
                universal: '当下需打开心扉，接纳情感。',
                transition: '从丰盛转向枯竭的消极转折。'
            },
            scenesEn: {
                career: 'Feeling uninspired at work; lack of emotional connection to your professional path.',
                relationship: 'Unrequited love; emotional instability; feeling "numb" in a relationship.',
                wealth: 'Spending money to fill an emotional void; unstable financial intuition.',
                health: 'Depression or anxiety; need for emotional detox.',
                universal: 'Nurture yourself first; your emotional cup needs refilling.',
                transition: 'A negative shift from openness to emotional withdrawal.'
            }
        }
    },

    // 038 - Two of Cups 圣杯二
    '038': {
        id: '038',
        name: 'Two of Cups',
        nameCn: '圣杯二',
        introduction: '圣杯二象征伙伴与结合，两人交换圣杯代表相互吸引、承诺与平等的关系。',
        introductionEn: 'The Two of Cups signifies a powerful bond between two individuals. It represents mutual respect, attraction, and the balance of dualities to create a harmonious union.',
        upright: {
            keywords: ['伙伴关系', '结合', '吸引', '和谐', '承诺', '平等'],
            keywordsEn: ['Partnership', 'Union', 'Attraction', 'Harmony', 'Commitment', 'Mutual Respect'],
            imageDescription: '一男一女面对面交换圣杯，上方有双蛇缠绕的杖。',
            imageDescriptionEn: 'A man and a woman exchange cups in a pledge of love. Above them floats the Caduceus of Hermes, symbolizing healing and balanced energy.',
            generalMeaning: '形成和谐的伙伴关系，相互吸引与承诺。',
            generalMeaningEn: 'A time of coming together. Whether in romance or business, this card points to a relationship based on shared values and reciprocal energy.',
            scenes: {
                career: '与合作伙伴建立良好关系。',
                relationship: '恋爱关系确立或关系深化。',
                wealth: '合作带来财务收益。',
                health: '情感和谐促进身心健康。',
                universal: '当下适合建立伙伴关系。',
                transition: '从单独进入结合的正向转折。'
            },
            scenesEn: {
                career: 'Successful business partnerships; a great connection with a colleague; a signed contract.',
                relationship: 'Deepening love; an engagement; finding a soulmate; a balanced romance.',
                wealth: 'Mutual financial gain through collaboration; shared assets.',
                health: 'Achieving physical balance; wellness through social support.',
                universal: 'Connection is your path forward; seek harmony with others.',
                transition: 'A positive shift from independence to a supportive union.'
            }
        },
        reversed: {
            keywords: ['关系不和', '失衡', '分离', '误解', '缺乏吸引'],
            keywordsEn: ['Disharmony', 'Breakup', 'Imbalance', 'Miscommunication', 'Tension', 'Self-Interest'],
            imageDescription: '两人背对或圣杯倾斜，关系失衡。',
            imageDescriptionEn: 'The figures turn away; the cups are out of sync, symbolizing a rift or a breakdown in communication.',
            generalMeaning: '关系出现问题或无法建立连接。',
            generalMeaningEn: 'Indicates a lack of harmony or a potential split. Trust may be broken, or the two parties are no longer moving in the same direction.',
            scenes: {
                career: '合作关系出现裂痕。',
                relationship: '感情出现问题或分离。',
                wealth: '合作失败影响财务。',
                health: '情感问题影响健康。',
                universal: '当下需修复关系或接受分离。',
                transition: '从和谐转向失衡的消极转折。'
            },
            scenesEn: {
                career: 'Business partnerships failing; lack of teamwork; conflicting goals at work.',
                relationship: 'Arguments; a breakup or temporary separation; feeling "disconnected" from a partner.',
                wealth: 'Financial disputes; losing money due to poor collaboration.',
                health: 'Stress caused by social conflict; hormonal or energetic imbalance.',
                universal: 'Address the friction in your relationships; seek internal balance.',
                transition: 'A negative shift from harmony to discord.'
            }
        }
    },

    // 039 - Three of Cups 圣杯三
    '039': {
        id: '039',
        name: 'Three of Cups',
        nameCn: '圣杯三',
        introduction: '圣杯三象征庆祝与友谊，三位女性举杯庆祝代表欢乐、社交与团体的和谐。',
        introductionEn: 'The Three of Cups is a card of celebration, friendship, and community. It represents the joy of shared success and the importance of supporting one another in a group.',
        upright: {
            keywords: ['庆祝', '友谊', '社交', '欢乐', '团聚', '创意合作'],
            keywordsEn: ['Celebration', 'Friendship', 'Community', 'Joy', 'Gathering', 'Group Success'],
            imageDescription: '三位女性举杯欢庆，周围是丰收的果实。',
            imageDescriptionEn: 'Three women raise their cups in a toast, their arms entwined in a circle of support. They are surrounded by fruits and flowers, suggesting abundance.',
            generalMeaning: '与朋友欢聚庆祝，社交活动频繁而愉快。',
            generalMeaningEn: 'A happy time spent with loved ones. It suggests a milestone worth celebrating or simply enjoying the warmth of your social circle.',
            scenes: {
                career: '团队合作成功，值得庆祝。',
                relationship: '朋友聚会或社交活动中可能遇到对象。',
                wealth: '庆祝财务成就。',
                health: '社交活动带来好心情。',
                universal: '当下是欢乐庆祝的时刻。',
                transition: '进入欢乐社交的正向阶段。'
            },
            scenesEn: {
                career: 'A successful team project; a workplace party; feeling supported by colleagues.',
                relationship: 'Socializing together as a couple; meeting someone at a party; supportive friends.',
                wealth: 'Financial windfalls shared with others; celebration of a goal reached.',
                health: 'Improved well-being through social connection and laughter.',
                universal: 'Celebrate your blessings with those who matter most.',
                transition: 'A joyful shift into community and social harmony.'
            }
        },
        reversed: {
            keywords: ['孤立', '过度放纵', '社交问题', '三角关系', '八卦'],
            keywordsEn: ['Overindulgence', 'Gossip', 'Social Isolation', 'Three-way Conflict', 'Canceled Plans'],
            imageDescription: '庆祝变得过度或有人被排斥。',
            imageDescriptionEn: 'The cups fall; the circle of friends is broken, suggesting excess or the "dark side" of social life.',
            generalMeaning: '社交出现问题或过度放纵。',
            generalMeaningEn: 'You may be feeling like an outsider, or perhaps you are over-committing to parties and neglecting other responsibilities.',
            scenes: {
                career: '团队出现问题。',
                relationship: '可能有第三者或社交圈影响感情。',
                wealth: '过度消费在社交上。',
                health: '社交压力或过度放纵影响健康。',
                universal: '当下需节制或注意社交圈问题。',
                transition: '从欢乐转向问题的消极转折。'
            },
            scenesEn: {
                career: 'Workplace cliques; lack of team cohesion; post-celebration exhaustion.',
                relationship: 'Third-party interference; being "the third wheel"; neglecting a partner for friends.',
                wealth: 'Spending too much on socializing; financial irresponsibility due to peer pressure.',
                health: 'Health issues from overindulgence in food or drink; social anxiety.',
                universal: 'Focus on quality connections rather than empty social filler.',
                transition: 'A negative shift from celebration to social burnout.'
            }
        }
    },

    // 040 - Four of Cups 圣杯四
    '040': {
        id: '040',
        name: 'Four of Cups',
        nameCn: '圣杯四',
        introduction: '圣杯四象征沉思与不满，一人坐在树下对三杯无动于衷。',
        introductionEn: 'The Four of Cups represents a state of apathy, boredom, or introspection. It suggests that you are so focused on what you lack that you ignore the opportunities right in front of you.',
        upright: {
            keywords: ['沉思', '不满', '冷淡', '内省', '错过机会', '无聊'],
            keywordsEn: ['Apathy', 'Contemplation', 'Boredom', 'Introspection', 'Missed Opportunities', 'Withdrawal'],
            imageDescription: '一人坐在树下沉思，对面前的杯子和云中递来的杯子都无视。',
            imageDescriptionEn: 'A young man sits under a tree with arms crossed, staring at three cups on the grass, seemingly unaware of a fourth cup offered by a hand from a cloud.',
            generalMeaning: '对现状感到不满或无聊，可能忽视了新机会。',
            generalMeaningEn: 'A period of emotional stagnation. You may be feeling uninspired or choosing to withdraw from the world to re-evaluate your feelings.',
            scenes: {
                career: '对工作感到无聊或不满。',
                relationship: '对感情感到倦怠。',
                wealth: '对财务状况不满意。',
                health: '情绪低落或冷漠。',
                universal: '当下需反思但也要注意不要错过机会。',
                transition: '进入沉思内省的过渡阶段。'
            },
            scenesEn: {
                career: 'Stagnation in your current role; ignoring a new job offer; lack of motivation.',
                relationship: 'Emotional boredom; taking a partner for granted; refusing to socialize.',
                wealth: 'Being ungrateful for your current income; focusing on what others have.',
                health: 'Low energy due to psychological fatigue; need for a change of pace.',
                universal: 'Look up; the universe is offering you a gift you haven\'t seen yet.',
                transition: 'A shift from active engagement to passive reflection.'
            }
        },
        reversed: {
            keywords: ['觉醒', '抓住机会', '走出舒适区', '新视角', '动力'],
            keywordsEn: ['Awareness', 'New Perspective', 'Acceptance', 'Motivation', 'End of Rut'],
            imageDescription: '人开始注意到云中的杯子。',
            imageDescriptionEn: 'The figure begins to reach out; the hand from the cloud becomes the focus, symbolizing a shift from apathy to action.',
            generalMeaning: '从沉思中觉醒，开始注意新机会。',
            generalMeaningEn: 'The period of withdrawal is ending. You are becoming aware of the beauty in life again and are ready to seize new emotional opportunities.',
            scenes: {
                career: '重新找到工作动力。',
                relationship: '感情中重新燃起热情。',
                wealth: '发现新的财务机会。',
                health: '情绪开始好转。',
                universal: '当下开始走出消沉，抓住机会。',
                transition: '从冷漠转向觉醒的正向转折。'
            },
            scenesEn: {
                career: 'Rediscovering passion for work; accepting a previously ignored opportunity.',
                relationship: 'Waking up to the value of a relationship; ending a period of isolation.',
                wealth: 'Finally noticing a financial opportunity that was right there.',
                health: 'Recovering from a depressive state; renewed interest in self-care.',
                universal: 'Step out of your shell; the world has been waiting for you.',
                transition: 'A positive shift from stagnation to renewed awareness.'
            }
        }
    },

    // 041 - Five of Cups 圣杯五
    '041': {
        id: '041',
        name: 'Five of Cups',
        nameCn: '圣杯五',
        introduction: '圣杯五象征失落与悲伤，专注于失去的东西而忽视了尚存的可能。',
        introductionEn: 'The Five of Cups is a card of grief, regret, and disappointment. It reminds us that while some things are lost, much still remains if we are willing to turn around.',
        upright: {
            keywords: ['失落', '悲伤', '后悔', '失去', '专注负面', '哀悼'],
            keywordsEn: ['Grief', 'Loss', 'Regret', 'Disappointment', 'Bereavement', 'Pessimism'],
            imageDescription: '黑衣人垂头望着倾倒的杯子，忽视身后仍立着的两杯。',
            imageDescriptionEn: 'A figure in a black cloak mourns over three spilled cups. Behind them stand two full cups, but they are looking the other way.',
            generalMeaning: '处于悲伤失落中，过于专注于失去的东西。',
            generalMeaningEn: 'You are mourning a loss or a failure. It is important to process the pain, but do not let it blind you to the support and love still present in your life.',
            scenes: {
                career: '因失败而沮丧。',
                relationship: '感情受伤或失恋。',
                wealth: '财务损失带来沮丧。',
                health: '悲伤情绪影响健康。',
                universal: '当下需要哀悼但也要看到还拥有的。',
                transition: '进入悲伤哀悼的阶段。'
            },
            scenesEn: {
                career: 'Regret over a failed project; focusing on a job loss rather than new prospects.',
                relationship: 'The pain of a breakup; mourning a lost connection; feeling abandoned.',
                wealth: 'Focusing on a financial loss; regret over a bad investment.',
                health: 'Mental health affected by grief; lethargy due to sadness.',
                universal: 'Don\'t cry over spilled milk; look at what you still have.',
                transition: 'A heavy shift into a period of mourning and regret.'
            }
        },
        reversed: {
            keywords: ['接受', '走出悲伤', '释怀', '前进', '看到希望'],
            keywordsEn: ['Acceptance', 'Healing', 'Moving On', 'Recovery', 'Emotional Rebirth'],
            imageDescription: '人开始转身注意到身后仍立着的杯子。',
            imageDescriptionEn: 'The figure turns toward the two standing cups, signifying a readiness to cross the bridge to a brighter future.',
            generalMeaning: '开始走出悲伤，接受失去并向前看。',
            generalMeaningEn: 'Recovery is beginning. You are finally letting go of the past and acknowledging the positive things that still remain in your life.',
            scenes: {
                career: '从失败中恢复。',
                relationship: '走出失恋的阴影。',
                wealth: '接受损失继续前进。',
                health: '情绪开始恢复。',
                universal: '当下开始释怀，看到希望。',
                transition: '从悲伤转向恢复的正向转折。'
            },
            scenesEn: {
                career: 'Learning from mistakes; finding a new job after a setback.',
                relationship: 'Healing after a breakup; re-entering the dating world with a clean slate.',
                wealth: 'Rebuilding finances after a loss; moving on from debt.',
                health: 'Emotional healing leading to physical recovery; lifting of depression.',
                universal: 'The worst is over; turn toward the light and keep moving.',
                transition: 'A positive shift from grief to emotional resilience.'
            }
        }
    },

    // 042 - Six of Cups 圣杯六
    '042': {
        id: '042',
        name: 'Six of Cups',
        nameCn: '圣杯六',
        introduction: '圣杯六象征怀旧与童真，代表回忆、纯真与与过去的联系。',
        introductionEn: 'The Six of Cups is a card of nostalgia, childhood, and pure intentions. It invites you to revisit happy memories or reconnect with your inner child to find joy in the present.',
        upright: {
            keywords: ['怀旧', '童真', '回忆', '过去', '礼物', '纯真'],
            keywordsEn: ['Nostalgia', 'Childhood', 'Innocence', 'Memories', 'Reunion', 'Generosity'],
            imageDescription: '一个孩子把花杯递给另一个孩子，背景是老房子。',
            imageDescriptionEn: 'A young boy hands a cup filled with flowers to a girl in a courtyard. Everything about the scene feels quaint, safe, and sweet.',
            generalMeaning: '沉浸在美好回忆中，或与过去的人事物重逢。',
            generalMeaningEn: 'A time of warmth and simplicity. You may be reuniting with an old friend, reminiscing about the past, or performing a simple act of kindness.',
            scenes: {
                career: '与旧同事重逢或回顾过去成就。',
                relationship: '旧情人可能出现或回忆过去。',
                wealth: '可能从过去获得遗产或礼物。',
                health: '回归简单健康的生活方式。',
                universal: '当下与过去有连接，享受怀旧的温馨。',
                transition: '进入与过去连接的温馨阶段。'
            },
            scenesEn: {
                career: 'Returning to a previous company; using old skills; business from the past.',
                relationship: 'An old flame returning; childhood sweethearts; a pure and simple love.',
                wealth: 'Inheritance; receiving a sentimental gift; stable family wealth.',
                health: 'Recovering through gentle, traditional methods; connecting with family roots.',
                universal: 'Kindness is its own reward; cherish your happy memories.',
                transition: 'A gentle shift into a period of comfort and nostalgia.'
            }
        },
        reversed: {
            keywords: ['过度怀旧', '无法前进', '童年创伤', '幼稚', '困在过去'],
            keywordsEn: ['Living in the Past', 'Immaturity', 'Childhood Trauma', 'Leaving Home', 'Forward Motion'],
            imageDescription: '花园变得阴暗，花朵枯萎。',
            imageDescriptionEn: 'The child grows up; the courtyard seems small or restrictive, symbolizing the need to leave the past behind.',
            generalMeaning: '过度沉溺于过去或被过去困住。',
            generalMeaningEn: 'You may be stuck in nostalgia to the point of ignoring your current life. It is time to stop looking back and start moving toward your future.',
            scenes: {
                career: '过度依赖旧方法无法创新。',
                relationship: '被过去的感情困住。',
                wealth: '因过去的财务决定受影响。',
                health: '童年创伤影响现在。',
                universal: '当下需放下过去向前看。',
                transition: '从怀旧转向困住的消极转折。'
            },
            scenesEn: {
                career: 'Outdated business models; inability to adapt to new technology.',
                relationship: 'Refusing to leave an old flame; childish behavior in a partnership.',
                wealth: 'Financial issues rooted in the past; outgrowing family support.',
                health: 'Facing deep-seated emotional issues from childhood; growing pains.',
                universal: 'It\'s time to grow up and face the future; the past is gone.',
                transition: 'A necessary shift from childhood comfort to adult reality.'
            }
        }
    },

    // 043 - Seven of Cups 圣杯七
    '043': {
        id: '043',
        name: 'Seven of Cups',
        nameCn: '圣杯七',
        introduction: '圣杯七象征幻想与选择，提示需在众多诱惑与可能中保持清醒。',
        introductionEn: 'The Seven of Cups represents choices, illusions, and daydreams. It warns that while there are many paths before you, not everything that glitters is gold.',
        upright: {
            keywords: ['幻想', '选择', '可能性', '白日梦', '诱惑', '想象力'],
            keywordsEn: ['Illusions', 'Choices', 'Wishful Thinking', 'Daydreaming', 'Temptation', 'Fantasy'],
            imageDescription: '一人面对云中七个杯子，每个杯中有不同的象征物。',
            imageDescriptionEn: 'A figure stands before seven cups floating in the clouds, each containing a different vision: treasure, beauty, a dragon, a snake, etc.',
            generalMeaning: '面对众多选择或沉浸在幻想中，需要分辨现实。',
            generalMeaningEn: 'You are in a world of imagination. While dreaming is good, you must eventually choose one cup and ground yourself in reality to make progress.',
            scenes: {
                career: '有多个机会但需要选择。',
                relationship: '可能有多个追求者或对感情有幻想。',
                wealth: '多种投资选项需要评估。',
                health: '可能对健康方案有不切实际的期望。',
                universal: '当下需分辨幻想与现实，做出选择。',
                transition: '进入选择与幻想的阶段。'
            },
            scenesEn: {
                career: 'Too many job options leading to confusion; unrealistic business plans.',
                relationship: 'Idealizing a partner; multiple "crushes" but no real commitment.',
                wealth: 'Get-rich-quick schemes; confusing financial options.',
                health: 'Trying too many fad diets; placebo effects; hypochondria.',
                universal: 'Be careful what you wish for; focus your energy on one goal.',
                transition: 'A shift into a confusing but imaginative state.'
            }
        },
        reversed: {
            keywords: ['清醒', '决定', '脚踏实地', '排除幻想', '聚焦'],
            keywordsEn: ['Clarity', 'Decision', 'Reality Check', 'Ending Confusion', 'Focus'],
            imageDescription: '云雾散去，选择变得清晰。',
            imageDescriptionEn: 'The clouds dissipate and one cup stands out; the figure finally makes a move to grab the truth.',
            generalMeaning: '从幻想中清醒，能够做出明智选择。',
            generalMeaningEn: 'The fog is lifting. You are finally able to see things as they really are and make a concrete decision after a period of confusion.',
            scenes: {
                career: '确定职业方向。',
                relationship: '对感情有清醒的认识。',
                wealth: '做出合理的财务决定。',
                health: '采取实际的健康措施。',
                universal: '当下能够脚踏实地做出决定。',
                transition: '从幻想转向清醒的正向转折。'
            },
            scenesEn: {
                career: 'Narrowing down your career path; seeing through a false business promise.',
                relationship: 'Seeing the reality of a partner (good or bad); deciding on one person.',
                wealth: 'Pragmatic financial planning; avoiding deceptive investments.',
                health: 'Sticking to a proven medical or fitness plan.',
                universal: 'Wake up and take charge; clarity is your new strength.',
                transition: 'A positive shift from delusion to grounded action.'
            }
        }
    },

    // 044 - Eight of Cups 圣杯八
    '044': {
        id: '044',
        name: 'Eight of Cups',
        nameCn: '圣杯八',
        introduction: '圣杯八象征离开与追寻，主动离开不再能带来满足的现状，去寻找更深层的意义。',
        introductionEn: 'The Eight of Cups represents walking away from a situation that no longer fulfills you. It is a journey of spiritual seeking and letting go of the "almost enough."',
        upright: {
            keywords: ['离开', '追寻', '放下', '旅程', '更深意义', '改变'],
            keywordsEn: ['Walking Away', 'Searching', 'Abandonment', 'Disillusionment', 'Spiritual Journey', 'Departure'],
            imageDescription: '一人在月光下离开排列整齐的杯子，走向远方。',
            imageDescriptionEn: 'Under a crescent moon, a figure walks away from eight neatly stacked cups, heading into the dark, barren mountains.',
            generalMeaning: '选择离开现状去追寻更有意义的事物。',
            generalMeaningEn: 'You have outgrown your current situation. Even if things look okay on the surface, you feel a void and must seek something deeper and more authentic.',
            scenes: {
                career: '主动离开工作寻找新方向。',
                relationship: '结束一段感情寻找更好的。',
                wealth: '放弃眼前利益追求长远目标。',
                health: '改变生活方式追求身心健康。',
                universal: '当下是主动改变追寻的时刻。',
                transition: '从安逸转向追寻的重要转折。'
            },
            scenesEn: {
                career: 'Leaving a secure but boring job; searching for a "calling" rather than a career.',
                relationship: 'Walking away from a long-term relationship that has lost its soul.',
                wealth: 'Giving up material comfort for peace of mind; cutting financial losses.',
                health: 'Retreating for mental rest; seeking holistic healing elsewhere.',
                universal: 'It is okay to leave; what you seek is not where you are.',
                transition: 'A solemn shift from emotional investment to detachment.'
            }
        },
        reversed: {
            keywords: ['逃避', '恐惧改变', '留恋', '漫无目的', '无法放下'],
            keywordsEn: ['Fear of Leaving', 'Stagnation', 'Indecision', 'Returning', 'Clinging'],
            imageDescription: '人站在原地犹豫或迷失方向。',
            imageDescriptionEn: 'The figure hesitates at the edge of the mountains; they keep looking back at the cups, unable to let go.',
            generalMeaning: '害怕改变或漫无目的的逃避。',
            generalMeaningEn: 'You may be staying in a bad situation out of fear of the unknown. You are unhappy, but you are not yet ready to make the necessary departure.',
            scenes: {
                career: '害怕离开不满意的工作。',
                relationship: '无法结束不健康的关系。',
                wealth: '因恐惧而不敢改变财务策略。',
                health: '无法改变不良习惯。',
                universal: '当下需面对恐惧，做出真正的选择。',
                transition: '从追寻转向逃避的消极转折。'
            },
            scenesEn: {
                career: 'Clinging to a toxic workplace for security; refusing to quit.',
                relationship: 'Staying in a dead relationship; "trying again" for the wrong reasons.',
                wealth: 'Hoarding money due to insecurity; refusing to change a failing plan.',
                health: 'Relapsing into bad habits; fear of facing health truths.',
                universal: 'You cannot move forward until you truly leave the past behind.',
                transition: 'A negative shift into avoidance and stagnation.'
            }
        }
    },

    // 045 - Nine of Cups 圣杯九
    '045': {
        id: '045',
        name: 'Nine of Cups',
        nameCn: '圣杯九',
        introduction: '圣杯九象征满足与愿望成真，被称为"许愿牌"，代表情感与物质的双重满足。',
        introductionEn: 'The Nine of Cups is often called the "Wish Card." It represents emotional fulfillment, satisfaction, and the pleasure of having your desires granted.',
        upright: {
            keywords: ['满足', '愿望成真', '幸福', '享受', '成就', '知足'],
            keywordsEn: ['Contentment', 'Satisfaction', 'Wishes Fulfilled', 'Success', 'Luxury', 'Confidence'],
            imageDescription: '一人满足地坐着，双臂交叉，身后是排列整齐的九个杯子。',
            imageDescriptionEn: 'A jovial man sits on a wooden bench with arms crossed, a broad smile on his face. Behind him, nine golden cups are arranged in a row.',
            generalMeaning: '愿望得以实现，享受满足与幸福。',
            generalMeaningEn: 'A time of great happiness and pride. You have worked hard and now you get to enjoy the results. Your "cup" is full.',
            scenes: {
                career: '事业目标达成，感到满足。',
                relationship: '感情美满幸福。',
                wealth: '财务目标实现。',
                health: '身心健康满足。',
                universal: '当下是享受成果的幸福时刻。',
                transition: '进入满足幸福的高峰阶段。'
            },
            scenesEn: {
                career: 'Reaching a career peak; feeling respected; a job well done.',
                relationship: 'A happy, fulfilling romance; being "lucky" in love; emotional security.',
                wealth: 'Abundance; having everything you need; financial comfort.',
                health: 'Radiant health; feeling good in your own skin; peak physical state.',
                universal: 'Savor this moment; your dreams are becoming reality.',
                transition: 'A peak positive shift from desire to realization.'
            }
        },
        reversed: {
            keywords: ['不满足', '贪婪', '空虚', '物质主义', '愿望落空'],
            keywordsEn: ['Greed', 'Dissatisfaction', 'Overindulgence', 'Inner Emptiness', 'Smugness'],
            imageDescription: '杯子虽满但人仍不满足。',
            imageDescriptionEn: 'The man looks bloated or bored; the cups behind him seem like empty trophies, lacking soul.',
            generalMeaning: '即使拥有仍感不满，或愿望无法实现。',
            generalMeaningEn: 'Material success without emotional depth. You may have what you wanted but find it doesn\'t make you as happy as you expected.',
            scenes: {
                career: '成功后仍不满足。',
                relationship: '感情中感到空虚。',
                wealth: '贪婪影响判断。',
                health: '过度追求影响健康。',
                universal: '当下需反思真正的满足是什么。',
                transition: '从满足转向空虚的消极转折。'
            },
            scenesEn: {
                career: 'Hollow success; feeling unfulfilled despite a high salary or title.',
                relationship: 'Shallow connections; using a partner for status; hidden unhappiness.',
                wealth: 'Wasting money on excess; greed; never having "enough."',
                health: 'Health issues from overeating or drinking; physical vanity.',
                universal: 'True satisfaction comes from within, not from external gain.',
                transition: 'A negative shift from genuine joy to hollow excess.'
            }
        }
    },

    // 046 - Ten of Cups 圣杯十
    '046': {
        id: '046',
        name: 'Ten of Cups',
        nameCn: '圣杯十',
        introduction: '圣杯十象征家庭幸福与情感的终极圆满，是塔罗中最幸福的牌之一。',
        introductionEn: 'The Ten of Cups represents ultimate emotional fulfillment and domestic harmony. It is the card of "happily ever after," signifying deep love and communal joy.',
        upright: {
            keywords: ['幸福', '家庭', '圆满', '和谐', '长久的爱', '祝福'],
            keywordsEn: ['Harmony', 'Divine Love', 'Happy Home', 'Alignment', 'Family', 'Peace'],
            imageDescription: '幸福的一家人手拉手，看着彩虹中排列的十个杯子。',
            imageDescriptionEn: 'A couple stands with their arms around each other as two children dance nearby. In the sky, a rainbow appears with ten golden cups inside it.',
            generalMeaning: '家庭幸福，情感生活圆满和谐。',
            generalMeaningEn: 'You are in perfect alignment with your emotional goals. Peace and love prevail in your household and your heart.',
            scenes: {
                career: '工作与生活平衡，事业家庭双丰收。',
                relationship: '爱情与家庭美满幸福。',
                wealth: '财务稳定支持幸福生活。',
                health: '家庭和睦促进身心健康。',
                universal: '当下是情感生活圆满的时刻。',
                transition: '进入幸福圆满的高峰阶段。'
            },
            scenesEn: {
                career: 'Ideal work-life balance; a supportive "work family"; retirement joy.',
                relationship: 'Marriage; long-term blissful commitment; family reunions.',
                wealth: 'Wealth that supports a happy home; long-term financial peace.',
                health: 'Total well-being; feeling spiritually and physically whole.',
                universal: 'Appreciate your family and loved ones; you are truly blessed.',
                transition: 'A joyful peak shift into lasting emotional peace.'
            }
        },
        reversed: {
            keywords: ['家庭不和', '破碎的梦', '失望', '疏离', '价值观冲突'],
            keywordsEn: ['Broken Home', 'Misalignment', 'Family Conflict', 'Disconnection', 'Strained Ties'],
            imageDescription: '彩虹消散，家庭成员疏离。',
            imageDescriptionEn: 'The rainbow fades; the family members are looking in different directions, symbolizing disharmony.',
            generalMeaning: '家庭关系出现问题或梦想破灭。',
            generalMeaningEn: 'Internal strife or a disconnect from your loved ones. The "perfect image" may be cracking, revealing underlying tension.',
            scenes: {
                career: '工作影响家庭生活。',
                relationship: '家庭或感情出现裂痕。',
                wealth: '财务问题影响家庭和谐。',
                health: '家庭问题影响身心健康。',
                universal: '当下需修复家庭关系。',
                transition: '从圆满转向破裂的消极转折。'
            },
            scenesEn: {
                career: 'Workaholism tearing a family apart; lack of support from home for career goals.',
                relationship: 'Domestic arguments; differing family values; hidden unhappiness in a home.',
                wealth: 'Disputes over money or inheritance; living beyond one\'s means.',
                health: 'Mental stress due to domestic problems; neglecting family health.',
                universal: 'Work on healing your home life; communication is failing.',
                transition: 'A negative shift from harmony to domestic tension.'
            }
        }
    },

    // 047 - Page of Cups 圣杯侍从
    '047': {
        id: '047',
        name: 'Page of Cups',
        nameCn: '圣杯侍从',
        introduction: '圣杯侍从象征直觉、创意与情感消息，代表一种温柔而好奇的探索。',
        introductionEn: 'The Page of Cups is the dreamer. He represents the emergence of intuitive ideas, surprising messages, and a youthful openness to emotional experiences.',
        upright: {
            keywords: ['直觉', '创意', '情感消息', '浪漫', '敏感', '好奇'],
            keywordsEn: ['Creative Spark', 'Messages', 'Intuition', 'Sensitivity', 'Surprise', 'Dreamer'],
            imageDescription: '年轻人惊奇地看着圣杯中跃出的鱼。',
            imageDescriptionEn: 'A youth dressed in floral clothing holds a cup. To his surprise, a fish—representing a spark of intuition—pops its head out of the cup.',
            generalMeaning: '接收情感消息，发挥直觉与创意。',
            generalMeaningEn: 'A creative or emotional opportunity is arriving. Be open to "the fish in the cup"—a surprising bit of news or a sudden intuitive hit.',
            scenes: {
                career: '工作中发挥创意。',
                relationship: '可能收到浪漫消息或表白。',
                wealth: '直觉指导财务决定。',
                health: '关注情感健康。',
                universal: '当下注意直觉信息。',
                transition: '进入敏感创意的阶段。'
            },
            scenesEn: {
                career: 'A new artistic project; positive news about an application; listening to your gut in meetings.',
                relationship: 'A love letter; a sweet gesture; a sensitive new admirer.',
                wealth: 'Unexpected financial news; small financial ideas inspired by dreams.',
                health: 'Emotional sensitivity affecting the body; healing through art.',
                universal: 'Follow your imagination; even the weirdest ideas might work.',
                transition: 'A shift into a period of gentle, dreamy discovery.'
            }
        },
        reversed: {
            keywords: ['情感不成熟', '逃避感受', '创意受阻', '坏消息'],
            keywordsEn: ['Emotional Immaturity', 'Creative Block', 'Insecurity', 'Canceled News', 'Escapism'],
            imageDescription: '鱼落回杯中，惊奇变成困惑。',
            imageDescriptionEn: 'The fish stays hidden or the youth looks clumsy, suggesting emotional awkwardness or ignored intuition.',
            generalMeaning: '情感不成熟或创意受阻。',
            generalMeaningEn: 'You may be ignoring your inner child or acting out of emotional insecurity. Intuition is being blocked by fear or ego.',
            scenes: {
                career: '创意无法发挥。',
                relationship: '收到令人失望的消息。',
                wealth: '直觉判断失误。',
                health: '逃避处理情绪问题。',
                universal: '当下需面对情感，成熟处理。',
                transition: '从创意转向受阻的消极转折。'
            },
            scenesEn: {
                career: 'Unoriginality; ignoring creative impulses; negative work feedback.',
                relationship: 'Moodiness; being too clingy or immature in love; disappointing news.',
                wealth: 'Following "hunches" that turn out to be wrong; financial gullibility.',
                health: 'Repressing emotions; psychic exhaustion.',
                universal: 'Stop daydreaming and start facing your real feelings.',
                transition: 'A shift from creative openness to emotional confusion.'
            }
        }
    },

    // 048 - Knight of Cups 圣杯骑士
    '048': {
        id: '048',
        name: 'Knight of Cups',
        nameCn: '圣杯骑士',
        introduction: '圣杯骑士是浪漫主义者，象征情感追求、邀请以及对美与理想的渴望。',
        introductionEn: 'The Knight of Cups is the "Prince Charming" of the Tarot. He represents the pursuit of romantic ideals, following one\'s heart, and arriving with an emotional offer.',
        upright: {
            keywords: ['浪漫', '魅力', '追求', '提议', '理想主义', '创意'],
            keywordsEn: ['Romance', 'Charm', 'Proposal', 'Following the Heart', 'Idealism', 'Creativity'],
            imageDescription: '骑士骑着白马，优雅地捧着圣杯前行。',
            imageDescriptionEn: 'A knight on a slow-moving white horse holds a cup forward. He wears armor decorated with fish and wings on his boots/helmet.',
            generalMeaning: '浪漫的追求或提议到来，充满魅力与理想。',
            generalMeaningEn: 'You are moving toward your heart\'s desire. This card often signals an invitation, a proposal, or a time to act on your artistic passions.',
            scenes: {
                career: '收到吸引人的工作邀请。',
                relationship: '浪漫的追求或求婚。',
                wealth: '理想化的财务提案。',
                health: '追求身心平衡。',
                universal: '当下可能收到浪漫或吸引人的提议。',
                transition: '进入浪漫追求的阶段。'
            },
            scenesEn: {
                career: 'A dream job offer; a creative business proposal; working with grace and charm.',
                relationship: 'A romantic pursuit; an offer of commitment; being "in love with love."',
                wealth: 'Financial offers based on artistic ventures; intuitive investments.',
                health: 'Wellness through emotional peace and beauty; artistic therapy.',
                universal: 'Follow your heart, but remember to keep your eyes on the road.',
                transition: 'An elegant shift from dreaming to active pursuit.'
            }
        },
        reversed: {
            keywords: ['不切实际', '情绪化', '欺骗', '失望', '轻浮'],
            keywordsEn: ['Moodiness', 'Over-Idealism', 'Unreliability', 'Deceptive Charm', 'Heartbreak'],
            imageDescription: '骑士的追求变得不切实际或欺骗。',
            imageDescriptionEn: 'The horse falters; the cup seems empty or is held loosely, signifying unfulfilled promises or shallow charm.',
            generalMeaning: '过于理想化或遭遇情感欺骗。',
            generalMeaningEn: 'Beware of "smoke and mirrors." Someone may be charming you for the wrong reasons, or you are letting your emotions run away with you.',
            scenes: {
                career: '不切实际的工作期望。',
                relationship: '遭遇情感欺骗或轻浮。',
                wealth: '财务提案有问题。',
                health: '情绪化影响判断。',
                universal: '当下需现实评估，小心欺骗。',
                transition: '从浪漫转向失望的消极转折。'
            },
            scenesEn: {
                career: 'Empty professional promises; lack of follow-through; unrealistic project goals.',
                relationship: 'A partner who is "all talk"; emotional manipulation; a "player."',
                wealth: 'Fraudulent financial offers; losing money on "gut feelings" that were actually fantasies.',
                health: 'Emotional highs and lows affecting physical health.',
                universal: 'Look for the truth beneath the charm; grounding is needed.',
                transition: 'A negative shift from romance to disillusionment.'
            }
        }
    },

    // 049 - Queen of Cups 圣杯皇后
    '049': {
        id: '049',
        name: 'Queen of Cups',
        nameCn: '圣杯皇后',
        introduction: '圣杯皇后象征情感智慧、直觉与慈悲，是内在深层感受的守护者。',
        introductionEn: 'The Queen of Cups is the master of empathy and intuition. She sits on the boundary between the subconscious and reality, offering nurturance and deep emotional understanding.',
        upright: {
            keywords: ['同情', '直觉', '情感智慧', '关怀', '敏感', '支持'],
            keywordsEn: ['Empathy', 'Intuition', 'Compassion', 'Nurturing', 'Emotional Security', 'Psychic'],
            imageDescription: '皇后坐在水边，凝视着华丽的有盖圣杯。',
            imageDescriptionEn: 'The Queen sits on a throne by the sea. She stares intensely at an ornate, closed cup, indicating that her thoughts are deep and internal.',
            generalMeaning: '具有深刻的情感智慧和同情心，能够给予支持。',
            generalMeaningEn: 'You are highly sensitive to the energies around you. Use your intuition and heart to guide your decisions and support those in need.',
            scenes: {
                career: '以情感智慧处理工作关系。',
                relationship: '是深情而支持的伴侣。',
                wealth: '凭直觉做出明智的财务决定。',
                health: '情感平衡促进健康。',
                universal: '当下以同情心和直觉行事。',
                transition: '进入情感智慧的正向阶段。'
            },
            scenesEn: {
                career: 'Counseling or artistic roles; being the "heart" of the team; working with empathy.',
                relationship: 'A loving, intuitive partner; deep soul-level connection; unconditional support.',
                wealth: 'Financial decisions guided by intuition; giving to charity.',
                health: 'Psychic health; listen to your body\'s emotional needs.',
                universal: 'Trust your gut; your heart knows the way.',
                transition: 'A shift into a state of deep emotional maturity.'
            }
        },
        reversed: {
            keywords: ['情绪过度', '依赖', '疏离', '情绪操控', '过度敏感'],
            keywordsEn: ['Emotional Insecurity', 'Codependency', 'Moodiness', 'Repression', 'Martyrdom'],
            imageDescription: '皇后的情感变得过度或操控。',
            imageDescriptionEn: 'The Queen is obscured by spray or looking away; the cup seems heavy or tilted, symbolizing emotional imbalance.',
            generalMeaning: '情绪失衡或过于依赖他人。',
            generalMeaningEn: 'Emotional overwhelm. You may be letting your feelings dictate your life too much, or you are acting as a "martyr" in a relationship.',
            scenes: {
                career: '情绪影响工作判断。',
                relationship: '情感依赖或操控。',
                wealth: '情绪化影响财务决定。',
                health: '情绪问题影响健康。',
                universal: '当下需平衡情绪，避免依赖。',
                transition: '从智慧转向失衡的消极转折。'
            },
            scenesEn: {
                career: 'Taking workplace criticism too personally; emotional instability in a role.',
                relationship: 'Smothering a partner; codependency; using guilt to control others.',
                wealth: 'Financial panic; spending based on mood swings.',
                health: 'Psychosomatic illness; emotional burnout; lack of boundaries.',
                universal: 'Draw a boundary; you don\'t have to carry everyone else\'s feelings.',
                transition: 'A negative shift from empathy to emotional instability.'
            }
        }
    },

    // 050 - King of Cups 圣杯国王
    '050': {
        id: '050',
        name: 'King of Cups',
        nameCn: '圣杯国王',
        introduction: '圣杯国王象征情感的极致掌控，代表智慧、宽容、成熟与在风暴中保持冷静。',
        introductionEn: 'The King of Cups represents mastery over emotions. He sits amidst a turbulent sea but remains calm and balanced, symbolizing the power of a mature heart.',
        upright: {
            keywords: ['情感成熟', '平衡', '智慧', '外交', '慷慨', '支持'],
            keywordsEn: ['Emotional Balance', 'Wisdom', 'Diplomacy', 'Calmness', 'Maturity', 'Compassion'],
            imageDescription: '国王稳坐于波涛中的王座，一手持杯一手持权杖。',
            imageDescriptionEn: 'The King sits on a throne floating on the sea. Despite the waves and a leaping fish, he remains steady, holding a scepter and a cup.',
            generalMeaning: '能够成熟地掌控情感，平衡理性与感性。',
            generalMeaningEn: 'A time of emotional stability. You are able to handle difficult people or situations with calm and compassion, never losing your head.',
            scenes: {
                career: '以成熟和外交方式处理工作。',
                relationship: '是成熟稳定的伴侣。',
                wealth: '理性与感性平衡的财务管理。',
                health: '情感平衡促进整体健康。',
                universal: '当下以成熟智慧处理情感事务。',
                transition: '进入情感掌控的正向阶段。'
            },
            scenesEn: {
                career: 'Expert negotiation; a calm leader during a crisis; mentoring others with empathy.',
                relationship: 'A pillar of strength; a partner who provides security and understanding.',
                wealth: 'Stable and generous financial state; balanced investment strategy.',
                health: 'Healthy heart and nervous system; stress-free mindset.',
                universal: 'Be the calm in the storm; your maturity is your strength.',
                transition: 'A positive shift from feeling to mastering the emotional depths.'
            }
        },
        reversed: {
            keywords: ['情绪压抑', '冷漠', '操控', '情绪爆发', '失衡'],
            keywordsEn: ['Emotional Manipulation', 'Mood Swings', 'Volatility', 'Coldness', 'Dishonesty'],
            imageDescription: '国王的情感失去平衡，波涛变得汹涌。',
            imageDescriptionEn: 'The waves threaten the throne; the King looks unsteady or aggressive, showing a loss of emotional control.',
            generalMeaning: '情感失衡或压抑，可能突然爆发。',
            generalMeaningEn: 'Emotional instability. This card warns against becoming manipulative, vengeful, or letting your temper ruin your peace.',
            scenes: {
                career: '情绪问题影响领导力。',
                relationship: '情感操控或冷漠。',
                wealth: '情绪影响财务判断。',
                health: '压抑的情绪影响健康。',
                universal: '当下需正视并平衡情绪。',
                transition: '从掌控转向失衡的消极转折。'
            },
            scenesEn: {
                career: 'An unstable boss; unprofessional emotional outbursts; lack of focus.',
                relationship: 'Emotional cruelty; a partner who withdraws their love to punish you; moodiness.',
                wealth: 'Financial loss through irrationality; a "leaky" wallet.',
                health: 'Addiction as emotional escape; hypertension or stress-related issues.',
                universal: 'Regain your center; you are acting out of fear, not wisdom.',
                transition: 'A negative shift from emotional mastery to volatility.'
            }
        }
    },

    // ========== Minor Arcana - Swords (宝剑) ==========

    // 051 - Ace of Swords 宝剑王牌
    '051': {
        id: '051',
        name: 'Ace of Swords',
        nameCn: '宝剑王牌',
        introduction: '宝剑王牌象征清晰与真理，一只手从云中伸出握着双刃剑，代表思想的力量、真相与决断。',
        introductionEn: 'The Ace of Swords represents a breakthrough of clarity and mental power. It is the raw element of Air, signifying a new perspective, the triumph of intellect, and the pursuit of absolute truth.',
        upright: {
            keywords: ['清晰', '真理', '突破', '决断', '新想法', '正义'],
            keywordsEn: ['Clarity', 'Breakthrough', 'Victory', 'New Ideas', 'Mental Power', 'Truth', 'Focus'],
            imageDescription: '一只手从云中伸出握着戴冠的宝剑。',
            imageDescriptionEn: 'A divine hand emerges from a cloud clutching an upright sword, topped with a golden crown draped in olive and palm branches, symbolizing victory and peace.',
            generalMeaning: '思维清晰，看透真相，是突破性想法的开始。',
            generalMeaningEn: 'A moment of profound insight. You are able to see through illusions and cut to the heart of a matter. A new project or idea will require strong logic and decisiveness.',
            scenes: {
                career: '事业上有清晰的洞察力和新想法。',
                relationship: '感情中需要诚实和清晰沟通。',
                wealth: '财务决策清晰果断。',
                health: '理性分析健康问题。',
                universal: '当下是看清真相、做出决断的时刻。',
                transition: '进入思维清晰的新阶段。'
            },
            scenesEn: {
                career: 'A sudden brilliant idea; success in exams or legal matters; a clean break from old habits.',
                relationship: 'Difficult but necessary honest conversations; seeing a partner’s true nature clearly.',
                wealth: 'Making a surgical, logical investment choice; clear-headed financial planning.',
                health: 'Surgery (if applicable); mental clarity helping physical recovery; a new diagnostic breakthrough.',
                universal: 'Seek the truth at all costs; your mind is your sharpest weapon.',
                transition: 'A powerful shift from confusion to sudden understanding.'
            }
        },
        reversed: {
            keywords: ['混乱', '误解', '残忍', '不公正', '思维阻塞'],
            keywordsEn: ['Confusion', 'Miscommunication', 'Clouded Judgment', 'Injustice', 'Abuse of Power'],
            imageDescription: '宝剑失去光芒或指向错误方向。',
            imageDescriptionEn: 'The sword is inverted or shaky; the crown falls, representing a failure of logic or a misuse of words.',
            generalMeaning: '思维混乱或以不当方式使用智力。',
            generalMeaningEn: 'You may be feeling confused or making decisions based on incomplete information. Be careful not to use your words as weapons in a destructive way.',
            scenes: {
                career: '事业上思路不清或决策失误。',
                relationship: '感情中沟通不善导致误解。',
                wealth: '财务判断力下降。',
                health: '过度思虑影响健康。',
                universal: '当下需恢复清晰思维。',
                transition: '从清晰转向混乱的消极转折。'
            },
            scenesEn: {
                career: 'Bureaucratic red tape; muddled thinking; a project failing due to poor logic.',
                relationship: 'Arguments filled with cutting words; misunderstandings; lack of mental connection.',
                wealth: 'Financial confusion; being misled by complex contracts.',
                health: 'Mental fog; stress headaches; overthinking preventing recovery.',
                universal: 'Take a step back; your current perspective is distorted.',
                transition: 'A negative shift from clarity into mental chaos.'
            }
        }
    },

    // 052 - Two of Swords 宝剑二
    '052': {
        id: '052',
        name: 'Two of Swords',
        nameCn: '宝剑二',
        introduction: '宝剑二象征僵局与抉择，蒙眼女子双手交叉持剑，代表逃避决定或内心冲突。',
        introductionEn: 'The Two of Swords represents a state of stalemate or being at a crossroads. It signifies a difficult choice where one might be choosing to ignore the facts to maintain a fragile peace.',
        upright: {
            keywords: ['僵局', '抉择', '平衡', '回避', '内心冲突', '中立'],
            keywordsEn: ['Stalemate', 'Difficult Choice', 'Blocked Emotions', 'Avoidance', 'Truce', 'Decision Required'],
            imageDescription: '蒙眼女子坐于水边，双手交叉持两把剑。',
            imageDescriptionEn: 'A blindfolded woman sits by the sea, holding two heavy swords crossed over her chest, balancing them with effort in a state of suspended action.',
            generalMeaning: '面临艰难抉择，暂时选择不做决定。',
            generalMeaningEn: 'You are caught between two opposing forces or choices. You may be closing your eyes to the truth because you aren\'t ready to face the consequences of a decision.',
            scenes: {
                career: '工作中面临两难选择。',
                relationship: '感情中犹豫不决。',
                wealth: '财务决策陷入僵局。',
                health: '需要做出健康方面的选择。',
                universal: '当下需要做出决定，无法永远回避。',
                transition: '进入需要抉择的僵局阶段。'
            },
            scenesEn: {
                career: 'Stuck between two job offers; a workplace stand-off; refusing to take a side in office politics.',
                relationship: 'An emotional impasse; hiding from the truth of a relationship; a "cold war" with a partner.',
                wealth: 'Ignoring financial warning signs; being unable to choose an investment path.',
                health: 'Refusal to acknowledge a health issue; choosing between two different treatments.',
                universal: 'Open your eyes; ignoring the problem doesn\'t make it go away.',
                transition: 'A shift from action to a state of tense, defensive balance.'
            }
        },
        reversed: {
            keywords: ['做出决定', '信息揭示', '压力', '过度思虑'],
            keywordsEn: ['Decision Made', 'Clarity', 'Movement', 'Emotional Release', 'Truth Revealed'],
            imageDescription: '眼罩移除，开始面对选择。',
            imageDescriptionEn: 'The blindfold falls off or the swords are dropped, suggesting the stalemate is finally broken and the path is revealed.',
            generalMeaning: '被迫做出决定或获得新信息。',
            generalMeaningEn: 'The period of indecision is over. Whether you are ready or not, the choice has been made, and the truth is coming to light.',
            scenes: {
                career: '工作压力迫使决定。',
                relationship: '感情中必须做出选择。',
                wealth: '财务问题不能再拖延。',
                health: '需要正视健康问题。',
                universal: '当下是时候做出决定了。',
                transition: '从僵局转向决定的转折。'
            },
            scenesEn: {
                career: 'Finally choosing a path after long hesitation; the resolution of a work conflict.',
                relationship: 'Ending a period of silence; choosing between two partners; facing the truth of love.',
                wealth: 'Taking action on a financial matter that was previously ignored.',
                health: 'Facing the reality of a medical condition; end of denial.',
                universal: 'The time for waiting has passed; step forward with your eyes open.',
                transition: 'A positive shift from paralysis to movement.'
            }
        }
    },

    // 053 - Three of Swords 宝剑三
    '053': {
        id: '053',
        name: 'Three of Swords',
        nameCn: '宝剑三',
        introduction: '宝剑三象征心碎与悲伤，三把剑刺穿心脏，代表痛苦、背叛与失落。',
        introductionEn: 'The Three of Swords is the card of emotional pain and heartbreak. It represents the necessity of feeling grief in order to heal, often brought about by words or events that hurt deeply.',
        upright: {
            keywords: ['心碎', '悲伤', '背叛', '痛苦', '失落', '分离'],
            keywordsEn: ['Heartbreak', 'Grief', 'Sorrow', 'Separation', 'Betrayal', 'Rejection', 'Pain'],
            imageDescription: '三把剑刺穿一颗红心，背景是阴雨天空。',
            imageDescriptionEn: 'Three swords pierce a large red heart, suspended against a backdrop of storm clouds and falling rain.',
            generalMeaning: '经历心碎或痛苦的情感体验。',
            generalMeaningEn: 'A period of intense sadness. This card often indicates a breakup, betrayal, or a painful realization. You must allow yourself to cry and process the hurt.',
            scenes: {
                career: '工作中遭遇挫折或被背叛。',
                relationship: '感情受伤或分手。',
                wealth: '财务损失带来痛苦。',
                health: '情绪痛苦影响健康。',
                universal: '当下需面对并处理悲伤。',
                transition: '进入悲伤痛苦的阶段。'
            },
            scenesEn: {
                career: 'Being fired or let go; a business partnership ending badly; hurtful professional criticism.',
                relationship: 'A painful breakup; discovering a partner’s dishonesty; the sting of rejection.',
                wealth: 'Severe financial loss; a contract being cut; losing money through a trusted person.',
                health: 'Heart issues (physical or emotional); mental stress affecting physical heart health.',
                universal: 'Pain is a part of growth; let the rain wash away the sorrow.',
                transition: 'A painful shift from peace to deep emotional hurt.'
            }
        },
        reversed: {
            keywords: ['恢复', '释放痛苦', '原谅', '走出阴霾'],
            keywordsEn: ['Healing', 'Forgiveness', 'Recovery', 'Releasing Pain', 'Overcoming Grief'],
            imageDescription: '剑开始从心中拔出，愈合开始。',
            imageDescriptionEn: 'The swords begin to slide out of the heart; the storm clouds start to part, indicating the beginning of recovery.',
            generalMeaning: '开始从痛苦中康复。',
            generalMeaningEn: 'The worst of the pain has passed. You are ready to let go of the bitterness and begin the slow process of healing and forgiveness.',
            scenes: {
                career: '从工作挫折中恢复。',
                relationship: '开始走出情伤。',
                wealth: '接受财务损失向前看。',
                health: '情绪开始恢复。',
                universal: '当下开始愈合过程。',
                transition: '从痛苦转向恢复的正向转折。'
            },
            scenesEn: {
                career: 'Recovering after a job loss; finding peace with a professional failure.',
                relationship: 'Forgiving an ex; moving past a period of heartbreak; beginning to trust again.',
                wealth: 'Financial stability returning after a crisis; accepting losses.',
                health: 'Recovery from depression; heart health improving; emotional release.',
                universal: 'The healing has begun; don\'t hold on to the old pain.',
                transition: 'A positive shift from suffering into renewal.'
            }
        }
    },

    // 054 - Four of Swords 宝剑四
    '054': {
        id: '054',
        name: 'Four of Swords',
        nameCn: '宝剑四',
        introduction: '宝剑四象征休息与恢复，骑士躺在石棺上，代表需要休养生息。',
        introductionEn: 'The Four of Swords is a card of rest, recuperation, and quiet reflection. It suggests that you have been through a battle and now require a period of sanctuary to heal.',
        upright: {
            keywords: ['休息', '恢复', '静养', '反思', '冥想', '暂停'],
            keywordsEn: ['Rest', 'Recuperation', 'Contemplation', 'Quiet', 'Meditation', 'Sanctuary'],
            imageDescription: '骑士躺在石棺上，三把剑悬于上方，一把在身侧。',
            imageDescriptionEn: 'The effigy of a knight lies on a tomb in a church; three swords hang on the wall above him, while a fourth lies beneath him, signifying readiness in rest.',
            generalMeaning: '需要休息和恢复，暂时退出战斗。',
            generalMeaningEn: 'Withdraw from the world to recharge. This is not a card of defeat, but of necessary preparation. Your mind needs silence to function again.',
            scenes: {
                career: '工作中需要休息调整。',
                relationship: '感情需要冷静期。',
                wealth: '财务上暂停活动。',
                health: '身体需要休养。',
                universal: '当下是休息恢复的时期。',
                transition: '进入休养调整的阶段。'
            },
            scenesEn: {
                career: 'Taking a vacation or sabbatical; stepping back from work stress; quiet planning.',
                relationship: 'Giving each other space; a "cooling off" period; reflecting on love alone.',
                wealth: 'Conserving resources; stopping all investments to reassess.',
                health: 'Hospitalization or bed rest; recovering from illness; mental health day.',
                universal: 'Silence is your best teacher right now; rest deeply.',
                transition: 'A shift from intense activity to necessary stillness.'
            }
        },
        reversed: {
            keywords: ['恢复活力', '行动起来', '躁动不安', '过度休息'],
            keywordsEn: ['Reawakening', 'Restlessness', 'Returning to Activity', 'Burnout', 'Insomnia'],
            imageDescription: '骑士开始苏醒，准备起身。',
            imageDescriptionEn: 'The knight seems to be getting up; the swords are vibrating, suggesting a return to the noise and struggle of life.',
            generalMeaning: '准备恢复活动或无法安静休息。',
            generalMeaningEn: 'You are ready to re-enter the fray, or perhaps you are being forced back into action before you are fully rested. Watch for signs of burnout.',
            scenes: {
                career: '准备重返工作。',
                relationship: '准备重新开始社交。',
                wealth: '准备恢复财务活动。',
                health: '恢复活力或躁动不安。',
                universal: '当下是时候恢复活动了。',
                transition: '从休息转向行动的转折。'
            },
            scenesEn: {
                career: 'Returning to work after a break; becoming restless with a slow project.',
                relationship: 'Reconnecting after a break; inability to give a partner space.',
                wealth: 'Starting to spend or invest again after a freeze.',
                health: 'Insomnia; returning to a busy life too early; slow recovery of energy.',
                universal: 'Gently re-engage with your goals; don\'t rush the process.',
                transition: 'A shift from rest back into the active world.'
            }
        }
    },

    // 055 - Five of Swords 宝剑五
    '055': {
        id: '055',
        name: 'Five of Swords',
        nameCn: '宝剑五',
        introduction: '宝剑五象征冲突与失败，一人捡起地上的剑，其他人沮丧离开，代表胜利的代价。',
        introductionEn: 'The Five of Swords is a card of hollow victory and conflict. It warns that winning an argument or battle might cost you more in relationships or self-respect than it is worth.',
        upright: {
            keywords: ['冲突', '失败', '胜利的代价', '背叛', '自私'],
            keywordsEn: ['Self-Interest', 'Conflict', 'Win at all costs', 'Hostility', 'Humiliation', 'Defeat'],
            imageDescription: '一人持剑得意，另外两人垂头丧气离去。',
            imageDescriptionEn: 'A man with a smirk holds three swords while two more lie on the ground. In the distance, two figures walk away with shoulders slumped in defeat.',
            generalMeaning: '经历冲突，即使胜利也付出代价。',
            generalMeaningEn: 'You may have "won," but at what cost? There is a sense of hostility or betrayal in the air. The victory feels empty and lonely.',
            scenes: {
                career: '工作中的争斗或竞争。',
                relationship: '感情中的冲突。',
                wealth: '不择手段的财务竞争。',
                health: '冲突带来压力。',
                universal: '当下需评估冲突是否值得。',
                transition: '进入冲突争斗的阶段。'
            },
            scenesEn: {
                career: 'Winning a workplace argument but losing respect; cutthroat competition; office bullying.',
                relationship: 'Winning a fight just to hurt a partner; lack of empathy in a relationship.',
                wealth: 'Getting money through unfair means; a lawsuit where no one truly wins.',
                health: 'Physical decline due to constant fighting or mental aggression.',
                universal: 'Ask yourself: is this victory worth the burnt bridges?',
                transition: 'A sharp shift from cooperation to hostile individualism.'
            }
        },
        reversed: {
            keywords: ['和解', '放下', '道歉', '学习教训'],
            keywordsEn: ['Resolution', 'Desire for Peace', 'Moving On', 'Regret', 'Ending Conflict'],
            imageDescription: '丢下的剑被拾起，开始和解。',
            imageDescriptionEn: 'The man drops the extra swords; the retreating figures look back, suggesting a possible end to the hostility.',
            generalMeaning: '准备放下冲突，寻求和解。',
            generalMeaningEn: 'You are tired of the fighting and are ready to move on. You may be apologizing or finally realizing that the conflict was destructive.',
            scenes: {
                career: '工作冲突开始化解。',
                relationship: '感情中寻求和解。',
                wealth: '结束财务纷争。',
                health: '压力开始缓解。',
                universal: '当下是时候放下了。',
                transition: '从冲突转向和解的正向转折。'
            },
            scenesEn: {
                career: 'Ending a workplace feud; deciding to collaborate after a period of tension.',
                relationship: 'Apologizing for hurtful words; deciding to stop the "blame game."',
                wealth: 'Paying off a debt that caused stress; settling a legal dispute peacefully.',
                health: 'Release of tension; mental health recovery after a stressful conflict.',
                universal: 'Let go of the need to be right; choose peace instead.',
                transition: 'A positive shift from hostility toward reconciliation.'
            }
        }
    },

    // 056 - Six of Swords 宝剑六
    '056': {
        id: '056',
        name: 'Six of Swords',
        nameCn: '宝剑六',
        introduction: '宝剑六象征过渡与转变，船夫载着一对母子从波涛驶向平静水域，代表离开困境。',
        introductionEn: 'The Six of Swords represents a transition or a journey away from trouble. It is the card of moving into calmer waters, carrying the memories of the past but looking toward a better future.',
        upright: {
            keywords: ['过渡', '转变', '离开', '疗愈', '旅程', '前进'],
            keywordsEn: ['Transition', 'Moving On', 'Departure', 'Healing', 'Journey', 'Calm after Storm'],
            imageDescription: '船夫划船载着母子从汹涌水域驶向平静处。',
            imageDescriptionEn: 'A man rows a boat containing two passengers toward a distant shore. Six swords are upright in the boat, but the water on the far side is smooth and calm.',
            generalMeaning: '离开困境，踏上新的旅程。',
            generalMeaningEn: 'A period of slow but necessary change. You are leaving a difficult situation behind. It may be sad to go, but it is the only way to find peace.',
            scenes: {
                career: '离开不好的工作环境。',
                relationship: '离开不健康的关系。',
                wealth: '摆脱财务困境。',
                health: '疾病康复在望。',
                universal: '当下是过渡转变的时期。',
                transition: '从困境进入过渡期。'
            },
            scenesEn: {
                career: 'Changing to a better job; moving past a project failure; relocation for work.',
                relationship: 'Escaping a toxic dynamic; a quiet period after a major fight; a physical journey together.',
                wealth: 'Moving away from debt; slowly rebuilding financial security.',
                health: 'Convalescence; the slow process of getting better after an illness.',
                universal: 'Keep rowing; you are heading toward a more peaceful place.',
                transition: 'A quiet shift from turmoil to tranquility.'
            }
        },
        reversed: {
            keywords: ['困住', '无法前进', '抵抗改变', '返回'],
            keywordsEn: ['Stuck', 'Resistance to Change', 'Unfinished Business', 'Difficult Transition', 'Delayed Journey'],
            imageDescription: '船停滞不前或转向回去。',
            imageDescriptionEn: 'The boat is spinning in circles or the swords fall over, suggesting that the journey is being sabotaged or delayed.',
            generalMeaning: '无法离开困境或抵抗改变。',
            generalMeaningEn: 'You are struggling to move on. Unresolved issues from the past are pulling you back, or external obstacles are making the transition very difficult.',
            scenes: {
                career: '无法离开不好的工作。',
                relationship: '困在不健康的关系中。',
                wealth: '财务困境持续。',
                health: '康复受阻。',
                universal: '当下需克服阻碍，允许改变。',
                transition: '从过渡转向停滞的消极转折。'
            },
            scenesEn: {
                career: 'Being unable to quit a bad job; a relocation falling through; stuck in a professional rut.',
                relationship: 'Unable to let go of an ex; returning to a bad relationship; stuck in a cycle of drama.',
                wealth: 'Debt returning; financial transitions being blocked by old obligations.',
                health: 'A relapse; slow or stagnant recovery; mental blockages to healing.',
                universal: 'Identify what is tying you to the shore; you must cut the rope to move on.',
                transition: 'A shift from progress to frustrating stagnation.'
            }
        }
    },

    // 057 - Seven of Swords 宝剑七
    '057': {
        id: '057',
        name: 'Seven of Swords',
        nameCn: '宝剑七',
        introduction: '宝剑七象征策略与欺骗，一人偷偷带走五把剑，代表策略、欺骗或独立行动。',
        introductionEn: 'The Seven of Swords is the card of strategy, but also of deception. It represents a "lone wolf" mentality where one might be trying to get away with something through wit rather than force.',
        upright: {
            keywords: ['策略', '欺骗', '独立', '偷偷摸摸', '逃避', '机智'],
            keywordsEn: ['Strategy', 'Deception', 'Stealth', 'Sneaking Away', 'Avoiding Confrontation', 'Independence'],
            imageDescription: '一人回头看着营地，手里抱着五把剑溜走。',
            imageDescriptionEn: 'A man steals away from a camp carrying five swords in his arms, looking back over his shoulder. Two swords remain stuck in the ground behind him.',
            generalMeaning: '采取策略性行动或可能涉及欺骗。',
            generalMeaningEn: 'You may be trying to solve a problem in an unconventional or sneaky way. While it might work for now, it often indicates a lack of transparency or a desire to avoid direct conflict.',
            scenes: {
                career: '工作中需要策略或小心背叛。',
                relationship: '感情中可能有隐瞒。',
                wealth: '财务上需要策略。',
                health: '逃避健康问题。',
                universal: '当下需审慎行事，注意欺骗。',
                transition: '进入需要策略的阶段。'
            },
            scenesEn: {
                career: 'Using secret tactics to get ahead; working behind the scenes; being wary of a dishonest colleague.',
                relationship: 'Keeping secrets from a partner; an affair or emotional hiding; lack of trust.',
                wealth: 'Tax avoidance or "creative" accounting; being careful with your belongings.',
                health: 'Self-medicating; ignoring symptoms by hiding them from others.',
                universal: 'Trust your instincts but watch your back; not everyone is playing fair.',
                transition: 'A shift from open conflict to tactical concealment.'
            }
        },
        reversed: {
            keywords: ['被揭露', '诚实', '面对后果', '坦白'],
            keywordsEn: ['Revelation', 'Confession', 'Caught Red-Handed', 'Honesty', 'Changing Tactics'],
            imageDescription: '偷剑者被发现或主动放下剑。',
            imageDescriptionEn: 'The man drops the swords or is caught by the guards; the hidden camp comes into full view.',
            generalMeaning: '欺骗被揭露或选择诚实。',
            generalMeaningEn: 'Secrets are coming out. The "sneaky" approach is failing, and you are forced to face the consequences of your actions or choose a more honest path.',
            scenes: {
                career: '不诚实行为被发现。',
                relationship: '感情中选择坦诚。',
                wealth: '财务问题曝光。',
                health: '正视健康问题。',
                universal: '当下是时候诚实面对了。',
                transition: '从隐瞒转向揭露的转折。'
            },
            scenesEn: {
                career: 'A workplace scam exposed; returning to a more transparent business model.',
                relationship: 'Coming clean to a partner; discovering a secret; ending a period of lying.',
                wealth: 'An audit; being caught for financial dishonesty; deciding to be honest about debt.',
                health: 'Finally seeing a doctor for a hidden problem; the truth about health habits.',
                universal: 'The truth sets you free, even if it hurts initially.',
                transition: 'A shift from deception to painful but necessary honesty.'
            }
        }
    },

    // 058 - Eight of Swords 宝剑八
    '058': {
        id: '058',
        name: 'Eight of Swords',
        nameCn: '宝剑八',
        introduction: '宝剑八象征束缚与限制，蒙眼女子被八把剑包围，代表自我设限与感到被困。',
        introductionEn: 'The Eight of Swords represents self-imposed restrictions. It signifies feeling trapped by one\'s own thoughts or circumstances, while the exit is actually available if one only opens their eyes.',
        upright: {
            keywords: ['束缚', '限制', '受困', '无力', '恐惧', '自我设限'],
            keywordsEn: ['Trapped', 'Self-Limitation', 'Powerlessness', 'Blindfold', 'Restriction', 'Anxiety'],
            imageDescription: '蒙眼被绑的女子站在八把剑之间。',
            imageDescriptionEn: 'A woman is tied up and blindfolded, standing in a muddy area surrounded by eight swords. However, her feet are not tied and there is a clear path out.',
            generalMeaning: '感到被困住，但限制多是自己设的。',
            generalMeaningEn: 'You feel like you have no options, but this is a mental prison. Fear is the only thing keeping you from walking toward freedom.',
            scenes: {
                career: '工作中感到无路可走。',
                relationship: '感情中感到被困。',
                wealth: '财务上感到受限。',
                health: '被负面思维困住。',
                universal: '当下需认识到很多限制是自己设的。',
                transition: '进入感到受困的阶段。'
            },
            scenesEn: {
                career: 'Feeling stuck in a dead-end job; paralyzed by fear of failure; unable to see career opportunities.',
                relationship: 'Feeling trapped in a marriage or partnership; victim mentality; refusing to leave.',
                wealth: 'Feeling helpless about debt; "ostrich policy" regarding money.',
                health: 'Psychological distress causing physical paralysis; feeling like you can\'t get healthy.',
                universal: 'Remove your blindfold; the path to freedom is right in front of you.',
                transition: 'A shift from freedom into a state of mental imprisonment.'
            }
        },
        reversed: {
            keywords: ['解脱', '打破限制', '新视角', '自由'],
            keywordsEn: ['Freedom', 'Release', 'New Perspective', 'Taking Control', 'Clarity'],
            imageDescription: '女子开始解开束缚或移除眼罩。',
            imageDescriptionEn: 'The woman begins to untie herself or the blindfold slips, showing her the way through the swords.',
            generalMeaning: '开始突破自我限制，获得自由。',
            generalMeaningEn: 'You are beginning to see the reality of your situation and are ready to take responsibility for your freedom. The mental blocks are dissolving.',
            scenes: {
                career: '突破工作中的限制。',
                relationship: '感情中获得自由。',
                wealth: '找到财务出路。',
                health: '战胜负面思维。',
                universal: '当下开始突破限制。',
                transition: '从受困转向自由的正向转折。'
            },
            scenesEn: {
                career: 'Finding a way out of a bad job; overcoming office anxiety; seeing new potential.',
                relationship: 'Leaving an abusive or stagnant relationship; standing up for oneself.',
                wealth: 'Finding a solution to debt; taking active control of finances.',
                health: 'Overcoming phobias; mental health improvement; seeing the light at the end of the tunnel.',
                universal: 'You are stronger than your fears; step out of the circle of swords.',
                transition: 'A positive shift from victimhood to empowerment.'
            }
        }
    },

    // 059 - Nine of Swords 宝剑九
    '059': {
        id: '059',
        name: 'Nine of Swords',
        nameCn: '宝剑九',
        introduction: '宝剑九象征焦虑与噩梦，一人夜半惊醒，九把剑悬于身后，代表忧虑与精神痛苦。',
        introductionEn: 'The Nine of Swords is the card of anxiety, nightmares, and mental anguish. It signifies a period where your worries are overwhelming you, often keeping you up at night.',
        upright: {
            keywords: ['焦虑', '噩梦', '忧虑', '失眠', '罪恶感', '精神痛苦'],
            keywordsEn: ['Anxiety', 'Nightmares', 'Worry', 'Guilt', 'Insomnia', 'Mental Anguish', 'Fear'],
            imageDescription: '一人坐在床上捂脸，墙上悬挂九把剑。',
            imageDescriptionEn: 'A person sits up in bed, face buried in their hands in despair. Nine swords hang on the wall like a heavy cloud above them.',
            generalMeaning: '被焦虑和忧虑折磨，精神痛苦。',
            generalMeaningEn: 'Your mind is your worst enemy right now. You are blowing problems out of proportion or suffering from deep guilt. Remember, most of what we fear never happens.',
            scenes: {
                career: '工作压力导致焦虑。',
                relationship: '感情忧虑不安。',
                wealth: '财务担忧影响睡眠。',
                health: '焦虑影响身心健康。',
                universal: '当下需面对并处理焦虑。',
                transition: '进入焦虑困扰的阶段。'
            },
            scenesEn: {
                career: 'Extreme workplace stress; fear of getting fired; impostor syndrome.',
                relationship: 'Worrying about betrayal; being kept up at night by an argument; deep insecurity.',
                wealth: 'Paralyzing fear of poverty; obsessive worrying over financial stability.',
                health: 'Insomnia; panic attacks; stress-induced illness.',
                universal: 'Don\'t suffer in silence; your fears are bigger in the dark.',
                transition: 'A shift from day-light logic to night-time terror.'
            }
        },
        reversed: {
            keywords: ['释放焦虑', '恢复', '面对恐惧', '寻求帮助'],
            keywordsEn: ['Release of Anxiety', 'Finding Help', 'Facing Fears', 'Recovery', 'Hope Returning'],
            imageDescription: '剑开始消散，人开始放松。',
            imageDescriptionEn: 'The person looks up from their hands; the swords on the wall seem less sharp or start to fade away.',
            generalMeaning: '开始释放焦虑，情况好转。',
            generalMeaningEn: 'The nightmare is ending. You are beginning to realize that your worries were exaggerated or you are finally seeking help to deal with your mental state.',
            scenes: {
                career: '工作压力开始缓解。',
                relationship: '感情忧虑减轻。',
                wealth: '财务担忧减少。',
                health: '焦虑症状改善。',
                universal: '当下焦虑开始缓解。',
                transition: '从焦虑转向缓解的正向转折。'
            },
            scenesEn: {
                career: 'Relief from work pressure; seeing that a professional fear was unfounded.',
                relationship: 'Coming out of a period of deep worry about a partner; realizing you are safe.',
                wealth: 'Financial worries subsiding; finding a way to manage expenses.',
                health: 'Sleeping better; recovery from a period of high stress; mental clarity.',
                universal: 'The dawn is coming; let go of the shadows of the night.',
                transition: 'A positive shift from mental torture to peace.'
            }
        }
    },

    // 060 - Ten of Swords 宝剑十
    '060': {
        id: '060',
        name: 'Ten of Swords',
        nameCn: '宝剑十',
        introduction: '宝剑十象征终结与毁灭，一人倒地被十把剑刺穿，代表痛苦的结束与黎明将至。',
        introductionEn: 'The Ten of Swords marks a final, painful ending. It is a card of "hitting rock bottom," but it carries the promise that the cycle is over and a new dawn is beginning.',
        upright: {
            keywords: ['终结', '毁灭', '最低点', '背叛', '失败', '黎明'],
            keywordsEn: ['Finality', 'Betrayal', 'Rock Bottom', 'Endings', 'Martyrdom', 'Deep Wounds'],
            imageDescription: '一人倒地，背上插着十把剑，远处有黎明。',
            imageDescriptionEn: 'A man lies face down with ten swords in his back. The sky above is dark, but on the horizon, a golden sun is starting to rise.',
            generalMeaning: '经历痛苦的终结，但黎明即将到来。',
            generalMeaningEn: 'You cannot go any lower than this. The betrayal or failure is complete. Accept the finality so that you can begin to heal. The worst is over.',
            scenes: {
                career: '事业遭遇重大挫折。',
                relationship: '感情彻底结束。',
                wealth: '财务触底。',
                health: '健康最低点。',
                universal: '当下虽是最低点，但新开始在望。',
                transition: '进入谷底但即将迎来黎明。'
            },
            scenesEn: {
                career: 'The end of a job; a business failing completely; being backstabbed at work.',
                relationship: 'A brutal breakup; the final realization that a relationship is dead.',
                wealth: 'Total financial loss; reaching the limit of a financial crisis.',
                health: 'Total exhaustion; a major health breakdown; the turning point of a crisis.',
                universal: 'Let the old situation die; there is nothing more to be done.',
                transition: 'A final shift into the darkest hour before the dawn.'
            }
        },
        reversed: {
            keywords: ['恢复', '重生', '最坏已过', '抵抗结束'],
            keywordsEn: ['Recovery', 'Survival', 'Resurrection', 'Pulling Oneself Together', 'Avoiding Ruin'],
            imageDescription: '剑开始脱落，人开始恢复。',
            imageDescriptionEn: 'The swords fall out or the person begins to stir; the sun on the horizon is now higher in the sky.',
            generalMeaning: '最坏的时期已经过去，开始恢复。',
            generalMeaningEn: 'You have survived the disaster. You are beginning to pull yourself up from the ground. The wounds are still there, but you are no longer dying from them.',
            scenes: {
                career: '事业开始复苏。',
                relationship: '准备新的开始。',
                wealth: '财务开始好转。',
                health: '健康开始恢复。',
                universal: '当下最坏已过，开始重建。',
                transition: '从谷底转向恢复的正向转折。'
            },
            scenesEn: {
                career: 'Bouncing back after a failure; finding a new job after being fired; recovery from scandal.',
                relationship: 'Healing after a devastating breakup; deciding not to be a victim anymore.',
                wealth: 'Slowly paying off debt; recovering from a bankruptcy.',
                health: 'A near-death experience leading to a new appreciation for life; slow recovery.',
                universal: 'You are a survivor; use this new beginning wisely.',
                transition: 'A positive shift from total destruction into slow rebuilding.'
            }
        }
    },

    // 061 - Page of Swords 宝剑侍从
    '061': {
        id: '061',
        name: 'Page of Swords',
        nameCn: '宝剑侍从',
        introduction: '宝剑侍从象征好奇与警觉，年轻人持剑警惕地观察，代表好学、机敏与新想法。',
        introductionEn: 'The Page of Swords is the intellectual apprentice. He represents curiosity, mental agility, and the excitement of a new idea or piece of information.',
        upright: {
            keywords: ['好奇', '警觉', '机敏', '新想法', '学习', '消息'],
            keywordsEn: ['Curiosity', 'Mental Agility', 'Alertness', 'New Ideas', 'Messages', 'Sharp Wit'],
            imageDescription: '年轻人持剑警惕地站在风中。',
            imageDescriptionEn: 'A youth stands on a rugged hilltop, holding a sword with both hands. He looks over his shoulder as if expecting a challenge; the wind blows his hair and the clouds behind him.',
            generalMeaning: '保持警觉和好奇心，有新的想法或消息。',
            generalMeaningEn: 'A time to be mentally sharp. You may receive news or have an idea that requires further research. Be curious and energetic, but stay objective.',
            scenes: {
                career: '学习新技能或收到消息。',
                relationship: '保持沟通警觉。',
                wealth: '财务上保持机敏。',
                health: '注意健康信号。',
                universal: '当下保持警觉和好奇心。',
                transition: '进入警觉学习的阶段。'
            },
            scenesEn: {
                career: 'Starting a new course; office gossip; a sharp new intern; receiving a job offer via email.',
                relationship: 'Observing a partner carefully; intellectual attraction; clear but possibly blunt communication.',
                wealth: 'Researching financial plans; receiving news about a bank or contract.',
                health: 'Learning about health; nervous energy; a need to be more active.',
                universal: 'Stay sharp and ask questions; the truth is in the details.',
                transition: 'A shift into a period of mental alertness and exploration.'
            }
        },
        reversed: {
            keywords: ['八卦', '尖酸', '谣言', '欺骗', '冲动'],
            keywordsEn: ['Gossip', 'Sarcasm', 'Deception', 'All Talk No Action', 'Paranoia', 'Bad News'],
            imageDescription: '侍从的警觉变成多疑或尖酸。',
            imageDescriptionEn: 'The Page looks unsteady; his sword is held awkwardly or is pointing at his own feet.',
            generalMeaning: '智力被用于负面目的。',
            generalMeaningEn: 'Watch your tongue. You may be gossiping, being too critical, or acting like a "know-it-all" without having the actual facts.',
            scenes: {
                career: '工作中有八卦或谣言。',
                relationship: '感情中言语伤人。',
                wealth: '财务信息不准确。',
                health: '过度担忧健康。',
                universal: '当下需注意言行，避免伤人。',
                transition: '从机敏转向尖酸的消极转折。'
            },
            scenesEn: {
                career: 'Office rumors causing trouble; someone stealing ideas; intellectual arrogance.',
                relationship: 'Spying on a partner; cutting remarks; breaking a promise.',
                wealth: 'Misleading financial news; being tricked by fine print.',
                health: 'Hypochondria; nervous exhaustion; ignoring advice.',
                universal: 'Stop talking and start listening; you don\'t know the whole story.',
                transition: 'A shift from curiosity to defensive cynicism.'
            }
        }
    },

    // 062 - Knight of Swords 宝剑骑士
    '062': {
        id: '062',
        name: 'Knight of Swords',
        nameCn: '宝剑骑士',
        introduction: '宝剑骑士象征行动与冲劲，骑士骑马冲锋，代表快速行动、果断与雄心。',
        introductionEn: 'The Knight of Swords is the warrior of the mind. He represents swift action, intellectual ambition, and charging forward to speak the truth with zero hesitation.',
        upright: {
            keywords: ['行动', '冲劲', '果断', '雄心', '直接', '勇敢'],
            keywordsEn: ['Swift Action', 'Determined', 'Direct', 'Logical', 'Ambition', 'Bravery'],
            imageDescription: '骑士骑马冲锋，剑举向前。',
            imageDescriptionEn: 'A knight on a charging white horse gallops forward. He holds his sword high, his cape streaming behind him in the wind as he rushes into battle.',
            generalMeaning: '快速果断地采取行动。',
            generalMeaningEn: 'Full speed ahead! You are focused on your goal and ready to push through any obstacle using logic and sheer force of will. Don\'t let anyone slow you down.',
            scenes: {
                career: '事业上果断行动。',
                relationship: '直接表达感情。',
                wealth: '快速做出财务决定。',
                health: '积极应对健康问题。',
                universal: '当下需要果断行动。',
                transition: '进入快速行动的阶段。'
            },
            scenesEn: {
                career: 'Pushing through a difficult project; a fast-paced work environment; sharp professional communication.',
                relationship: 'Making a sudden romantic move; intense intellectual connection; being brutally honest.',
                wealth: 'Rapid financial growth through smart moves; decisive investment.',
                health: 'Fast recovery; acting quickly on medical advice.',
                universal: 'Charge toward your goal; momentum is on your side.',
                transition: 'A sudden burst of intellectual and physical speed.'
            }
        },
        reversed: {
            keywords: ['鲁莽', '冲动', '争论', '没有计划'],
            keywordsEn: ['Haste', 'Aggression', 'Tactlessness', 'Scattered Energy', 'Dangerous Speed'],
            imageDescription: '骑士冲锋失控或受阻。',
            imageDescriptionEn: 'The knight seems to be falling off or the horse is out of control; his sword is pointed dangerously in the wrong direction.',
            generalMeaning: '行动过于冲动或缺乏计划。',
            generalMeaningEn: 'You are moving too fast without a plan. Your bluntness is hurting people, or your "charge" is leading you straight into a wall. Slow down and think.',
            scenes: {
                career: '工作中过于激进。',
                relationship: '言语过激伤人。',
                wealth: '财务决定过于仓促。',
                health: '对健康问题处理过激。',
                universal: '当下需要三思后行。',
                transition: '从果断转向鲁莽的消极转折。'
            },
            scenesEn: {
                career: 'Arrogance at work; rushing a project into failure; being too blunt with a boss.',
                relationship: 'Aggressive arguments; hurtful honesty without compassion; a "rebound" romance.',
                wealth: 'Impulsive spending; taking huge risks without research.',
                health: 'Injuries due to haste; burnout from mental intensity.',
                universal: 'Speed is useless if you are going the wrong way.',
                transition: 'A shift from focused power to reckless aggression.'
            }
        }
    },

    // 063 - Queen of Swords 宝剑皇后
    '063': {
        id: '063',
        name: 'Queen of Swords',
        nameCn: '宝剑皇后',
        introduction: '宝剑皇后象征清晰与独立，皇后高举宝剑，代表理性、独立与清晰的判断。',
        introductionEn: 'The Queen of Swords is the master of intellectual clarity. She is independent, observant, and cuts through any nonsense with her sharp mind and honest words.',
        upright: {
            keywords: ['清晰', '独立', '理性', '公正', '智慧', '直言'],
            keywordsEn: ['Independence', 'Clarity', 'Directness', 'Perceptive', 'Boundary', 'Intellect'],
            imageDescription: '皇后端坐王座，一手高举宝剑。',
            imageDescriptionEn: 'The Queen sits on a throne decorated with a cherub (spirit). She holds a sword upright in one hand and beckons with the other. A single bird flies high in the clear sky.',
            generalMeaning: '以清晰理性的头脑做出判断。',
            generalMeaningEn: 'A time to be objective. You need to use your head over your heart right now. Set firm boundaries and speak the truth, even if it feels cold.',
            scenes: {
                career: '工作中展现智慧和公正。',
                relationship: '独立理性看待感情。',
                wealth: '理性管理财务。',
                health: '理性对待健康问题。',
                universal: '当下以清晰头脑行事。',
                transition: '进入理性清晰的阶段。'
            },
            scenesEn: {
                career: 'Expert consulting; setting professional boundaries; a sharp, respected female leader.',
                relationship: 'Value of personal space; an honest but cool partnership; prioritizing self-reliance.',
                wealth: 'Strict financial logic; unbiased financial advice.',
                health: 'Scientific approach to wellness; mental health through setting boundaries.',
                universal: 'Be honest with yourself; cut away the illusions.',
                transition: 'A shift from emotional fog to sharp mental clarity.'
            }
        },
        reversed: {
            keywords: ['冷酷', '刻薄', '偏见', '情绪化'],
            keywordsEn: ['Cruelty', 'Bitter', 'Critical', 'Manipulative', 'Malicious Gossip'],
            imageDescription: '皇后的理性变成冷漠或刻薄。',
            imageDescriptionEn: 'The Queen looks severe or turning away; the bird is falling or absent, symbolizing a loss of higher perspective.',
            generalMeaning: '过于冷酷或陷入情绪化。',
            generalMeaningEn: 'Your intellect has turned into bitterness. You may be overly critical of yourself or others, or you are using your intelligence to manipulate people.',
            scenes: {
                career: '工作中过于苛刻。',
                relationship: '感情中冷漠或刻薄。',
                wealth: '财务判断失误。',
                health: '情绪影响健康。',
                universal: '当下需平衡理性与情感。',
                transition: '从清晰转向冷酷的消极转折。'
            },
            scenesEn: {
                career: 'A toxic boss; harsh workplace criticism; being too blunt and hurting morale.',
                relationship: 'Being the "ice queen/king"; using logic to punish a partner; bitterness from past hurt.',
                wealth: 'Being overly stingy or judgmental about others\' spending.',
                health: 'Hypocrisy in health; mental bitterness causing physical stiffness.',
                universal: 'Let a little compassion into your cold logic.',
                transition: 'A shift from sharp clarity to destructive coldness.'
            }
        }
    },

    // 064 - King of Swords 宝剑国王
    '064': {
        id: '064',
        name: 'King of Swords',
        nameCn: '宝剑国王',
        introduction: '宝剑国王象征权威与真理，国王端坐持剑，代表智慧、权威与公正的判断。',
        introductionEn: 'The King of Swords represents the peak of intellectual authority. He is the ultimate judge, lawyer, or advisor who uses law, logic, and truth to govern his world.',
        upright: {
            keywords: ['权威', '真理', '智慧', '公正', '领导', '纪律'],
            keywordsEn: ['Authority', 'Logical', 'Truthful', 'Analytical', 'Fair', 'Intellectual'],
            imageDescription: '国王端坐王座，垂直持剑。',
            imageDescriptionEn: 'The King sits on a throne decorated with butterflies (transformation) and cherubs. He holds a sword upright, tilted slightly away, looking forward with total focus.',
            generalMeaning: '以智慧和公正行使权威。',
            generalMeaningEn: 'You are in a position of power that requires absolute objectivity. Stick to the facts, follow the rules, and use your mind to lead. Seek expert advice if needed.',
            scenes: {
                career: '展现领导智慧。',
                relationship: '以理性处理感情。',
                wealth: '明智的财务决策。',
                health: '理性管理健康。',
                universal: '当下以智慧和公正行事。',
                transition: '进入智慧领导的阶段。'
            },
            scenesEn: {
                career: 'Legal professional; high-level strategic planning; a fair but firm manager.',
                relationship: 'Intellectual bonding; a partner who values truth over drama; clear relationship rules.',
                wealth: 'Professional financial advisor; using law and contracts to protect wealth.',
                health: 'Specialist medical advice; a disciplined, evidence-based health plan.',
                universal: 'Let logic be your guide; do not be swayed by emotion.',
                transition: 'A shift into a period of high intellectual authority.'
            }
        },
        reversed: {
            keywords: ['专制', '冷酷', '不公正', '滥用权力'],
            keywordsEn: ['Tyrant', 'Manipulative', 'Abuse of Intellect', 'Cold-hearted', 'Corrupt'],
            imageDescription: '国王的权威变成专制。',
            imageDescriptionEn: 'The King grips his sword like a weapon; he looks away from justice, symbolizing a biased or cruel judgment.',
            generalMeaning: '滥用智力或权力。',
            generalMeaningEn: 'An abuse of power. You may be acting like a tyrant, using your superior intelligence to make others feel small or to justify cruel actions.',
            scenes: {
                career: '领导方式过于专制。',
                relationship: '感情中过于控制。',
                wealth: '财务决策不公正。',
                health: '忽视情感健康。',
                universal: '当下需反思权力的使用。',
                transition: '从权威转向专制的消极转折。'
            },
            scenesEn: {
                career: 'A manipulative leader; unethical professional behavior; "all brain, no heart" management.',
                relationship: 'Mental bullying; emotional detachment used as a weapon; controlling a partner with logic.',
                wealth: 'Financial corruption; using cleverness to cheat others.',
                health: 'Mental exhaustion; ignoring the mind-body connection.',
                universal: 'Power without compassion is merely a cage.',
                transition: 'A negative shift from fair leadership to cruel tyranny.'
            }
        }
    },

    // ========== Minor Arcana - Pentacles (星币) ==========

    // 065 - Ace of Pentacles 星币王牌
    '065': {
        id: '065',
        name: 'Ace of Pentacles',
        nameCn: '星币王牌',
        introduction: '星币王牌象征物质的开始，一只手从云中伸出托着金币，代表财富、机会与物质基础。',
        introductionEn: 'The Ace of Pentacles represents the seed of prosperity in the material world. it signifies a new financial opportunity, a physical manifestion of goals, and the solid foundation for future wealth.',
        upright: {
            keywords: ['新机会', '财富', '物质', '基础', '繁荣', '开始'],
            keywordsEn: ['Prosperity', 'New Opportunity', 'Abundance', 'Manifestation', 'Financial Stability', 'Security'],
            imageDescription: '一只手从云中伸出托着一枚金币，下方是花园。',
            imageDescriptionEn: 'A divine hand emerges from the clouds holding a large gold coin. Below, a lush garden with a pathway leads through an archway of flowers, symbolizing earthly paradise.',
            generalMeaning: '物质或财务上有新的机会和开始。',
            generalMeaningEn: 'A gift or opportunity in the physical realm. It marks a period of manifesting your goals into reality and the beginning of a prosperous venture.',
            scenes: {
                career: '新的事业机会或工作。',
                relationship: '感情有物质基础。',
                wealth: '财务上有新机会。',
                health: '身体健康有好的开始。',
                universal: '当下是抓住物质机会的时刻。',
                transition: '进入物质繁荣的新阶段。'
            },
            scenesEn: {
                career: 'A new job offer; a profitable business contract; the start of a productive project.',
                relationship: 'A stable and secure relationship; sharing financial goals with a partner.',
                wealth: 'An unexpected windfall; a sound investment opportunity; physical abundance.',
                health: 'Starting a new health regime; peak physical vitality; body awareness.',
                universal: 'Plant the seeds of your future now; wealth is within reach.',
                transition: 'A grounded shift from intention to physical manifestation.'
            }
        },
        reversed: {
            keywords: ['错失机会', '财务问题', '贪婪', '不稳定'],
            keywordsEn: ['Financial Loss', 'Missed Opportunity', 'Lack of Planning', 'Greed', 'Insecurity'],
            imageDescription: '金币从手中滑落或失去光泽。',
            imageDescriptionEn: 'The hand drops the coin or the garden below is withered, suggesting a missed chance at stability or poor financial flow.',
            generalMeaning: '物质机会错失或财务不稳定。',
            generalMeaningEn: 'A warning of financial instability or a missed opportunity. You may be focusing too much on money or failing to plan for the long term.',
            scenes: {
                career: '工作机会流失。',
                relationship: '物质问题影响感情。',
                wealth: '财务出现问题。',
                health: '健康基础不稳。',
                universal: '当下需注意物质方面的问题。',
                transition: '从机会转向流失的消极转折。'
            },
            scenesEn: {
                career: 'A failed business deal; job insecurity; lack of professional growth.',
                relationship: 'Money arguments in a relationship; feeling insecure about the future.',
                wealth: 'Unexpected expenses; poor financial management; greed leading to loss.',
                health: 'Neglecting physical health; poor diet; lack of groundedness.',
                universal: 'Check your foundations; don\'t chase hollow material gains.',
                transition: 'A negative shift from potential prosperity to instability.'
            }
        }
    },

    // 066 - Two of Pentacles 星币二
    '066': {
        id: '066',
        name: 'Two of Pentacles',
        nameCn: '星币二',
        introduction: '星币二象征平衡与调整，一人在波涛间玩杂耍般平衡两枚金币，代表适应与灵活。',
        introductionEn: 'The Two of Pentacles represents the dance of daily life. It signifies the need to balance multiple responsibilities, remain flexible, and manage resources during times of change.',
        upright: {
            keywords: ['平衡', '调整', '适应', '灵活', '优先级', '管理'],
            keywordsEn: ['Balance', 'Adaptability', 'Prioritization', 'Flexibility', 'Multi-tasking', 'Time Management'],
            imageDescription: '年轻人在海边玩杂耍般平衡两枚金币。',
            imageDescriptionEn: 'A young man juggles two large coins encircled by an infinity symbol. In the background, ships ride high waves, symbolizing the ups and downs of life.',
            generalMeaning: '需要在多个事务间保持平衡。',
            generalMeaningEn: 'You are managing a lot right now. Success depends on your ability to stay adaptable and keep your priorities in order as circumstances shift.',
            scenes: {
                career: '工作中需要多任务处理。',
                relationship: '感情与其他事务需要平衡。',
                wealth: '财务需要灵活管理。',
                health: '生活各方面需要平衡。',
                universal: '当下需要灵活应对多重任务。',
                transition: '进入需要平衡的阶段。'
            },
            scenesEn: {
                career: 'Managing two jobs or projects; multitasking at work; adapting to a new schedule.',
                relationship: 'Balancing work and love; trying to make time for a partner amidst a busy life.',
                wealth: 'Balancing the budget; moving money around to cover expenses; small fluctuations.',
                health: 'Finding a balance between diet and exercise; managing stress through flexibility.',
                universal: 'Keep things moving; adaptability is your greatest strength right now.',
                transition: 'A shift into a period of active balancing and adjustment.'
            }
        },
        reversed: {
            keywords: ['失衡', '不堪重负', '混乱', '无法应对'],
            keywordsEn: ['Imbalance', 'Overwhelmed', 'Disorganized', 'Financial Mess', 'Rigidity'],
            imageDescription: '金币掉落，无法保持平衡。',
            imageDescriptionEn: 'The coins are dropped; the juggler looks panicked, suggesting that the "balls" have been dropped and things are falling apart.',
            generalMeaning: '无法保持平衡，感到不堪重负。',
            generalMeaningEn: 'You are taking on too much. The lack of organization is leading to stress and potential failure. It is time to simplify your life.',
            scenes: {
                career: '工作超负荷。',
                relationship: '感情被其他事务影响。',
                wealth: '财务管理混乱。',
                health: '生活失衡影响健康。',
                universal: '当下需要重新调整优先级。',
                transition: '从平衡转向混乱的消极转折。'
            },
            scenesEn: {
                career: 'Dropping the ball at work; poor time management; being overwhelmed by the workload.',
                relationship: 'Neglecting a relationship due to overworking; emotional exhaustion.',
                wealth: 'Messy financial accounts; overspending; inability to balance the books.',
                health: 'Burnout from trying to do too much; lack of a stable routine.',
                universal: 'Stop juggling; you need to focus on one thing at a time.',
                transition: 'A negative shift from flexibility to total overwhelm.'
            }
        }
    },

    // 067 - Three of Pentacles 星币三
    '067': {
        id: '067',
        name: 'Three of Pentacles',
        nameCn: '星币三',
        introduction: '星币三象征合作与技艺，工匠在教堂工作，代表团队合作与专业技能。',
        introductionEn: 'The Three of Pentacles represents teamwork, collaboration, and apprenticeship. It emphasizes that great things are achieved when people combine their diverse skills for a common goal.',
        upright: {
            keywords: ['合作', '技艺', '团队', '学习', '专业', '计划'],
            keywordsEn: ['Teamwork', 'Collaboration', 'Mastery', 'Skill', 'Planning', 'Recognition'],
            imageDescription: '工匠在教堂雕刻，与他人讨论设计图。',
            imageDescriptionEn: 'A young stonemason works in a cathedral. A priest and an architect stand by, holding plans and consulting him, showing mutual respect for their different skills.',
            generalMeaning: '团队合作发挥专业技能。',
            generalMeaningEn: 'Success through cooperation. This is a time to work with others, seek expert advice, and refine your craft. Your skills are being recognized.',
            scenes: {
                career: '团队合作项目进展顺利。',
                relationship: '共同建设感情。',
                wealth: '合作带来财务收益。',
                health: '专业健康建议有效。',
                universal: '当下适合团队合作。',
                transition: '进入合作发展的阶段。'
            },
            scenesEn: {
                career: 'Successful collaboration on a project; being a valued team member; building professional reputation.',
                relationship: 'Building a future together; working on relationship goals as a team.',
                wealth: 'Financial gain through collective effort; professional advice leading to profit.',
                health: 'Working with a coach or doctor; structured health improvements.',
                universal: 'Collaborate with others; your contribution is essential to the whole.',
                transition: 'A positive shift from solo work to professional synergy.'
            }
        },
        reversed: {
            keywords: ['合作问题', '缺乏技能', '冲突', '质量问题'],
            keywordsEn: ['Poor Teamwork', 'Lack of Skill', 'Disharmony', 'No Planning', 'Disregard'],
            imageDescription: '工匠与他人意见不合或工作质量下降。',
            imageDescriptionEn: 'The mason works alone or the plans are ignored; the cathedral looks unfinished or poorly built, symbolizing lack of cohesion.',
            generalMeaning: '合作出现问题或技能不足。',
            generalMeaningEn: 'Indications of friction within a group. A lack of planning or poor communication is hindering the project\'s quality and progress.',
            scenes: {
                career: '团队合作出现问题。',
                relationship: '共建感情遇到困难。',
                wealth: '合作导致财务问题。',
                health: '健康建议不合适。',
                universal: '当下需改善合作方式。',
                transition: '从合作转向冲突的消极转折。'
            },
            scenesEn: {
                career: 'Office politics; lack of motivation; a team working at cross-purposes; shoddy work.',
                relationship: 'Failing to plan for the future together; lack of mutual effort in a bond.',
                wealth: 'Financial loss due to poor advice; lack of professional oversight.',
                health: 'Inconsistent health routines; ignoring expert medical guidance.',
                universal: 'Fix the foundation; you cannot build success without unity.',
                transition: 'A negative shift from cooperation to professional discord.'
            }
        }
    },

    // 068 - Four of Pentacles 星币四
    '068': {
        id: '068',
        name: 'Four of Pentacles',
        nameCn: '星币四',
        introduction: '星币四象征守财与安全，一人紧抱金币，代表控制、安全感与吝啬。',
        introductionEn: 'The Four of Pentacles represents a desire for control and security. While it shows stability and savings, it also warns against greed, possessiveness, and the fear of loss.',
        upright: {
            keywords: ['安全', '保守', '控制', '储蓄', '稳定', '边界'],
            keywordsEn: ['Security', 'Conservatism', 'Control', 'Saving', 'Hoarding', 'Possessiveness'],
            imageDescription: '一人紧紧抱着四枚金币，守护着不放。',
            imageDescriptionEn: 'A man sits on a stone stool, clutching a coin to his chest, with two coins under his feet and one on his crown, showing a total "lock" on his wealth.',
            generalMeaning: '注重物质安全，可能过于保守。',
            generalMeaningEn: 'You are focused on protecting what you have. While financial stability is good, be careful that your need for security doesn\'t turn into emotional or material rigidity.',
            scenes: {
                career: '工作中追求稳定安全。',
                relationship: '感情中控制欲强。',
                wealth: '财务保守储蓄。',
                health: '重视身体安全。',
                universal: '当下可能过于追求安全。',
                transition: '进入保守稳定的阶段。'
            },
            scenesEn: {
                career: 'Clinging to a safe job; protective over ideas; playing it safe professionally.',
                relationship: 'Possessiveness in love; holding back emotions; fear of a partner leaving.',
                wealth: 'Strict budgeting; hoarding cash; being very careful with investments.',
                health: 'Rigid health routines; focusing on physical safety; potentially blocked energy.',
                universal: 'Security is good, but don\'t let it become a prison.',
                transition: 'A shift from growth to protective stability.'
            }
        },
        reversed: {
            keywords: ['放手', '开放', '挥霍', '不安全感'],
            keywordsEn: ['Letting Go', 'Generosity', 'Overspending', 'Insecurity', 'Releasing Control'],
            imageDescription: '金币开始从手中滑落或主动放开。',
            imageDescriptionEn: 'The man lets go of the coin or the coins fall; he looks less stable, suggesting a release of control—either voluntary or forced.',
            generalMeaning: '开始放手或失去控制。',
            generalMeaningEn: 'A period of releasing old patterns. You may be loosening your grip on money or people, which could lead to either greater freedom or financial risk.',
            scenes: {
                career: '工作上不再紧抓。',
                relationship: '感情中放下控制。',
                wealth: '财务上开始花费。',
                health: '放松对健康的过度担忧。',
                universal: '当下需在保守与开放间找平衡。',
                transition: '从紧握转向放手的转折。'
            },
            scenesEn: {
                career: 'Delegating tasks; taking a risk at work; losing professional control.',
                relationship: 'Ending possessive behavior; a relationship opening up or falling apart.',
                wealth: 'Reckless spending; a sudden outflow of cash; becoming more generous.',
                health: 'Relaxing a rigid diet; physical release; unblocking stagnant energy.',
                universal: 'The energy of life must flow; you cannot hold onto anything forever.',
                transition: 'A shift from hoarding to either freedom or loss.'
            }
        }
    },

    // 069 - Five of Pentacles 星币五
    '069': {
        id: '069',
        name: 'Five of Pentacles',
        nameCn: '星币五',
        introduction: '星币五象征困难与贫困，代表物质困难、孤立以及在困境中寻求帮助。',
        introductionEn: 'The Five of Pentacles is the card of hardship and poverty. It represents a "dark night of the soul" regarding material resources, but reminds us that help is often nearby if we look up.',
        upright: {
            keywords: ['困难', '贫困', '孤立', '担忧', '不安全', '排斥'],
            keywordsEn: ['Hardship', 'Financial Loss', 'Isolation', 'Worry', 'Insecurity', 'Poverty Mindset'],
            imageDescription: '两个衣衫褴褛的人在雪中走过彩色玻璃窗。',
            imageDescriptionEn: 'Two shivering, injured beggars walk through the snow past a glowing stained-glass church window. They are so focused on their pain they don\'t see the light inside.',
            generalMeaning: '经历物质困难或感到被排斥。',
            generalMeaningEn: 'A challenging time financially or emotionally. You may feel "left out in the cold," but the card encourages you to seek the sanctuary and support that is available.',
            scenes: {
                career: '工作不顺或失业。',
                relationship: '感情中感到孤立。',
                wealth: '财务困难。',
                health: '健康出现问题。',
                universal: '当下虽困难，但帮助就在身边。',
                transition: '进入困难的低谷阶段。'
            },
            scenesEn: {
                career: 'Job loss; career setbacks; feeling unsupported by colleagues; business struggles.',
                relationship: 'Feeling lonely within a relationship; abandonment; joint financial stress.',
                wealth: 'Debt; poverty; focusing on what you lack; significant financial loss.',
                health: 'Physical illness; lack of energy; struggle to afford medical care.',
                universal: 'Don\'t suffer in silence; look for the light of help around you.',
                transition: 'A painful shift into a period of lack and struggle.'
            }
        },
        reversed: {
            keywords: ['恢复', '接受帮助', '改善', '希望'],
            keywordsEn: ['Recovery', 'Improving Finances', 'End of Hardship', 'Renewed Hope', 'Sanctuary'],
            imageDescription: '两人看到教堂的门或得到帮助。',
            imageDescriptionEn: 'The beggars turn toward the church door; the snow starts to melt, symbolizing the end of a long winter of the soul.',
            generalMeaning: '开始走出困境，接受帮助。',
            generalMeaningEn: 'Recovery is on the horizon. The period of lack is ending, and you are beginning to rebuild your stability and find your way back into the warmth.',
            scenes: {
                career: '工作情况改善。',
                relationship: '感情中得到支持。',
                wealth: '财务开始好转。',
                health: '健康恢复。',
                universal: '当下开始走出困境。',
                transition: '从困难转向恢复的正向转折。'
            },
            scenesEn: {
                career: 'Finding a new job; overcoming professional obstacles; stability returning.',
                relationship: 'Rebuilding a bond after a crisis; finding emotional support again.',
                wealth: 'Debts being paid off; financial flow returning; end of poverty.',
                health: 'Slow recovery from an illness; starting to feel physically stronger.',
                universal: 'The worst is behind you; accept the help being offered.',
                transition: 'A positive shift from lack back toward security.'
            }
        }
    },

    // 070 - Six of Pentacles 星币六
    '070': {
        id: '070',
        name: 'Six of Pentacles',
        nameCn: '星币六',
        introduction: '星币六象征给予与接受，代表慷慨、慈善、公平交换以及在财务上的平衡。',
        introductionEn: 'The Six of Pentacles is the card of generosity and charity. it represents the healthy flow of resources, reminding us of the balance between giving and receiving.',
        upright: {
            keywords: ['给予', '接受', '慷慨', '公平', '慈善', '分享'],
            keywordsEn: ['Generosity', 'Charity', 'Giving and Receiving', 'Fairness', 'Gratitude', 'Sharing Wealth'],
            imageDescription: '商人一手持天平，一手向乞丐施舍。',
            imageDescriptionEn: 'A wealthy merchant holds a balanced scale in one hand and drops coins into the hands of two kneeling beggars with the other.',
            generalMeaning: '给予或接受帮助，公平的交换。',
            generalMeaningEn: 'A time of financial or emotional balance. You may be in a position to help others, or you are receiving the exact support you need to succeed.',
            scenes: {
                career: '工作中得到认可或帮助他人。',
                relationship: '感情中相互付出。',
                wealth: '财务上的给予或接受。',
                health: '得到健康方面的帮助。',
                universal: '当下是给予或接受的时刻。',
                transition: '进入慷慨分享的阶段。'
            },
            scenesEn: {
                career: 'A generous mentor; receiving a bonus or raise; sharing professional knowledge.',
                relationship: 'A relationship where both parties give equally; supportive partners.',
                wealth: 'Giving to charity; receiving a loan or grant; financial harmony.',
                health: 'Finding the right doctor or therapy; health support from others.',
                universal: 'Balance the scales of your life; what you give returns to you.',
                transition: 'A positive shift into a state of balanced sharing.'
            }
        },
        reversed: {
            keywords: ['不公平', '有条件的给予', '债务', '自私'],
            keywordsEn: ['Selfishness', 'Unfairness', 'Debt', 'Strings Attached', 'Financial Abuse'],
            imageDescription: '天平失衡或施舍有附加条件。',
            imageDescriptionEn: 'The scales are tipped; the merchant looks arrogant or the beggars are rejected, suggesting an imbalance of power.',
            generalMeaning: '交换不公平或给予有条件。',
            generalMeaningEn: 'A warning of greed or an unfair power dynamic. One party may be taking too much, or help is being offered with harmful strings attached.',
            scenes: {
                career: '工作中不公平对待。',
                relationship: '感情中付出不均。',
                wealth: '财务上有债务问题。',
                health: '帮助不是无偿的。',
                universal: '当下需注意交换的公平性。',
                transition: '从慷慨转向不公的消极转折。'
            },
            scenesEn: {
                career: 'Unfair wages; an ego-driven boss; lack of recognition for hard work.',
                relationship: 'A one-sided partnership; using money to control a lover; resentment.',
                wealth: 'Bad debt; selfish financial behavior; refusing to help when able.',
                health: 'Ineffective or overpriced health advice; feeling drained by others.',
                universal: 'Beware of "gifts" that cost your freedom; check for hidden motives.',
                transition: 'A negative shift from generosity to exploitation.'
            }
        }
    },

    // 071 - Seven of Pentacles 星币七
    '071': {
        id: '071',
        name: 'Seven of Pentacles',
        nameCn: '星币七',
        introduction: '星币七象征评估与耐心，代表在收获前对成果的审视与长期的耕耘。',
        introductionEn: 'The Seven of Pentacles is the card of patience and assessment. It represents a pause in labor to evaluate your progress and decide how to invest your energy for the final harvest.',
        upright: {
            keywords: ['评估', '耐心', '投资', '长期', '反思', '收获在望'],
            keywordsEn: ['Patience', 'Investment', 'Assessment', 'Long-term Goals', 'Hard Work Paying Off', 'Diligence'],
            imageDescription: '农夫倚锄看着藤蔓上的金币，代表评估成果与长期投资。',
            imageDescriptionEn: 'A gardener leans on his tool, looking at his crop of seven coins blooming on a bush. He is not picking them yet; he is waiting for the perfect moment.',
            generalMeaning: '评估已有成果，等待长期投资的回报。',
            generalMeaningEn: 'Your hard work is growing, but it is not yet time to harvest. Be patient, take a breath, and evaluate your long-term strategy to ensure the best results.',
            scenes: {
                career: '事业评估进展。',
                relationship: '反思感情付出。',
                wealth: '评估投资回报。',
                health: '评估健康计划效果。',
                universal: '当下是评估和等待的时期。',
                transition: '进入评估反思的阶段。'
            },
            scenesEn: {
                career: 'Reviewing a business project; waiting for a promotion; taking stock of career progress.',
                relationship: 'Considering the long-term potential of a bond; working slowly on a relationship.',
                wealth: 'Waiting for investments to mature; financial planning for the future.',
                health: 'Waiting for results from a health change; steady but slow physical improvement.',
                universal: 'Don\'t rush the process; the best fruits take time to ripen.',
                transition: 'A thoughtful shift from active labor to patient observation.'
            }
        },
        reversed: {
            keywords: ['无回报', '急躁', '缺乏远见', '浪费'],
            keywordsEn: ['Impatience', 'Lack of Reward', 'Poor Planning', 'Waste', 'Frustrated Effort'],
            imageDescription: '藤蔓上的金币减少或农夫放弃。',
            imageDescriptionEn: 'The bush is empty or the fruit is rotten; the gardener looks frustrated or gives up, symbolizing lost effort.',
            generalMeaning: '投入没有回报或缺乏耐心。',
            generalMeaningEn: 'You may be feeling frustrated with the slow pace of progress. There is a risk of giving up too soon or realizing that you have invested your time in the wrong things.',
            scenes: {
                career: '事业投入没有回报。',
                relationship: '感情付出未被珍惜。',
                wealth: '投资回报不佳。',
                health: '健康计划效果不佳。',
                universal: '当下需反思策略或坚持耐心。',
                transition: '从等待转向失望的消极转折。'
            },
            scenesEn: {
                career: 'Burnout from lack of results; a project with no ROI; quitting a job too early.',
                relationship: 'Feeling that a relationship is not worth the effort; impatience with a partner.',
                wealth: 'Financial loss; bad investments; lack of long-term savings.',
                health: 'Inconsistent health results leading to frustration; lack of discipline.',
                universal: 'Re-evaluate where you put your energy; don\'t throw good money after bad.',
                transition: 'A negative shift from patience to wasted effort.'
            }
        }
    },

    // 072 - Eight of Pentacles 星币八
    '072': {
        id: '072',
        name: 'Eight of Pentacles',
        nameCn: '星币八',
        introduction: '星币八象征勤勉与技艺，代表对工作的专注、学习与对完美的追求。',
        introductionEn: 'The Eight of Pentacles represents craftsmanship and apprenticeship. It is the card of the "diligent student," focusing on the repetitive work required to master a skill.',
        upright: {
            keywords: ['勤勉', '技艺', '学习', '专注', '精进', '努力'],
            keywordsEn: ['Craftsmanship', 'Diligence', 'Knowledge', 'Focus', 'Hard Work', 'Skill Development'],
            imageDescription: '工匠专心雕刻金币，周围是已完成的作品。',
            imageDescriptionEn: 'A stonemason sits at a bench, meticulously carving a coin. Behind him, seven completed coins hang on a wall, showing his steady progress.',
            generalMeaning: '专注于技能提升，勤勉工作。',
            generalMeaningEn: 'A period of intense focus and learning. You are refining your talents and building your reputation through consistent, high-quality work.',
            scenes: {
                career: '专注于技能提升。',
                relationship: '努力经营感情。',
                wealth: '勤勉积累财富。',
                health: '认真管理健康。',
                universal: '当下是勤勉学习的时期。',
                transition: '进入专注精进的阶段。'
            },
            scenesEn: {
                career: 'Mastering a new skill; taking a professional course; high productivity; attention to detail.',
                relationship: 'Putting real effort into a relationship; working on communication; stable bonding.',
                wealth: 'Steady income through hard work; long-term financial discipline.',
                health: 'Consistent exercise and diet; dedication to physical improvement.',
                universal: 'Master your craft; your dedication is the key to your future success.',
                transition: 'A positive shift from learning to focused mastery.'
            }
        },
        reversed: {
            keywords: ['懈怠', '低质量', '捷径', '缺乏专注'],
            keywordsEn: ['Laziness', 'Lack of Ambition', 'Unskilled', 'Poor Workmanship', 'Shortcuts'],
            imageDescription: '工匠放下工具或作品质量下降。',
            imageDescriptionEn: 'The mason is distracted or his work is sloppy; the coins are crooked or falling, symbolizing a lack of commitment.',
            generalMeaning: '缺乏专注或走捷径。',
            generalMeaningEn: 'A warning against laziness or cutting corners. You may be bored with your routine or lacking the ambition needed to truly succeed.',
            scenes: {
                career: '工作懈怠。',
                relationship: '感情不够用心。',
                wealth: '财务管理马虎。',
                health: '健康管理不认真。',
                universal: '当下需重新专注。',
                transition: '从勤勉转向懈怠的消极转折。'
            },
            scenesEn: {
                career: 'Sloppy work; boredom at the office; missing a promotion due to lack of effort.',
                relationship: 'Taking a partner for granted; neglecting the "work" needed in love.',
                wealth: 'Financial neglect; unstable income due to lack of focus.',
                health: 'Quitting a fitness plan; lack of care for the body; stagnation.',
                universal: 'Do it right or don\'t do it at all; shortcuts will cost you later.',
                transition: 'A negative shift from diligence to mediocrity.'
            }
        }
    },

    // 073 - Nine of Pentacles 星币九
    '073': {
        id: '073',
        name: 'Nine of Pentacles',
        nameCn: '星币九',
        introduction: '星币九象征独立、优雅与丰盛，代表通过个人努力获得的物质成就。',
        introductionEn: 'The Nine of Pentacles represents self-sufficiency, independence, and luxury. It signifies the enjoyment of a comfortable life that has been earned through discipline and hard work.',
        upright: {
            keywords: ['独立', '丰盛', '成就', '自足', '优雅', '享受'],
            keywordsEn: ['Independence', 'Abundance', 'Self-Reliance', 'Luxury', 'Success', 'Grace'],
            imageDescription: '优雅的女子站在丰收的葡萄园中，手托猎鹰。',
            imageDescriptionEn: 'A wealthy, elegant woman stands in a lush garden filled with grapes and gold coins. A hooded falcon sits on her hand, representing her mastered intellect and self-control.',
            generalMeaning: '享受独立奋斗的成果。',
            generalMeaningEn: 'A time of high status and financial freedom. You have reached a point where you can rely on yourself and enjoy the finer things in life with peace and dignity.',
            scenes: {
                career: '事业成就独立。',
                relationship: '独立自主的感情状态。',
                wealth: '财务独立丰盛。',
                health: '享受健康生活。',
                universal: '当下享受努力的成果。',
                transition: '进入独立丰盛的阶段。'
            },
            scenesEn: {
                career: 'Successful entrepreneurship; reaching a high professional level; working independently.',
                relationship: 'Being happy alone or in an independent partnership; appreciating your own worth.',
                wealth: 'Financial independence; enjoying luxury and comfort; property success.',
                health: 'Radiant health; refined self-care; enjoying outdoor activities and nature.',
                universal: 'You are whole and abundant on your own; enjoy your freedom.',
                transition: 'A positive shift from labor to comfortable independence.'
            }
        },
        reversed: {
            keywords: ['依赖', '损失', '挥霍', '过度工作'],
            keywordsEn: ['Over-Investment', 'Insecurity', 'Dependence', 'Financial Setback', 'Hollow Success'],
            imageDescription: '葡萄园衰败或女子失去优雅。',
            imageDescriptionEn: 'The garden looks overgrown or empty; the woman looks anxious or the falcon is gone, suggesting a loss of self-reliance.',
            generalMeaning: '失去独立或成就受损。',
            generalMeaningEn: 'A warning of becoming too dependent on others or the fear that your security is unstable. You may be overspending to maintain an image of success.',
            scenes: {
                career: '事业独立受损。',
                relationship: '感情上过于依赖。',
                wealth: '财务受损。',
                health: '过度工作影响健康。',
                universal: '当下需保护已有成就。',
                transition: '从丰盛转向损失的消极转折。'
            },
            scenesEn: {
                career: 'Losing professional independence; failure of a solo venture; workaholism.',
                relationship: 'Codependency; feeling insecure without a partner; shallow romantic goals.',
                wealth: 'Living beyond your means; financial loss; insecurity about wealth.',
                health: 'Health issues from over-indulgence or neglect; physical insecurity.',
                universal: 'Re-evaluate your relationship with wealth and self-worth.',
                transition: 'A negative shift from abundance to instability.'
            }
        }
    },

    // 074 - Ten of Pentacles 星币十
    '074': {
        id: '074',
        name: 'Ten of Pentacles',
        nameCn: '星币十',
        introduction: '星币十象征传承、家族与物质世界的终极成就。',
        introductionEn: 'The Ten of Pentacles represents the peak of financial security and family legacy. It is about lasting success, traditional values, and the culmination of a life\'s work.',
        upright: {
            keywords: ['传承', '完满', '家族', '财富', '稳定', '遗产'],
            keywordsEn: ['Wealth', 'Family', 'Legacy', 'Inheritance', 'Tradition', 'Stability'],
            imageDescription: '三代同堂在拱门下，十枚金币组成生命之树图案。',
            imageDescriptionEn: 'An elderly man sits with his dogs under an archway. A couple and a child are nearby. Ten coins are arranged in the pattern of the Tree of Life.',
            generalMeaning: '家族财富和长久的物质安稳。',
            generalMeaningEn: 'You have achieved a state of long-term security. This card points to family abundance, established traditions, and the satisfaction of building something that lasts.',
            scenes: {
                career: '事业稳定可传承。',
                relationship: '家庭和睦幸福。',
                wealth: '财务稳定有遗产。',
                health: '家族健康传统。',
                universal: '当下享受完满的物质生活。',
                transition: '进入完满传承的阶段。'
            },
            scenesEn: {
                career: 'Building a lasting company; family business success; secure retirement.',
                relationship: 'Strong family ties; marriage built on shared history; domestic harmony.',
                wealth: 'Inheritance; real estate success; long-term financial safety.',
                health: 'Good genetic health; longevity; established wellness habits.',
                universal: 'Honor your roots while securing your future.',
                transition: 'A peak positive shift from growth to established legacy.'
            }
        },
        reversed: {
            keywords: ['家族问题', '遗产纷争', '不稳定', '传承中断'],
            keywordsEn: ['Family Feud', 'Financial Loss', 'Broken Tradition', 'Instability', 'Short-term Focus'],
            imageDescription: '家族成员疏离或财富图案破裂。',
            imageDescriptionEn: 'The coins fall out of pattern; the archway cracks, symbolizing a threat to the family unit or legacy.',
            generalMeaning: '家族问题或传承中断。',
            generalMeaningEn: 'Financial or family stability is being threatened. There may be arguments over money, a loss of security, or a feeling of disconnection from your roots.',
            scenes: {
                career: '事业传承出问题。',
                relationship: '家庭出现问题。',
                wealth: '遗产纷争。',
                health: '家族健康问题。',
                universal: '当下需处理家族事务。',
                transition: '从完满转向问题的消极转折。'
            },
            scenesEn: {
                career: 'A failing family business; lack of career security; disputes at the workplace.',
                relationship: 'Family arguments; feeling like an outsider; inheritance disputes.',
                wealth: 'Sudden financial loss; bad real estate deals; losing an inheritance.',
                health: 'Neglecting family health history; stress-related illnesses in the home.',
                universal: 'Do not sacrifice your relationships for material gain.',
                transition: 'A negative shift from security to domestic instability.'
            }
        }
    },

    // 075 - Page of Pentacles 星币侍从
    '075': {
        id: '075',
        name: 'Page of Pentacles',
        nameCn: '星币侍从',
        introduction: '星币侍从象征学习、新机会与务实的探索。',
        introductionEn: 'The Page of Pentacles represents the student of the material world. He is a messenger of new financial news and an explorer of practical skills and opportunities.',
        upright: {
            keywords: ['学习', '开始', '机会', '务实', '专注', '好消息'],
            keywordsEn: ['Manifestation', 'Opportunity', 'Student', 'Skill-Building', 'Practicality', 'New Plans'],
            imageDescription: '年轻人专注地端详手中的金币。',
            imageDescriptionEn: 'A young man stands in a flowering field, holding a gold coin with great care and focus, as if studying its value and potential.',
            generalMeaning: '专注学习，有新的物质机会。',
            generalMeaningEn: 'A time of setting new goals and developing practical skills. You are receiving news about money or career that requires your focused attention.',
            scenes: {
                career: '新工作或学习机会。',
                relationship: '认真经营感情。',
                wealth: '理财新手入门。',
                health: '学习健康知识。',
                universal: '当下专注学习新事物。',
                transition: '进入学习成长的阶段。'
            },
            scenesEn: {
                career: 'Starting a new job or course; learning a trade; an entry-level opportunity.',
                relationship: 'A stable new romance; being serious about a partner; practical expressions of love.',
                wealth: 'Receiving news about an investment; starting a small savings plan.',
                health: 'Learning about nutrition; starting a new physical habit.',
                universal: 'Focus on the details; small steps lead to big wealth.',
                transition: 'A shift into a period of practical discovery and learning.'
            }
        },
        reversed: {
            keywords: ['缺乏进展', '分心', '浪费机会', '不切实际'],
            keywordsEn: ['Lack of Focus', 'Procrastination', 'Unrealistic', 'Missed Opportunities', 'Laziness'],
            imageDescription: '侍从分心或丢失金币。',
            imageDescriptionEn: 'The Page looks bored or the coin is slipping from his hands, signifying a lack of follow-through.',
            generalMeaning: '学习不专注或机会流失。',
            generalMeaningEn: 'You may be lacking the discipline needed to reach your goals. Plans are being made but not executed, leading to wasted potential.',
            scenes: {
                career: '工作机会错失。',
                relationship: '感情不够用心。',
                wealth: '理财不当。',
                health: '忽视健康。',
                universal: '当下需重新专注。',
                transition: '从学习转向分心的消极转折。'
            },
            scenesEn: {
                career: 'Lack of professional focus; failing a course; missing a deadline.',
                relationship: 'Ignoring a partner; lack of commitment; being "all talk" in love.',
                wealth: 'Financial disorganization; small losses due to carelessness.',
                health: 'Ignoring health symptoms; starting and stopping a diet.',
                universal: 'Ground yourself; stop daydreaming and start working.',
                transition: 'A negative shift from learning to lack of progress.'
            }
        }
    },

    // 076 - Knight of Pentacles 星币骑士
    '076': {
        id: '076',
        name: 'Knight of Pentacles',
        nameCn: '星币骑士',
        introduction: '星币骑士象征可靠、勤勉与稳步的前行。',
        introductionEn: 'The Knight of Pentacles is the most reliable of the Knights. He represents the slow, steady, and methodical pursuit of a goal through pure hard work.',
        upright: {
            keywords: ['可靠', '勤勉', '坚持', '稳定', '负责', '耐心'],
            keywordsEn: ['Reliability', 'Hard Work', 'Patience', 'Routine', 'Persistence', 'Stability'],
            imageDescription: '骑士骑着壮实的黑马，静静持着金币。',
            imageDescriptionEn: 'A knight sits on a heavy, stationary black horse in a field. He holds a single coin and looks forward with a patient, unshakeable gaze.',
            generalMeaning: '稳健可靠地朝目标前进。',
            generalMeaningEn: 'Success comes through consistency. You are the "tortoise" in the race—moving slowly but with total certainty. Stick to your routine.',
            scenes: {
                career: '工作勤勉可靠。',
                relationship: '感情稳定可靠。',
                wealth: '财务稳健积累。',
                health: '坚持健康习惯。',
                universal: '当下需要稳健可靠地前进。',
                transition: '进入稳健行动的阶段。'
            },
            scenesEn: {
                career: 'Steadfast professional progress; a reliable employee; completing tasks methodically.',
                relationship: 'A loyal and dependable partner; a relationship built on practical support.',
                wealth: 'Conservative saving; slow but steady growth; financial security.',
                health: 'Disciplined fitness routine; stable physical recovery.',
                universal: 'Don\'t rush; your persistence is your superpower.',
                transition: 'A grounded shift from plan to steady execution.'
            }
        },
        reversed: {
            keywords: ['固执', '懒惰', '无聊', '停滞'],
            keywordsEn: ['Boredom', 'Laziness', 'Rigidity', 'Stagnation', 'Lack of Ambition'],
            imageDescription: '骑士和马不动或方向错误。',
            imageDescriptionEn: 'The horse is stuck in mud or the knight looks exhausted, suggesting that the routine has become a rut.',
            generalMeaning: '过于固执或停滞不前。',
            generalMeaningEn: 'You may be feeling stuck in a boring routine. Your caution has turned into stubbornness, and you are refusing to change even when necessary.',
            scenes: {
                career: '工作停滞或过于保守。',
                relationship: '感情变得无聊。',
                wealth: '财务无进展。',
                health: '健康习惯中断。',
                universal: '当下需打破停滞。',
                transition: '从稳健转向停滞的消极转折。'
            },
            scenesEn: {
                career: 'Workplace stagnation; being a "workaholic" without joy; resisting new methods.',
                relationship: 'A dull or stagnant relationship; stubborn refusal to compromise.',
                wealth: 'Financial drought; being overly stingy to the point of misery.',
                health: 'Neglecting physical health; becoming sedentary or lazy.',
                universal: 'Break the routine; you need a spark of life to move forward.',
                transition: 'A negative shift from consistency to a boring rut.'
            }
        }
    },

    // 077 - Queen of Pentacles 星币皇后
    '077': {
        id: '077',
        name: 'Queen of Pentacles',
        nameCn: '星币皇后',
        introduction: '星币皇后象征滋养、丰盛与务实的母性力量。',
        introductionEn: 'The Queen of Pentacles represents the archetypal mother of the material world. She is practical, nurturing, and generous, balancing a successful career with a warm home.',
        upright: {
            keywords: ['滋养', '务实', '丰盛', '关怀', '安全', '居家'],
            keywordsEn: ['Nurturing', 'Practicality', 'Abundance', 'Security', 'Motherly', 'Generosity'],
            imageDescription: '皇后坐在花园王座上，膝上端详金币，兔子在脚边。',
            imageDescriptionEn: 'The Queen sits on a throne carved with fruit and animals in a lush garden. She holds a coin and watches a rabbit—symbol of fertility—hop by.',
            generalMeaning: '以务实方式创造丰盛与滋养。',
            generalMeaningEn: 'A period of abundance and comfort. You are able to take care of yourself and others with grace, using your resources to create a beautiful, secure environment.',
            scenes: {
                career: '工作务实高效。',
                relationship: '给予感情滋养。',
                wealth: '财务稳定丰盛。',
                health: '注重身体滋养。',
                universal: '当下以务实方式照顾自己和他人。',
                transition: '进入丰盛滋养的阶段。'
            },
            scenesEn: {
                career: 'Nurturing workplace leadership; a successful working mother; stable business management.',
                relationship: 'A supportive and grounded partner; creating a warm home together.',
                wealth: 'Financial abundance used for comfort; sound money management.',
                health: 'Focusing on nutrition and rest; a healthy, grounded body image.',
                universal: 'Be a source of comfort; your practical care is highly valued.',
                transition: 'A positive shift into a state of graceful abundance.'
            }
        },
        reversed: {
            keywords: ['物质执念', '忽视自我', '不安全感', '过度操劳'],
            keywordsEn: ['Insecurity', 'Materialism', 'Self-Neglect', 'Over-work', 'Possessiveness'],
            imageDescription: '皇后的滋养变成执念或自我忽视。',
            imageDescriptionEn: 'The garden looks overgrown and the Queen looks worried; she clutches the coin too tightly, symbolizing fear of loss.',
            generalMeaning: '过于关注物质或忽视自我。',
            generalMeaningEn: 'You may be neglecting your personal needs for material gain, or your need for security has become a source of anxiety. Beware of becoming overly possessive.',
            scenes: {
                career: '过度工作。',
                relationship: '忽视自我照顾。',
                wealth: '物质执念。',
                health: '忽视自己健康。',
                universal: '当下需平衡给予与自我照顾。',
                transition: '从滋养转向忽视的消极转折。'
            },
            scenesEn: {
                career: 'Burnout; neglecting family for work; feeling unprofessional or insecure.',
                relationship: 'Smothering a partner; feeling unappreciated; neglecting the emotional bond for money.',
                wealth: 'Fear of poverty; hoarding resources; using money to control others.',
                health: 'Poor diet; physical exhaustion; neglecting the body\'s warnings.',
                universal: 'Re-center your values; wealth is empty without inner peace.',
                transition: 'A negative shift from nurturing to anxious materialism.'
            }
        }
    },

    // 078 - King of Pentacles 星币国王
    '078': {
        id: '078',
        name: 'King of Pentacles',
        nameCn: '星币国王',
        introduction: '星币国王象征成就、领导与商业上的圆满。',
        introductionEn: 'The King of Pentacles represents the ultimate master of the physical world. He is the successful entrepreneur, the reliable leader, and the provider who has achieved absolute security.',
        upright: {
            keywords: ['成就', '领导', '稳定', '商业', '安全', '慷慨'],
            keywordsEn: ['Success', 'Leadership', 'Stability', 'Entrepreneur', 'Wealth', 'Mastery'],
            imageDescription: '国王坐在硕果累累的王座上，代表物质成就、稳定与商业成功。',
            imageDescriptionEn: 'The King sits on a throne adorned with bulls (Taurus). His robe is embroidered with grapes, and he holds a coin and a scepter, surrounded by blooming plants.',
            generalMeaning: '物质成就达到顶峰，稳定而慷慨。',
            generalMeaningEn: 'You have reached a position of true power and stability. You possess the "Midas touch"—the ability to make any venture profitable and secure.',
            scenes: {
                career: '事业成功有成就。',
                relationship: '提供稳定的感情基础。',
                wealth: '财务成就顶峰。',
                health: '享有健康的生活方式。',
                universal: '当下是物质成就的巅峰。',
                transition: '进入成就巅峰的阶段。'
            },
            scenesEn: {
                career: 'CEO or business leader; financial success in career; being a master of your industry.',
                relationship: 'A protective and loyal partner; providing a wealthy and stable life for the family.',
                wealth: 'Peak financial security; successful real estate or large-scale investments.',
                health: 'Mastery over one\'s body; energetic and physically powerful; long-term health.',
                universal: 'Lead with authority; your success is built on a solid foundation.',
                transition: 'A final shift into the peak of material achievement.'
            }
        },
        reversed: {
            keywords: ['贪婪', '控制欲', '固执', '物质主义'],
            keywordsEn: ['Greed', 'Corruption', 'Rigidity', 'Financial Failure', 'Materialistic'],
            imageDescription: '国王变得贪婪或失去财富。',
            imageDescriptionEn: 'The King looks greedy or his treasures are decaying; he turns away from the growth of his garden, symbolizing stagnation.',
            generalMeaning: '过于贪婪或物质主义。',
            generalMeaningEn: 'An abuse of power or wealth. You may be focusing solely on status and money, losing your moral compass or your connection to people.',
            scenes: {
                career: '事业过于追求利益。',
                relationship: '感情中用金钱控制。',
                wealth: '贪婪影响财务。',
                health: '物质主义影响生活质量。',
                universal: '当下需反思对物质的态度。',
                transition: '从成就转向贪婪的消极转折。'
            },
            scenesEn: {
                career: 'A ruthless boss; ethical lapses in business; loss of professional status.',
                relationship: 'Trying to "buy" love; coldness in a marriage; focus on status over heart.',
                wealth: 'Financial loss through greed; corruption; being "money-hungry."',
                health: 'Health issues from over-indulgence; high stress due to greed.',
                universal: 'True wealth includes character, not just coins.',
                transition: 'A negative shift from mastery to corrupt greed.'
            }
        }
    }
};

// 获取牌意的辅助函数
export const getCardMeaning = (cardId: string): CardMeaning | undefined => {
    return tarotMeanings[cardId];
};

// 获取所有牌的列表
export const getAllCards = (): CardMeaning[] => {
    return Object.values(tarotMeanings);
};
